import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { resolvePublicAssetBackgroundUrl, resolvePublicAssetUrl } from './utils/resolvePublicAssetUrl';
import { getCountryFromCellId, getCellIdFromCountry, getAllCountries } from './worldMap/countryToCellMapping';
import { getNeighbourCountries, getNeighbourCellIds } from './worldMap/countryNeighbours';

// Parse last action message for a country name so we can highlight on map when server doesn't send lastPlayerActionCellId
function getCellIdFromLastActionMessage(message) {
  if (!message || typeof message !== 'string') return null;
  const countries = getAllCountries().sort((a, b) => b.length - a.length);
  for (const country of countries) {
    if (message.includes(country)) return getCellIdFromCountry(country);
  }
  return null;
}
import { API_URL } from './config/apiConfig';
import { buildOrderDetailLangQuery } from './utils/buildOrderDetailLangQuery';
import CustomDropdown from './CustomDropdown';
import { playSoundEffect } from './hooks/useSoundEffects';
import { getEffectiveSoundOutputVolume } from './hooks/soundEffectsManager';
import { createPortal } from 'react-dom';
import OrderDesign from './OrderDesign';
import OrderSidebarStyleCard from './components/OrderSidebarStyleCard';
import './components/OrderSidebarStyleCard.css';
import { OrderCardBubbles } from './components/OrderCardBubbles';
import './StrategyPreferenceRewardModal.css';
import './WarEffortFortification.css';
import WarEffortBotLog from './WarEffortBotLog';
import AchievementUnlockedToast from './notifications/AchievementUnlockedToast';
import { usePendingAchievementToasts } from './hooks/usePendingAchievementToasts';
import AttackStrategyStatisticsPanel from './components/AttackStrategyStatisticsPanel';
import { getActiveAttackStrategyOrders } from './utils/attackStrategyStatistics';

/** Attack strategy modal row — same card style as Strategy sidebar / battle S: modal. */
function AttackStrategySlotRow({
  order,
  orderId,
  occurrence,
  isDeactivated,
  costLabel,
  onToggle,
  setTooltipRect,
}) {
  const cost = order.cost ?? order.intel_cost ?? 0;
  return (
    <div
      className={`war-effort-attack-strategy-entry${isDeactivated ? ' war-effort-attack-strategy-entry--inactive' : ''}`}
      role="button"
      tabIndex={0}
      onClick={() => {
        playSoundEffect('/sounds/click.mp3');
        onToggle(orderId, occurrence);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          playSoundEffect('/sounds/click.mp3');
          onToggle(orderId, occurrence);
        }
      }}
      onMouseEnter={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const showAbove = typeof window !== 'undefined' && rect.bottom + 400 > window.innerHeight;
        setTooltipRect({ order, top: rect.top, bottom: rect.bottom, left: rect.left, showAbove });
      }}
      onMouseLeave={() => setTooltipRect(null)}
    >
      <OrderSidebarStyleCard
        order={order}
        displayName={order.name}
        displayCost={cost}
        costLabel={costLabel}
        className="order-sidebar-style-card--battle-modal-list"
      />
    </div>
  );
}

// Trading Hub effect_type (snake_case) -> translation key under warEffort.tradingHub.shop
const SHOP_EFFECT_TYPE_TO_KEY = {
  future_knowledge: 'futureKnowledge',
  movement_voucher: 'movementVoucher',
  structural_exposure: 'structuralExposure',
  sneaky_peeky: 'sneakyPeeky',
  peak_generation: 'peakGeneration',
  infrastructure_subsidy: 'infrastructureSubsidy',
  sabotage: 'sabotage',
  conflict_cooldown: 'conflictCooldown',
  cease_fire_buyout: 'ceaseFireBuyout',
  speedpass: 'speedpass',
  domestic_turmoil: 'domesticTurmoil',
  tectonic_reconcilement: 'tectonicReconcilement',
  strategic_crippling: 'strategicCrippling'
};

// effect_type -> image filename (matches server catalog in /images/)
const SHOP_EFFECT_TYPE_TO_IMAGE = {
  future_knowledge: '1_future_knowledge_injection.jpg',
  movement_voucher: '4_movement_voucher.jpg',
  structural_exposure: '2_structural_exposure.jpg',
  sneaky_peeky: '5_sneaky_peeky.jpg',
  peak_generation: '10_generation_surge.jpg',
  infrastructure_subsidy: '8_discount.jpg',
  sabotage: '9_sabotage.jpg',
  conflict_cooldown: '3_cease_fire.jpg',
  cease_fire_buyout: '7_cease_fire_buyout.jpg',
  speedpass: '6_speedpass.jpg',
  domestic_turmoil: '11_turmoil.jpg',
  tectonic_reconcilement: '12_Reconcilement.jpg',
  strategic_crippling: '13_Crippling.jpg'
};

const THUMBNAIL_SIZE = 44;

function ActiveShopEffectsThumbnails({ effects, effectTypeToKey, effectTypeToImage, t }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const thumbRefs = useRef([]);

  const handleMouseEnter = (idx, e) => {
    setHoveredIndex(idx);
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPos({ x: rect.left, y: rect.top });
  };

  const handleMouseLeave = () => setHoveredIndex(null);

  return (
    <div className="war-effort-map-chrome--shop-effects">
      <div className="war-effort-map-chrome--shop-effects-panel">
        <div style={{ fontWeight: 'bold', color: '#4ECDC4', fontSize: '13px' }}>
          {t('warEffort.tradingHub.activeEffects', 'Active shop effects')}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center' }}>
          {effects.map((eff, idx) => {
            const key = effectTypeToKey[eff.effectType] || eff.effectType;
            const name = t(`warEffort.tradingHub.shop.${key}`, eff.effectType);
            const descKey = `${key}Desc`;
            const description = t(`warEffort.tradingHub.shop.${descKey}`, '');
            const image = effectTypeToImage[eff.effectType] || 'shop_energy.jpg';
            const imgSrc = `/images/${image}`;
            return (
              <div
                key={idx}
                ref={el => { thumbRefs.current[idx] = el; }}
                onMouseEnter={(e) => handleMouseEnter(idx, e)}
                onMouseLeave={handleMouseLeave}
                style={{
                  width: THUMBNAIL_SIZE,
                  height: THUMBNAIL_SIZE,
                  borderRadius: 6,
                  overflow: 'hidden',
                  border: '2px solid rgba(255,255,255,0.25)',
                  cursor: 'default',
                  flexShrink: 0
                }}
              >
                <img
                  src={imgSrc}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => { e.target.src = '/images/shop_energy.jpg'; }}
                />
              </div>
            );
          })}
        </div>
      </div>
      {hoveredIndex != null && effects[hoveredIndex] && (() => {
        const eff = effects[hoveredIndex];
        const key = effectTypeToKey[eff.effectType] || eff.effectType;
        const name = t(`warEffort.tradingHub.shop.${key}`, eff.effectType);
        const descKey = `${key}Desc`;
        const description = t(`warEffort.tradingHub.shop.${descKey}`, '');
        const hasExpiry = eff.expiresAt != null;
        const hasTicks = eff.remainingTicks != null && eff.remainingTicks > 0;
        const rect = thumbRefs.current[hoveredIndex]?.getBoundingClientRect();
        const gap = 12;
        const tooltipWidth = 340;
        const rightEdge = rect ? rect.left - gap : window.innerWidth - tooltipPos.x - gap;
        const topY = rect ? rect.top + rect.height / 2 : tooltipPos.y + THUMBNAIL_SIZE / 2;
        return (
          <div
            style={{
              position: 'fixed',
              right: `${window.innerWidth - rightEdge}px`,
              top: topY,
              transform: 'translateY(-50%)',
              width: tooltipWidth,
              maxWidth: 'min(340px, calc(100vw - 120px))',
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              border: '2px solid rgba(78, 205, 196, 0.6)',
              borderRadius: '8px',
              padding: '14px 18px',
              zIndex: 10001,
              pointerEvents: 'none',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.8)',
              color: 'white',
              fontSize: '14px',
              fontFamily: 'Arial, sans-serif',
              lineHeight: 1.5
            }}
          >
            <div style={{ fontWeight: 'bold', color: '#4ECDC4', marginBottom: '8px', fontSize: '15px' }}>{name}</div>
            {description && <div style={{ color: '#E0E0E0', lineHeight: 1.5, marginBottom: hasExpiry || hasTicks ? '8px' : 0 }}>{description}</div>}
            {hasExpiry && (
              <div style={{ fontSize: '12px', color: '#9CA3AF' }}>
                {t('warEffort.tradingHub.expiresAt', 'expires')} {new Date(eff.expiresAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
            {hasTicks && !hasExpiry && (
              <div style={{ fontSize: '12px', color: '#9CA3AF' }}>
                {eff.remainingTicks} {t('warEffort.tradingHub.remainingTicks', 'ticks')}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

const FORTIFICATION_TYPES = [
  { type: 'health_boost', name: 'Health Boost', cost: 280, description: 'All defending units have +1 health', unitId: 227 },
  { type: 'attack_boost', name: 'Attack Boost', cost: 320, description: 'All defending units have +1 attack', unitId: 77 },
  { type: 'intel_boost', name: 'Intel Boost', cost: 200, description: 'Defender has +1 intel', unitId: 237 },
  { type: 'order_boost', name: 'Order Boost', cost: 200, description: 'Defender has +1 order', unitId: 103 },
  { type: 'disorientation', name: 'Disorientation', cost: 380, description: 'Attacker cannot play hand orders on their first 2 turns', unitId: 59 },
  { type: 'defend_twice', name: 'Defend Twice', cost: 420, description: 'Defender can defend twice', unitId: 187 },
  { type: 'instakill', name: 'Instakill', cost: 500, description: 'Defender can instakill a unit once', unitId: 60 },
  { type: 'deploy_random', name: 'Deploy Random', cost: 500, description: 'Defender can deploy a random unit costing max intel once', unitId: 279 },
  { type: 'ability_relay_drone', name: 'Ability Relay Drone', cost: 400, description: 'Defender starts with ability relay drone', unitId: 399 }
];

// Same card size as AfterBattle victory reward screen (390px) so orders are full size
const REWARD_CARD_SIZE = 390;

// Render a single reward card (order, billy_bucks, resource, duplicator, building, emote) for mission-over screen
function WarEffortMissionOverRewardCard({ reward, index, t }) {
  if (!reward || !reward.type) return null;
  const cardStyle = {
    width: REWARD_CARD_SIZE,
    height: REWARD_CARD_SIZE,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.6)',
    borderRadius: 8,
    padding: 20,
    overflow: 'hidden',
    position: 'relative'
  };
  if (reward.type === 'order' && reward.data && reward.data.id) {
    const order = reward.data;
    const rarity = (order.rarity || 'common').toLowerCase();
    return (
      <div key={index} className={`reward-order-container rarity-${rarity}`} style={{ width: REWARD_CARD_SIZE, height: REWARD_CARD_SIZE, position: 'relative', margin: 0, padding: 0, flexShrink: 0 }}>
        <OrderDesign order={order} count={0} minimal={false} noMargin={true} />
        <OrderCardBubbles rarity={order?.rarity || reward.data?.rarity || 'common'} />
      </div>
    );
  }
  if (reward.type === 'billy_bucks') {
    return (
      <div key={index} style={cardStyle}>
        <img src="/images/billy_bucks.png" alt="Billy Bucks" style={{ width: 240, height: 240, marginBottom: 20, objectFit: 'contain' }} />
        <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: 10 }}>{reward.amount ?? 0}</div>
        <div style={{ fontSize: '1.2rem', color: '#ccc' }}>Billy Bucks</div>
      </div>
    );
  }
  if (reward.type === 'resource') {
    const resourceMap = { lavender: '/images/resource_lavender.png', biomass: '/images/resource_biomass.png', food_rations: '/images/resource_foodrations.png', stone: '/images/resource_stone.png', erray: '/images/resource_erray.png', energy: '/images/powerplant.png' };
    const resourceType = reward.resourceType || reward.data?.resourceType || '';
    const amount = reward.amount ?? reward.data?.amount ?? 0;
    const imgSrc = resourceMap[resourceType.toLowerCase()] || '/images/resource_biomass.png';
    const name = t ? (t(`warEffort.resourceNames.${resourceType.toLowerCase()}`) || resourceType || 'Resource') : (resourceType || 'Resource');
    return (
      <div key={index} style={cardStyle}>
        <img src={imgSrc} alt={name} style={{ width: 240, height: 240, marginBottom: 20, objectFit: 'contain' }} onError={(e) => { e.target.src = '/images/resource_biomass.png'; }} />
        <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: 10 }}>{amount}</div>
        <div style={{ fontSize: '1.2rem', color: '#ccc' }}>{name}</div>
      </div>
    );
  }
  if (reward.type === 'duplicator' && reward.data) {
    return (
      <div key={index} style={cardStyle}>
        <img src="/images/duplicator.jpg" alt="Duplicator" style={{ width: 240, height: 240, marginBottom: 20, objectFit: 'contain' }} />
        <div style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: 10 }}>{reward.data.name}</div>
        <div style={{ fontSize: '0.9rem', color: '#ccc', textTransform: 'capitalize' }}>{reward.data.rarity} Duplicator</div>
      </div>
    );
  }
  if (reward.type === 'building' && reward.data) {
    const buildingImages = { powerplant: '/images/powerplant.png', barracks: '/images/barracks.png', training_grounds: '/images/training.png', armory: '/images/armory.png', drone_hangar: '/images/dronehangar.png', communications_hub: '/images/comhub.png', intelligence_agency: '/images/intel.png', bank: '/images/bank.png', research_center: '/images/research.png', hospital: '/images/hospital.png', airfield: '/images/airfield.png' };
    const bid = reward.data.building_id || '';
    const displayName = (reward.data.name || bid).replace(/_/g, ' ');
    const imgSrc = buildingImages[bid] || `/images/${bid}.png` || '/images/command.png';
    return (
      <div key={index} style={cardStyle}>
        <img src={imgSrc} alt={displayName} style={{ width: 240, height: 240, marginBottom: 20, objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
        <div style={{ fontSize: '1.2rem', fontWeight: 'bold', textAlign: 'center', marginBottom: 10 }}>HQ Building</div>
        <div style={{ fontSize: '1rem', color: '#ccc' }}>{displayName}</div>
      </div>
    );
  }
  if (reward.type === 'emote') {
    return (
      <div key={index} style={cardStyle}>
        <img src="/images/emotes.jpg" alt="Emote" style={{ width: 240, height: 240, marginBottom: 20, objectFit: 'contain' }} />
        <div style={{ fontSize: '1.2rem', color: '#ccc' }}>{reward.data?.speechBubble || reward.data?.name || 'Emote'}</div>
      </div>
    );
  }
  return null;
}

// Redirect winner or loser to AfterBattle so they get the same experience as "Dev War Effort Rewards" / "Dev War Effort Rewards Loser" (message phase, flip cards).
function WarEffortRedirectToAfterBattle({ missionId, missionEndGameId, result, navigate }) {
  useEffect(() => {
    navigate('/after-battle', {
      state: {
        gameId: missionEndGameId,
        result: result || 'win',
        isWarEffort: true,
        gameState: { warEffortMissionId: missionId, warEffortBattleType: 'enemy_hq' },
        fromWarEffortMissionOver: true,
        missionIdForDismiss: missionId
      },
      replace: true
    });
  }, [missionId, missionEndGameId, result, navigate]);
  return (
    <div style={{ position: 'fixed', inset: 0, background: '#0a0a12', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
      Loading…
    </div>
  );
}

// Mission-over screen (win or loss). Shown when mission is completed.
// All winners see the same victory screen: wareffort_success.jpg + rewards (whether they won by conquest or opponents leaving).
// Leavers get no rewards (backend does not grant); same as PVE resign — show "no rewards" and do not call grant.
function WarEffortMissionOverScreen({ playerWon, missionId, missionEndGameId, onBack, API_URL, t }) {
  const { i18n } = useTranslation();
  const [missionEndRewards, setMissionEndRewards] = useState(null);
  const [noRewardsForUser, setNoRewardsForUser] = useState(false); // true when 404 or empty (e.g. leaver)
  useEffect(() => {
    if (!missionEndGameId) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const langQuery = buildOrderDetailLangQuery(i18n.language);
    fetch(`${API_URL}/battle-rewards/${missionEndGameId}?${langQuery}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => {
        if (res.status === 404) {
          setNoRewardsForUser(true);
          return null;
        }
        return res.ok ? res.json() : null;
      })
      .then((data) => {
        if (data && Array.isArray(data.rewards) && data.rewards.length > 0) {
          setMissionEndRewards(data.rewards);
        } else if (data && (data.noRewards === true || !data.rewards || data.rewards.length === 0)) {
          setNoRewardsForUser(true);
        }
      })
      .catch(() => {});
  }, [missionEndGameId, API_URL, i18n.language]);

  const handleContinue = async () => {
    const token = localStorage.getItem('token');
    // Only call grant when we have rewards (leavers have no rewards row; same as PVE resign)
    if (token && missionEndGameId && missionEndRewards && missionEndRewards.length > 0) {
      try {
        await fetch(`${API_URL}/battle-rewards/${missionEndGameId}/grant`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) {
        console.error('Failed to grant battle rewards:', e);
      }
    }
    if (missionId) {
      try {
        const token = localStorage.getItem('token');
        await fetch(`${API_URL}/war-effort/mission/${missionId}/dismiss-completion`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) {
        console.error('Failed to dismiss mission completion:', e);
      }
    }
    if (onBack) onBack();
  };

  return (
    <div
      className="war-effort-mission-over"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        background: playerWon ? 'url(/images/wareffort_success.jpg) center/cover no-repeat' : undefined,
        color: '#fff',
        padding: 24,
        textAlign: 'center',
        overflowY: 'auto'
      }}
    >
      {!playerWon && (
        <video
          autoPlay
          muted
          loop
          playsInline
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0
          }}
          aria-hidden
        >
          <source src="/war_effort_loss.mp4" type="video/mp4" />
        </video>
      )}
      <h2 style={{ marginBottom: 16, fontSize: '1.75rem', position: 'relative', zIndex: 1 }}>
        {playerWon ? t('warEffort.missionWon', 'Mission won') : t('warEffort.missionLost', 'You lost this mission')}
      </h2>
      <p style={{ marginBottom: 16, maxWidth: 400, color: '#ccc', position: 'relative', zIndex: 1 }}>
        {playerWon
          ? t('warEffort.missionWonCompleted', 'You have completed this mission. You received your rewards.')
          : noRewardsForUser
            ? t('warEffort.missionLostNoRewards', 'You left the mission. No rewards were granted.')
            : t('warEffort.missionLostEnded', 'The mission has ended. You still received participation rewards.')}
      </p>
      {missionEndRewards && missionEndRewards.length > 0 && (
        <>
          <p style={{ marginBottom: 12, color: '#aaa', fontSize: '0.95rem', position: 'relative', zIndex: 1 }}>
            {t('warEffort.rewardsReceived', 'Rewards received')}
          </p>
          <div
            className="war-effort-mission-over-rewards-grid"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 40,
              justifyContent: 'center',
              marginBottom: 24,
              position: 'relative',
              zIndex: 1
            }}
          >
            {missionEndRewards.map((reward, index) => (
              <WarEffortMissionOverRewardCard key={index} reward={reward} index={index} t={t} />
            ))}
          </div>
        </>
      )}
      <button
        type="button"
        onClick={handleContinue}
        style={{
          padding: '12px 24px',
          fontSize: '1rem',
          fontWeight: 'bold',
          color: '#fff',
          backgroundColor: '#4ECDC4',
          border: 'none',
          borderRadius: 8,
          cursor: 'pointer',
          position: 'relative',
          zIndex: 1,
          marginTop: 5
        }}
      >
        {t('warEffort.continue', 'Continue')}
      </button>
    </div>
  );
}

// Purpose to faction mapping (must match backend)
const PURPOSE_TO_FACTION_MAPPING = {
  'Consume': ['Asrah', 'The Hollow', 'The Forsaken Pact'],
  'Eradicate': ['Exolot Dominion', 'The Corrupted Core', 'The Iron Alliance'],
  'Colonize': ['League of Shadows', 'The Conspiracy', 'The Shadow Consortium'],
  'Protect': ['Ra\'s Children', 'The Divine Syndicate', 'The Covenant'],
  'Survive': ['Remnants of Humanity', 'The Last Crusade', 'The Silent Accord']
};

// Faction name -> purpose (for deriving opponent Command Center stripe per cell in FFA/2v2)
const FACTION_TO_PURPOSE = (() => {
  const m = {};
  Object.entries(PURPOSE_TO_FACTION_MAPPING).forEach(([purpose, factions]) => {
    (factions || []).forEach((f) => { m[f] = purpose; });
  });
  return m;
})();

// Purpose -> HQ cell ID (first faction with HQ for that purpose)
const PURPOSE_TO_HQ_CELL_ID = {
  'Consume': 333,   // India (Asrah)
  'Eradicate': 254, // Peru (Exolot Dominion)
  'Colonize': 296,  // Switzerland (League of Shadows)
  'Protect': 413,   // Egypt (Ra's Children)
  'Survive': 213    // Canada (Remnants of Humanity)
};

// Reverse: HQ cell ID -> purpose (for resolving which opponent owns an HQ in FFA)
const HQ_CELL_ID_TO_PURPOSE = Object.fromEntries(
  Object.entries(PURPOSE_TO_HQ_CELL_ID).map(([p, id]) => [id, p])
);

// Purpose -> mission type label (for mission box display)
const PURPOSE_TO_MISSION_TYPE = {
  'Consume': 'Infect key officials',
  'Eradicate': 'Sabotage production',
  'Colonize': 'Infiltrate main communities',
  'Protect': 'Weaken enemy forces',
  'Survive': 'Raid targets'
};

// Purpose -> Primary faction name (for dev mode)
const DEV_PURPOSE_TO_FACTION = {
  'Consume': 'Asrah',
  'Eradicate': 'Exolot Dominion',
  'Colonize': 'League of Shadows',
  'Protect': "Ra's Children",
  'Survive': 'Remnants of Humanity'
};

// Purpose -> color for info box display (sandy yellow, olive green, etc.)
const PURPOSE_TO_COLOR = {
  'Protect': '#C4A35A',   // sandy yellow
  'Survive': '#6B8E23',   // olive green
  'Consume': '#DC143C',   // crimson red
  'Eradicate': '#800080', // purple
  'Colonize': '#4A4A4A'   // dark gray
};

// Command Center stripe colors per purpose (base + darker for map fill)
const PURPOSE_COMMAND_CENTER_STRIPE_COLORS = {
  Survive:   { base: '#556B2F', darker: '#3d4d21' },   // remnant green, dark green
  Protect:   { base: '#DAA520', darker: '#8B6914' },   // egypt yellow, dark yellow
  Colonize:  { base: '#1A1A1A', darker: '#0a0a0a' },  // shadow black, darkest black
  Consume:   { base: '#8B0000', darker: '#5c0000' },   // asrah red, dark red
  Eradicate: { base: '#4B0082', darker: '#2a004a' }    // exolot purple, dark purple
};

// The Sentients HQ (Madagascar) - trading hub, not attackable (must match backend SENTIENTS_HQ_CELL_ID)
const SENTIENTS_HQ_CELL_ID = 457;

// Country ownership bonuses (cell_id -> tooltip text; shown for whoever owns the country; must match backend)
const COUNTRY_BONUS_CELL_IDS = [224, 308, 387, 354]; // Cuba, Iceland, Sri Lanka, Taiwan
const ICELAND_CELL_ID = 308;
const COUNTRY_BONUS_TOOLTIPS = {
  224: 'Max energy 1500',           // Cuba
  308: 'Defending countries get +1 starting intel', // Iceland
  387: '+20 energy per tick',       // Sri Lanka
  354: 'All buildings cost 20% less' // Taiwan
};
// Iceland bonus applies to all defending countries of the faction that owns Iceland (show on every owned country's tooltip)
const ICELAND_BONUS_TOOLTIP = 'Defending countries get +1 starting intel';

// HQ building page: buildings available to build
// Why the image "lower border" was unknown: (1) HQ_BUILDINGS only has image URL, no width/height or aspectRatio.
// (2) With object-fit: contain the image is scaled to fit the box while preserving aspect ratio, so the actual
// painted image can end well above the box bottom (e.g. landscape images). (3) We never read the loaded image's
// naturalWidth/naturalHeight or measured the img element, so we had no data to compute where the image content ends.
// Fix: we now read naturalWidth/naturalHeight on img load and store aspect ratio per image src, then use it in
// layout so the progress bar sits just below the actual image (see buildingImageAspectRatio state and img onLoad).
const HQ_BUILDINGS = [
  { id: 'powerplant', name: 'Powerplant', image: '/images/powerplant.png', cost: 160, description: 'Level 1: 8 energy/min. Level 2: 16 energy/min.', maxCount: 5 },
  { id: 'powerplant_upgraded', name: 'Upgraded Powerplant', image: '/images/powerplant_upgraded.png', cost: 0, description: 'Level 1: 8 energy/min. Level 2: 16 energy/min.', maxCount: 5 },
  { id: 'eclipse_array', name: 'Eclipse Array', image: '/images/array.png', cost: 100, description: 'Level 1: Reveal neutral fortification types. Level 2: Also reveal opponent fortification types.', maxCount: 1 },
  { id: 'eclipse_array_upgraded', name: 'Upgraded Eclipse Array', image: '/images/array_upgraded.png', cost: 0, description: 'Level 1: Reveal neutral fortification types. Level 2: Also reveal opponent fortification types.', maxCount: 1 },
  { id: 'command_center', name: 'Command Center', image: '/images/command.png', cost: 260, description: 'When one of your countries is attacked, if you own 3+ neighbouring countries, all your units get +1 health.', maxCount: 1 },
  { id: 'cattle_ranch', name: 'Cattle Farm', image: '/images/cattle.png', cost: 800, description: 'Collect to 80 to complete. When complete and defense has ≥30 Remnants orders: all your Remnants units get +1 health in your battles.', maxCount: 1 },
  { id: 'quarry', name: 'Quarry', image: '/images/stone.png', cost: 900, description: 'Collect to 80 to complete. When complete and defense has ≥30 Ra\'s Children orders: at start of your turn, defender units with Shield get their shield back.', maxCount: 1 },
  { id: 'sewers', name: 'Sewer', image: '/images/sewers.png', cost: 700, description: 'Collect to 80 to complete. When complete and defense has ≥30 Asrah orders: all defender units get Mend Wounds.', maxCount: 1 },
  { id: 'portal', name: 'Portal', image: '/images/erray.png', cost: 800, description: 'Collect to 80 to complete. When complete and defense has ≥30 League of Shadows orders: each defender unit gets one charge to switch zones.', maxCount: 1 },
  { id: 'lavender_field', name: 'Lavender Farm', image: '/images/lavender.png', cost: 850, description: 'Collect to 80 to complete. When complete and defense has ≥30 Exolot Dominion orders: all defender units that cost <4 intel get Blitz.', maxCount: 1 },
  { id: 'training_grounds', name: 'Training Grounds', image: '/images/training.png', cost: 300, description: 'Level 1: Keep 2 of 3 orders from conquests and subversion. Level 2: Keep all 3. (Buildings like Barracks always give 1 choice.)', maxCount: 1 },
  { id: 'training_grounds_upgraded', name: 'Upgraded Training Grounds', image: '/images/training_upgraded.png', cost: 0, description: 'Level 1: Keep 2 of 3 orders from conquests and subversion. Level 2: Keep all 3. (Buildings like Barracks always give 1 choice.)', maxCount: 1 },
  { id: 'barracks', name: 'Barracks', image: '/images/barracks.png', cost: 160, description: 'Train new units to fight for you. Level 1: 40% common, 50% rare, 10% epic. Level 2: 10% common, 30% rare, 40% epic, 20% legendary.', maxCount: 2 },
  { id: 'barracks_upgraded', name: 'Upgraded Barracks', image: '/images/barracks_upgraded.png', cost: 0, description: 'Train new units to fight for you. Level 1: 40% common, 50% rare, 10% epic. Level 2: 10% common, 30% rare, 40% epic, 20% legendary.', maxCount: 2 },
  { id: 'armory', name: 'Armory', image: '/images/armory.png', cost: 130, description: 'Build new weapons for your troops. Level 1: 40% common, 50% rare, 10% epic. Level 2: 10% common, 30% rare, 40% epic, 20% legendary.', maxCount: 2 },
  { id: 'armory_upgraded', name: 'Upgraded Armory', image: '/images/armory_upgraded.png', cost: 0, description: 'Build new weapons for your troops. Level 1: 40% common, 50% rare, 10% epic. Level 2: 10% common, 30% rare, 40% epic, 20% legendary.', maxCount: 2 },
  { id: 'drone_hangar', name: 'Drone Hangar', image: '/images/dronehangar.png', cost: 100, description: 'Create drones to support you on the battlefield. Level 1: 40% common, 50% rare, 10% epic. Level 2: 10% common, 30% rare, 40% epic, 20% legendary.', maxCount: 2 },
  { id: 'drone_hangar_upgraded', name: 'Upgraded Drone Hangar', image: '/images/dronehangar_upgraded.png', cost: 0, description: 'Create drones to support you on the battlefield. Level 1: 40% common, 50% rare, 10% epic. Level 2: 10% common, 30% rare, 40% epic, 20% legendary.', maxCount: 2 },
  { id: 'communications_hub', name: 'Communications Hub', image: '/images/comhub.png', cost: 200, description: 'Call upon mighty stratagems to aid you in battle. Level 1: 40% common, 50% rare, 10% epic. Level 2: 10% common, 30% rare, 40% epic, 20% legendary.', maxCount: 2 },
  { id: 'communications_hub_upgraded', name: 'Upgraded Communications Hub', image: '/images/comhub_upgraded.png', cost: 0, description: 'Call upon mighty stratagems to aid you in battle. Level 1: 40% common, 50% rare, 10% epic. Level 2: 10% common, 30% rare, 40% epic, 20% legendary.', maxCount: 2 },
  { id: 'intelligence_agency', name: 'Intelligence Agency', image: '/images/intel.png', cost: 200, description: 'Show what countries your opponent is attacking.', maxCount: 1 },
  { id: 'bank', name: 'Bank', image: '/images/bank.png', cost: 200, description: 'Generate Billy Bucks. 5 per minute, stores 100.', maxCount: 1 },
  { id: 'research_center', name: 'Research Center', image: '/images/research.png', cost: 400, description: 'Earn an additional reward at the end of the mission.', maxCount: 1 },
  { id: 'hospital', name: 'Hospital', image: '/images/hospital.png', cost: 520, description: 'Level 1: HQ defenders +1 health. Level 2: HQ defenders +1 health and +1 attack.', maxCount: 1 },
  { id: 'hospital_upgraded', name: 'Upgraded Hospital', image: '/images/hospital_upgraded.png', cost: 0, description: 'Level 1: HQ defenders +1 health. Level 2: HQ defenders +1 health and +1 attack.', maxCount: 1 },
  { id: 'airfield', name: 'Airfield', image: '/images/airfield.png', cost: 720, description: 'Reduce deployment costs by 20%.', maxCount: 1 }
];

// Sound for clicking each placed building (building id -> /sounds/<name>.mp3)
const BUILDING_CLICK_SOUNDS = {
  airfield: '/sounds/airfield.mp3',
  bank: '/sounds/bank.mp3',
  cattle_ranch: '/sounds/cattle.mp3',
  command_center: '/sounds/command.mp3',
  eclipse_array: '/sounds/eclipse.mp3',
  eclipse_array_upgraded: '/sounds/eclipse.mp3',
  hospital: '/sounds/hospital.mp3',
  hospital_upgraded: '/sounds/hospital.mp3',
  intelligence_agency: '/sounds/intel.mp3',
  lavender_field: '/sounds/lavender.mp3',
  portal: '/sounds/portal.mp3',
  powerplant: '/sounds/powerplant.mp3',
  powerplant_upgraded: '/sounds/powerplant.mp3',
  quarry: '/sounds/quarry.mp3',
  research_center: '/sounds/research.mp3',
  sewers: '/sounds/sewers.mp3',
  training_grounds: '/sounds/training.mp3',
  training_grounds_upgraded: '/sounds/training.mp3',
  barracks: '/sounds/barracks.mp3',
  barracks_upgraded: '/sounds/barracks.mp3',
  armory: '/sounds/armory.mp3',
  armory_upgraded: '/sounds/armory.mp3',
  drone_hangar: '/sounds/dronehangar.mp3',
  drone_hangar_upgraded: '/sounds/dronehangar.mp3',
  communications_hub: '/sounds/comhub.mp3',
  communications_hub_upgraded: '/sounds/comhub.mp3'
};

const BUILDING_UPGRADES = {
  powerplant: { upgradedId: 'powerplant_upgraded', cost: 200 },
  eclipse_array: { upgradedId: 'eclipse_array_upgraded', cost: 350 },
  training_grounds: { upgradedId: 'training_grounds_upgraded', cost: 800 },
  drone_hangar: { upgradedId: 'drone_hangar_upgraded', cost: 400 },
  communications_hub: { upgradedId: 'communications_hub_upgraded', cost: 600 },
  barracks: { upgradedId: 'barracks_upgraded', cost: 500 },
  armory: { upgradedId: 'armory_upgraded', cost: 450 },
  hospital: { upgradedId: 'hospital_upgraded', cost: 900 }
};

/** Buildings that share a cap: base + upgraded count toward the same limit */
const BUILDING_CAP_GROUPS = {
  powerplant: ['powerplant', 'powerplant_upgraded'],
  eclipse_array: ['eclipse_array', 'eclipse_array_upgraded'],
  training_grounds: ['training_grounds', 'training_grounds_upgraded'],
  hospital: ['hospital', 'hospital_upgraded'],
  barracks: ['barracks', 'barracks_upgraded'],
  armory: ['armory', 'armory_upgraded'],
  drone_hangar: ['drone_hangar', 'drone_hangar_upgraded'],
  communications_hub: ['communications_hub', 'communications_hub_upgraded']
};

/** HQ build menu tabs (fixed order). Base building ids only (upgraded variants are not separate menu rows). */
const BUILD_MENU_TAB_KEYS = ['economy', 'orderGeneration', 'defensive', 'espionage'];

const BUILD_MENU_TAB_BASE_IDS = {
  economy: ['airfield', 'bank', 'powerplant', 'research_center'],
  orderGeneration: ['armory', 'barracks', 'communications_hub', 'drone_hangar', 'training_grounds'],
  defensive: ['cattle_ranch', 'command_center', 'hospital', 'lavender_field', 'portal', 'quarry', 'sewers'],
  espionage: ['eclipse_array', 'intelligence_agency']
};

(function validateBuildMenuTabCoverage() {
  const visibleMenuIds = new Set(
    HQ_BUILDINGS.filter((b) => !Object.values(BUILDING_UPGRADES).some((u) => u.upgradedId === b.id)).map((b) => b.id)
  );
  const categorized = new Set();
  for (const tabKey of BUILD_MENU_TAB_KEYS) {
    const ids = BUILD_MENU_TAB_BASE_IDS[tabKey];
    if (!ids || ids.length === 0) {
      throw new Error(`War Effort build menu: empty or missing id list for tab "${tabKey}"`);
    }
    for (const id of ids) {
      if (categorized.has(id)) {
        throw new Error(`War Effort build menu: building id "${id}" assigned to more than one tab`);
      }
      categorized.add(id);
    }
  }
  for (const id of visibleMenuIds) {
    if (!categorized.has(id)) {
      throw new Error(`War Effort build menu: no tab category for building id "${id}"`);
    }
  }
  for (const id of categorized) {
    if (!visibleMenuIds.has(id)) {
      throw new Error(`War Effort build menu: tab references unknown or non-menu building id "${id}"`);
    }
  }
})();

/** Inline SVGs for HQ build menu tabs (currentColor, matches tab label styling). */
function WarEffortBuildMenuTabIcon({ tabKey }) {
  const cls = 'war-effort-build-menu-tab__icon';
  const stroke = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round'
  };
  switch (tabKey) {
    case 'economy':
      return (
        <svg className={cls} viewBox="0 0 24 24" aria-hidden>
          <line x1="12" y1="1" x2="12" y2="23" {...stroke} />
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" {...stroke} />
        </svg>
      );
    case 'orderGeneration':
      return (
        <svg className={cls} viewBox="0 0 24 24" aria-hidden>
          <path d="M12 5v2M9 3l1.5 2M15 3l-1.5 2" {...stroke} />
          <circle cx="12" cy="10" r="3" {...stroke} />
          <path d="M9 21v-5a3 3 0 0 1 6 0v5" {...stroke} />
          <path d="M15 12h5.5l1 2h-3l-1.5-2" {...stroke} />
          <path d="M20.5 14v4" {...stroke} />
        </svg>
      );
    case 'defensive':
      return (
        <svg className={cls} viewBox="0 0 24 24" aria-hidden>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" {...stroke} />
        </svg>
      );
    case 'espionage':
      return (
        <svg className={cls} viewBox="0 0 24 24" aria-hidden>
          <ellipse cx="12" cy="6.5" rx="8" ry="2" {...stroke} />
          <path d="M5 6.5h14v3H5z" fill="currentColor" stroke="none" />
          <circle cx="9.5" cy="14" r="2" {...stroke} />
          <circle cx="14.5" cy="14" r="2" {...stroke} />
          <path d="M11.5 14h1" {...stroke} />
          <path d="M8.5 18.5c1.2 1.8 2.8 2.5 3.5 2.5s2.3-.7 3.5-2.5" {...stroke} />
        </svg>
      );
    default:
      throw new Error(`WarEffortBuildMenuTabIcon: unknown tabKey "${tabKey}"`);
  }
}

function getBuildingCapInfo(buildingId) {
  const baseId = buildingId.endsWith('_upgraded') ? buildingId.replace(/_upgraded$/, '') : buildingId;
  const groupIds = BUILDING_CAP_GROUPS[baseId] || [baseId];
  const baseBuilding = HQ_BUILDINGS.find((b) => b.id === baseId);
  const cap = baseBuilding?.maxCount ?? 999;
  return { groupIds, cap };
}

const BUILDINGS_WITH_PRODUCER_UI = ['barracks', 'barracks_upgraded', 'armory', 'armory_upgraded', 'drone_hangar', 'drone_hangar_upgraded', 'communications_hub', 'communications_hub_upgraded'];
const COMPLETION_BUILDING_IDS = ['lavender_field', 'cattle_ranch', 'quarry', 'sewers', 'portal'];

/**
 * Stable key for lastOpponentAction so we can compare by content, not reference.
 * Server sends an object each time (new reference); we only play sound when content actually changed.
 */
function getStableOpponentActionKey(data) {
  if (data == null) return null;
  if (typeof data === 'string') return data.trim() || null;
  if (data.type === 'subversion') {
    return `subversion:${data.username || ''}:${data.countryName || ''}:${data.countryCellId ?? ''}`;
  }
  if (data.type === 'attack') {
    return `attack:${data.username || ''}:${data.countryName || ''}:${data.countryCellId ?? ''}:${data.won === true}`;
  }
  return null;
}

function getLastOpponentActionDisplay(data, stateCellId, translate) {
  if (data == null) return null;
  if (typeof data === 'string') return { message: data, cellId: stateCellId ?? null };
  if (data.type === 'subversion') {
    return {
      message: translate('warEffort.lastOpponentSubversion', { username: data.username, country: data.countryName || '' }),
      cellId: data.countryCellId ?? stateCellId ?? null
    };
  }
  if (data.type === 'attack') {
    const won = data.won === true;
    const country = data.countryName || '';
    const message = country
      ? translate(won ? 'warEffort.lastOpponentAttackedAndWon' : 'warEffort.lastOpponentAttackedAndLost', { username: data.username, country })
      : translate(won ? 'warEffort.lastOpponentAttackedAndWonNoCountry' : 'warEffort.lastOpponentAttackedAndLostNoCountry', { username: data.username });
    return { message, cellId: data.countryCellId ?? stateCellId ?? null };
  }
  return null;
}

function WarEffortFortification({ mission, user, onBack, onFortificationComplete, onLogout, showBuildingPage, onBuildingPageChange }) {
  const { t, i18n: i18nInstance } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [hqBuildingCountryName, setHqBuildingCountryName] = useState('');
  const [showBuildMenu, setShowBuildMenu] = useState(false);
  const [buildMenuTab, setBuildMenuTab] = useState('economy');
  const [buildingForPlacement, setBuildingForPlacement] = useState(null);
  const [placementMouse, setPlacementMouse] = useState({ x: 0, y: 0 });
  const [placedBuildings, setPlacedBuildings] = useState([]);
  const [selectedPlacementId, setSelectedPlacementId] = useState(null);
  const [collectSparkPlacementId, setCollectSparkPlacementId] = useState(null);
  const [collectSparkAmount, setCollectSparkAmount] = useState(null);
  const [trainingResult, setTrainingResult] = useState(null);
  const [barracksOrdersModal, setBarracksOrdersModal] = useState({ show: false, placementId: null, buildingId: null, orders: [], loading: false });
  const [barracksOrdersModalSelectedIds, setBarracksOrdersModalSelectedIds] = useState([]);
  const [barracksEnrichedOrders, setBarracksEnrichedOrders] = useState([]);
  const [attackStrategyModal, setAttackStrategyModal] = useState({ show: false, loading: false, strategy: null, deactivatedSlots: [] });
  const [attackStrategyTooltipRect, setAttackStrategyTooltipRect] = useState(null);
  const [placementRefreshTrigger, setPlacementRefreshTrigger] = useState(0);
  const [powerplantFlashPlacements, setPowerplantFlashPlacements] = useState([]);
  const [unlockedBuildingIds, setUnlockedBuildingIds] = useState(['powerplant', 'barracks']);
  // Per image src: aspect ratio (width/height) from naturalWidth/naturalHeight on load; used to size producer image area so progress bar sits at true image bottom
  const [buildingImageAspectRatio, setBuildingImageAspectRatio] = useState({});
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedFortification, setSelectedFortification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [attackDialog, setAttackDialog] = useState({ show: false, countryCellId: null, fortificationType: null, countryName: null, selectedStrategyId: null });
  const [enemyHqAttackInfo, setEnemyHqAttackInfo] = useState(null);
  const [attackEnemyHQDialog, setAttackEnemyHQDialog] = useState({ show: false, countryName: null, hqCellId: null, movementCost: null, selectedStrategyId: null, defenderUserId: null });
  const [attackOtherCountryDialog, setAttackOtherCountryDialog] = useState({ show: false, countryCellId: null, countryName: null, ownerFaction: null, selectedStrategyId: null, isOpponentUnfortified: false });
  const [ownedCountryChoice, setOwnedCountryChoice] = useState(null);
  const [tectonicBoredriveCost, setTectonicBoredriveCost] = useState(null);
  const [tectonicBoredriveLoading, setTectonicBoredriveLoading] = useState(false);
  const [tectonicBoredriveResult, setTectonicBoredriveResult] = useState(null);
  const [ownedHQChoice, setOwnedHQChoice] = useState(null);
  const [tradingHubOpen, setTradingHubOpen] = useState(false);
  const [tradingHubStatus, setTradingHubStatus] = useState(null);
  const [tradingHubLoading, setTradingHubLoading] = useState(false);
  const [tradingHubProtectCellId, setTradingHubProtectCellId] = useState(null);
  const [tradingHubSubvertCellId, setTradingHubSubvertCellId] = useState(null);
  const [shopSlotCellId, setShopSlotCellId] = useState({}); // { slotIndex: cellId } for shop items that need a target (Protect, Subvert, Structural Exposure, Speedpass)
  const [shopCountdownTick, setShopCountdownTick] = useState(() => Date.now()); // tick every 1s to update countdown display and refetch when slot reveal time reached
  const [protectedCellId, setProtectedCellId] = useState(null); // Trading Hub protected country (from mission-status)
  const [tradingHubBoosterOrders, setTradingHubBoosterOrders] = useState(null); // 3 orders shown after buying booster bundle
  const [gambleWonItem, setGambleWonItem] = useState(null); // { id, nameKey, descriptionKey, image } when player buys Gamble in Trading Hub
  const [achievementUnlocked, setAchievementUnlocked] = useState(null);
  const { current: pendingToast, dismissCurrent: dismissPendingToast } = usePendingAchievementToasts();
  const [energyCooldownDisplaySeconds, setEnergyCooldownDisplaySeconds] = useState(null); // live countdown for free energy (updated every second)
  const [reconquerCooldowns, setReconquerCooldowns] = useState([]); // [{ cell_id, remaining_seconds }] - countries we cannot reconquer yet
  const [cellsUnderAttack, setCellsUnderAttack] = useState([]); // Cell IDs with an active battle (crimson glow, block duplicate attack)
  const [trainingGroundsOrdersToKeep, setTrainingGroundsOrdersToKeep] = useState(1); // 1, 2, or 3 - from mission-status
  const [lastPlayerAction, setLastPlayerAction] = useState(null); // Last action for Your Purpose box (e.g. "You have just attacked Austria and won.")
  const [lastPlayerActionCellId, setLastPlayerActionCellId] = useState(null); // Cell ID for hover highlight on map (mission/last action)
  const [lastOpponentAction, setLastOpponentAction] = useState(null); // Opponent's last attack (country + outcome), same style as player; human or bot
  const [lastOpponentActionCellId, setLastOpponentActionCellId] = useState(null); // Cell ID for hover highlight on map
  const [opponentHqBuildingIds, setOpponentHqBuildingIds] = useState([]); // Opponent HQ building IDs for tooltip (1v1)
  const [opponentHqBuildingIdsByUser, setOpponentHqBuildingIdsByUser] = useState({}); // FFA/2v2: { [userId]: building_id[] } for tooltip
  const [viewerHasEclipseArrayUpgraded, setViewerHasEclipseArrayUpgraded] = useState(false); // Eclipse Array: show opponent fortification bonuses in tooltip only when true (from mission-status)
  const [viewerHasIntelligenceAgency, setViewerHasIntelligenceAgency] = useState(false); // Intelligence Agency: show opponent attack targets in info box (from mission-status)
  const [opponentAttackTargets, setOpponentAttackTargets] = useState([]); // [{ userId, username, countryCellId, countryName }] — only when viewer has Intelligence Agency
  const [activeShopEffects, setActiveShopEffects] = useState([]); // Trading Hub effects bought and still in effect: [{ effectType, expiresAt?, remainingTicks? }]
  const [devBotEnergy, setDevBotEnergy] = useState(null); // Dev: bot energy (when mission has 1v1 or FFA bot)
  const [devBotCurrentAttack, setDevBotCurrentAttack] = useState(null); // Dev: { countryCellId, countryName } when bot is attacking
  const [devHasBot, setDevHasBot] = useState(false); // Dev: mission has a bot slot (so we show bot status block)
  const [fortifiedCountryUnderAttackWarning, setFortifiedCountryUnderAttackWarning] = useState(null); // { countryNames: string[] } - red box for 5s
  const fortifiedCountryWarningTimerRef = useRef(null);
  const [attackFromCountry, setAttackFromCountry] = useState(null);
  const [attackModeTargetCost, setAttackModeTargetCost] = useState(null);
  const [missionSubmissionHoverCellId, setMissionSubmissionHoverCellId] = useState(null);
  const [expandedInfoSlots, setExpandedInfoSlots] = useState([]); // Array of slot numbers (1-4) that are expanded; multiple can be open at once
  const [ceaseFireRemainingSeconds, setCeaseFireRemainingSeconds] = useState(0);
  const [opponentFortifiedCeaseFireRemainingSeconds, setOpponentFortifiedCeaseFireRemainingSeconds] = useState(0);
  const [conquestAttackerCeaseFireRemainingSeconds, setConquestAttackerCeaseFireRemainingSeconds] = useState(0);
  const [defenseStrategyDialog, setDefenseStrategyDialog] = useState({
    show: false,
    cellId: null,
    countryName: null,
    selectedStrategyId: null,
    useEverywhere: false,
    loading: false,
    saving: false
  });
  const [ownedCountryDefenseStrategyId, setOwnedCountryDefenseStrategyId] = useState(null);
  const [ownedCountryDefenseStrategyLoading, setOwnedCountryDefenseStrategyLoading] = useState(false);
  const [showMadagascarTradeHint, setShowMadagascarTradeHint] = useState(false);
  const madagascarHintTimerRef = useRef(null);
  const madagascarHintHideTimerRef = useRef(null);
  const openTradingHubAfterMapRef = useRef(false);
  const openBuildMenuAfterBuildingPageRef = useRef(false);
  const [worldMapResetTrigger, setWorldMapResetTrigger] = useState(0);
  const errorAutoCloseTimerRef = useRef(null);
  // When user returns from AfterBattle after dismissing, skip redirect until parent clears mission (avoids redirect loop when replace clears location.state)
  const skipRedirectForMissionIdRef = useRef(null);
  const prevEnergyRef = useRef(null);
  const lastOpponentActionRef = useRef(null);
  const lastOpponentActionKeyRef = useRef(null);
  const tradingHubAmbienceRef = useRef(null);
  // Keep last displayable neutral_fortifications so map never flashes empty when state is briefly overwritten (e.g. on tick).
  const neutralFortificationsStableRef = useRef(null);
  // Auto-close red error messages (e.g. "You do not own this country") after 5 seconds
  useEffect(() => {
    if (error == null) return;
    if (errorAutoCloseTimerRef.current) clearTimeout(errorAutoCloseTimerRef.current);
    errorAutoCloseTimerRef.current = setTimeout(() => {
      setError(null);
      errorAutoCloseTimerRef.current = null;
    }, 5000);
    return () => {
      if (errorAutoCloseTimerRef.current) {
        clearTimeout(errorAutoCloseTimerRef.current);
        errorAutoCloseTimerRef.current = null;
      }
    };
  }, [error]);
  // Stop trading hub ambience when leaving the hub
  useEffect(() => {
    if (!tradingHubOpen && tradingHubAmbienceRef.current) {
      tradingHubAmbienceRef.current.pause();
      tradingHubAmbienceRef.current.currentTime = 0;
      tradingHubAmbienceRef.current = null;
    }
  }, [tradingHubOpen]);
  // When user clicks Trading from building page: switch to world map first, then open Trading Hub
  useEffect(() => {
    if (!showBuildingPage && openTradingHubAfterMapRef.current) {
      openTradingHubAfterMapRef.current = false;
      setTradingHubOpen(true);
    }
  }, [showBuildingPage]);
  // When user clicks Build from world map: switch to building page first, then open build menu
  useEffect(() => {
    if (showBuildingPage && openBuildMenuAfterBuildingPageRef.current) {
      openBuildMenuAfterBuildingPageRef.current = false;
      setShowBuildMenu(true);
    }
  }, [showBuildingPage]);
  useEffect(() => {
    if (showBuildMenu) {
      setBuildMenuTab('economy');
    }
  }, [showBuildMenu]);
  const buildMenuBuildingsForTab = useMemo(() => {
    const orderInTab = BUILD_MENU_TAB_BASE_IDS[buildMenuTab];
    if (!orderInTab) {
      throw new Error(`War Effort build menu: invalid tab "${buildMenuTab}"`);
    }
    const indexMap = new Map(orderInTab.map((id, i) => [id, i]));
    return [...HQ_BUILDINGS]
      .filter((b) => !Object.values(BUILDING_UPGRADES).some((u) => u.upgradedId === b.id))
      .filter((b) => indexMap.has(b.id))
      .sort((a, b) => indexMap.get(a.id) - indexMap.get(b.id));
  }, [buildMenuTab]);
  // Format initial mission data
  const formatMissionData = (missionData) => {
    if (!missionData) return null;
    const formatted = { ...missionData };
    // Handle PostgreSQL array format for fortified countries
    if (formatted.player1_fortified_countries && !Array.isArray(formatted.player1_fortified_countries)) {
      if (typeof formatted.player1_fortified_countries === 'string') {
        const cleaned = formatted.player1_fortified_countries.replace(/[{}]/g, '');
        formatted.player1_fortified_countries = cleaned ? cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id)) : [];
      } else {
        formatted.player1_fortified_countries = [];
      }
    }
    if (formatted.player2_fortified_countries && !Array.isArray(formatted.player2_fortified_countries)) {
      if (typeof formatted.player2_fortified_countries === 'string') {
        const cleaned = formatted.player2_fortified_countries.replace(/[{}]/g, '');
        formatted.player2_fortified_countries = cleaned ? cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id)) : [];
      } else {
        formatted.player2_fortified_countries = [];
      }
    }
    for (const key of ['player3_fortified_countries', 'player4_fortified_countries']) {
      if (formatted[key] && !Array.isArray(formatted[key])) {
        if (typeof formatted[key] === 'string') {
          const cleaned = formatted[key].replace(/[{}]/g, '');
          formatted[key] = cleaned ? cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id)) : [];
        } else {
          formatted[key] = [];
        }
      }
    }
    return formatted;
  };
  
  const [missionData, setMissionData] = useState(() => formatMissionData(mission));

  // Get user ID from JWT token (declared before effects that depend on it)
  const userId = useMemo(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const decoded = jwtDecode(token);
        return decoded.userId ? String(decoded.userId) : null;
      }
    } catch (error) {
      console.error('Error decoding token:', error);
    }
    return localStorage.getItem('userId');
  }, []);

  // Helper: neutral_fortifications is "valid" for map display only if at least one cell has count 1–3 (same as neutralFortificationCountMap).
  const hasDisplayableNeutrals = (obj) => {
    if (!obj || typeof obj !== 'object') return false;
    return Object.values(obj).some((d) => (d && typeof d === 'object' && Number(d.count) >= 1 && Number(d.count) <= 3));
  };

  // Sync mission prop into local state when parent updates. Never overwrite existing neutral_fortifications with empty or non-displayable prop (tick broadcast can send empty; backend returns 22 with count 1–3).
  // Also preserve viewer/opponent_command_center_boost_cell_ids and viewer_engineers_available from prev when prop doesn't have them (prop often comes from active-mission list which doesn't include these).
  useEffect(() => {
    if (mission) {
      const formatted = formatMissionData(mission);
      if (formatted) {
        setMissionData((prev) => {
          if (!prev) return formatted;
          const prevNeutrals = prev.neutral_fortifications;
          const propNeutrals = formatted.neutral_fortifications;
          const prevHasNeutrals = hasDisplayableNeutrals(prevNeutrals);
          const propHasNeutrals = hasDisplayableNeutrals(propNeutrals);
          let next = formatted;
          if (prevHasNeutrals && !propHasNeutrals) next = { ...next, neutral_fortifications: prevNeutrals };
          const hasValidViewerCc = Array.isArray(formatted.viewer_command_center_boost_cell_ids) && formatted.viewer_command_center_boost_cell_ids.length > 0;
          const hasValidOppCc = Array.isArray(formatted.opponent_command_center_boost_cell_ids) && formatted.opponent_command_center_boost_cell_ids.length > 0;
          if (!hasValidViewerCc && Array.isArray(prev.viewer_command_center_boost_cell_ids) && prev.viewer_command_center_boost_cell_ids.length > 0) {
            next = { ...next, viewer_command_center_boost_cell_ids: prev.viewer_command_center_boost_cell_ids };
          }
          if (!hasValidOppCc && Array.isArray(prev.opponent_command_center_boost_cell_ids) && prev.opponent_command_center_boost_cell_ids.length > 0) {
            next = { ...next, opponent_command_center_boost_cell_ids: prev.opponent_command_center_boost_cell_ids };
          }
          if (next.viewer_engineers_available == null && userId) {
            const uid = Number(userId);
            const slot = uid === Number(next.player1_id) ? 1 : uid === Number(next.player2_id) ? 2 : uid === Number(next.player3_id) ? 3 : uid === Number(next.player4_id) ? 4 : null;
            if (slot != null && next[`player${slot}_engineers_available`] != null) next.viewer_engineers_available = Number(next[`player${slot}_engineers_available`]);
            else if (prev.viewer_engineers_available != null) next.viewer_engineers_available = prev.viewer_engineers_available;
          }
          return next;
        });
      }
    }
  }, [mission, userId]);

  // On entering world map: fetch full mission (GET includes neutral_fortifications) so neutrals are visible immediately.
  // Cache-bust so we never get a cached response from before neutrals were assigned.
  const missionIdForFetch = mission?.id ?? missionData?.id;
  useEffect(() => {
    if (!missionIdForFetch) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    const url = `${API_URL}/war-effort/mission/${missionIdForFetch}?t=${Date.now()}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache', Pragma: 'no-cache' } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        const formatted = formatMissionData(data);
        if (formatted) {
          setMissionData((prev) => {
            const hasValidViewerCc = Array.isArray(formatted.viewer_command_center_boost_cell_ids) && formatted.viewer_command_center_boost_cell_ids.length > 0;
            const hasValidOppCc = Array.isArray(formatted.opponent_command_center_boost_cell_ids) && formatted.opponent_command_center_boost_cell_ids.length > 0;
            if (hasValidViewerCc && hasValidOppCc) {
              if (formatted.viewer_engineers_available == null && userId) {
                const uid = Number(userId);
                const slot = uid === Number(formatted.player1_id) ? 1 : uid === Number(formatted.player2_id) ? 2 : uid === Number(formatted.player3_id) ? 3 : uid === Number(formatted.player4_id) ? 4 : null;
                if (slot != null && formatted[`player${slot}_engineers_available`] != null) formatted.viewer_engineers_available = Number(formatted[`player${slot}_engineers_available`]);
              }
              return formatted;
            }
            if (!prev) return formatted;
            const next = { ...formatted };
            if (!hasValidViewerCc && Array.isArray(prev.viewer_command_center_boost_cell_ids) && prev.viewer_command_center_boost_cell_ids.length > 0) {
              next.viewer_command_center_boost_cell_ids = prev.viewer_command_center_boost_cell_ids;
            }
            if (!hasValidOppCc && Array.isArray(prev.opponent_command_center_boost_cell_ids) && prev.opponent_command_center_boost_cell_ids.length > 0) {
              next.opponent_command_center_boost_cell_ids = prev.opponent_command_center_boost_cell_ids;
            }
            if (next.viewer_engineers_available == null) {
              const uid = userId ? Number(userId) : null;
              const slot = uid != null && uid === Number(next.player1_id) ? 1 : uid === Number(next.player2_id) ? 2 : uid === Number(next.player3_id) ? 3 : uid === Number(next.player4_id) ? 4 : null;
              if (slot != null && next[`player${slot}_engineers_available`] != null) next.viewer_engineers_available = Number(next[`player${slot}_engineers_available`]);
              else if (prev?.viewer_engineers_available != null) next.viewer_engineers_available = prev.viewer_engineers_available;
            }
            return next;
          });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [missionIdForFetch, userId]);

  // Fallback: if we have a mission but no neutral_fortifications yet, fetch and merge them (covers race where prop/initial fetch didn't have them)
  const hasNeutrals = missionData?.neutral_fortifications && typeof missionData.neutral_fortifications === 'object' && Object.keys(missionData.neutral_fortifications).length > 0;
  useEffect(() => {
    if (!missionData?.id || hasNeutrals) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const requestedId = missionData.id;
    let cancelled = false;
    const url = `${API_URL}/war-effort/mission/${requestedId}?t=${Date.now()}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache', Pragma: 'no-cache' } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data || data.id !== requestedId) return;
        const neutrals = data.neutral_fortifications;
        if (neutrals && typeof neutrals === 'object' && Object.keys(neutrals).length > 0) {
          setMissionData((prev) => prev && prev.id === requestedId ? { ...prev, neutral_fortifications: neutrals } : prev);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [missionData?.id, hasNeutrals]);

  // Fetch Eclipse Array status once when mission loads so tooltip masking is correct before first poll
  useEffect(() => {
    if (!missionData?.id) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_URL}/war-effort/mission-status/${missionData.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) {
          if (typeof data.viewerHasEclipseArrayUpgraded === 'boolean') setViewerHasEclipseArrayUpgraded(data.viewerHasEclipseArrayUpgraded);
          if (typeof data.viewerHasIntelligenceAgency === 'boolean') setViewerHasIntelligenceAgency(data.viewerHasIntelligenceAgency);
          if (Array.isArray(data.opponentAttackTargets)) setOpponentAttackTargets(data.opponentAttackTargets);
        }
      })
      .catch(() => {});
  }, [missionData?.id]);

  const handleAttackStrategyToggleSlot = useCallback((orderId, occurrence) => {
    const token = localStorage.getItem('token');
    if (!token || !missionData?.id) return;
    fetch(`${API_URL}/war-effort/mission/${missionData.id}/attack-strategy/toggle-slot`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ orderId, occurrence })
    })
      .then((res) => res.ok ? res.json() : Promise.reject(new Error('Failed to toggle')))
      .then(() => {
        return fetch(`${API_URL}/war-effort/mission/${missionData.id}/attack-strategy`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Failed to fetch'))))
      .then((data) => setAttackStrategyModal((prev) => ({ ...prev, strategy: data.strategy, deactivatedSlots: data.deactivatedSlots || [] })))
      .catch((err) => console.error('Attack strategy toggle error:', err));
  }, [missionData?.id]);
  const [controlledCountries, setControlledCountries] = useState([]);
  const [warEffortData, setWarEffortData] = useState(null);
  const [playerResource, setPlayerResource] = useState(null);
  // Play energy_increase.mp3 when energy goes up (tick, Trading Hub claim, etc.)
  useEffect(() => {
    const amount = playerResource?.amount;
    if (typeof amount !== 'number') return;
    const prev = prevEnergyRef.current;
    if (prev !== null && amount > prev) {
      playSoundEffect('/sounds/energy_increase.mp3');
    }
    prevEnergyRef.current = amount;
  }, [playerResource?.amount]);
  // Ref: stable key of the last opponent action we already played sound for (so we never repeat for same log message)
  const lastOpponentActionSoundPlayedRef = useRef(null);
  // Ref: have we run the opponent-action effect at least once (skip sound on first run = no sound on refresh/initial load)
  const opponentActionEffectHasRunRef = useRef(false);
  // Reset when mission changes so a new mission can play sound for its first opponent action
  useEffect(() => {
    lastOpponentActionRef.current = null;
    lastOpponentActionSoundPlayedRef.current = null;
    lastOpponentActionKeyRef.current = null;
    opponentActionEffectHasRunRef.current = false;
  }, [missionData?.id]);
  // Play opponent_news_update.mp3 exactly once per distinct action. Do not play on initial load or refresh (only when the message changes while the user is on the page).
  useEffect(() => {
    const key = getStableOpponentActionKey(lastOpponentAction);
    if (!key) {
      lastOpponentActionRef.current = null;
      return;
    }
    lastOpponentActionRef.current = lastOpponentAction;
    const prevKey = lastOpponentActionSoundPlayedRef.current;
    lastOpponentActionSoundPlayedRef.current = key;
    const isFirstRun = !opponentActionEffectHasRunRef.current;
    opponentActionEffectHasRunRef.current = true;
    if (!isFirstRun && prevKey !== key) {
      playSoundEffect('/sounds/opponent_news_update.mp3');
    }
  }, [lastOpponentAction]);
  const [buildingCostMultiplier, setBuildingCostMultiplier] = useState(1); // Taiwan: 0.8 = 20% off; Infrastructure Subsidy: *0.45
  const [hasInfrastructureSubsidy, setHasInfrastructureSubsidy] = useState(false); // Trading Hub: next build 55% off until used
  const [hoveredCell, setHoveredCell] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [completionBonusStatus, setCompletionBonusStatus] = useState({});
  const [completionBonusLoadingCell, setCompletionBonusLoadingCell] = useState(null);
  const [opponentUsername, setOpponentUsername] = useState(null); // 1v1/2v2: single name
  const [opponentUsernames, setOpponentUsernames] = useState([]);  // FFA: list of opponent display names
  const [userStrategies, setUserStrategies] = useState([]);
  const [selectedStrategyId, setSelectedStrategyId] = useState(null);
  
  // Dev mode state (only visible for player1)
  // Check if current user is player1 (same pattern as BattleOverview.js)
  const devUserId = typeof user === 'string' ? user : (user?.id || user?.username);
  const isDevUser = devUserId === 'player1' || devUserId === 1 || user?.username === 'player1';
  
  const [devModeActive, setDevModeActive] = useState(false);
  const [devSelectedPurpose, setDevSelectedPurpose] = useState(null);
  const [devShowFortifications, setDevShowFortifications] = useState(false);
  const [devCommandCenterCells, setDevCommandCenterCells] = useState({ byPurpose: {}, allCells: [] });
  const [devLoading, setDevLoading] = useState(false);
  const [devSimulateOpponentLoading, setDevSimulateOpponentLoading] = useState(false);
  const [devEndCeaseFireLoading, setDevEndCeaseFireLoading] = useState(false);
  const [devResetEnergyLoading, setDevResetEnergyLoading] = useState(false);
  // Pending changes in dev mode (cellId -> factionName or null)
  // These are NOT saved to DB until "Store as Default" is clicked
  const [devPendingOwners, setDevPendingOwners] = useState({});
  // Transfer energy to ally (2v2): modal open state and loading
  const [transferEnergyModalOpen, setTransferEnergyModalOpen] = useState(false);
  const [transferEnergyLoading, setTransferEnergyLoading] = useState(false);
  const [showNeutralFortifiedPopup, setShowNeutralFortifiedPopup] = useState(false);
  
  // Viewer slot (1–4): which of player1_id..player4_id is the current user. Used for FFA/2v2 fortified display.
  const viewerSlot = useMemo(() => {
    if (!userId || !missionData) return null;
    const uid = Number(userId);
    if (Number(missionData.player1_id) === uid) return 1;
    if (missionData.player2_id != null && Number(missionData.player2_id) === uid) return 2;
    if (missionData.player3_id != null && Number(missionData.player3_id) === uid) return 3;
    if (missionData.player4_id != null && Number(missionData.player4_id) === uid) return 4;
    return null;
  }, [userId, missionData?.player1_id, missionData?.player2_id, missionData?.player3_id, missionData?.player4_id]);

  // Verify that the current user is actually part of this mission
  const isUserInMission = useMemo(() => {
    if (!userId || !missionData) return false;
    const userIdNum = Number(userId);
    return [missionData.player1_id, missionData.player2_id, missionData.player3_id, missionData.player4_id]
      .filter(Boolean).some(id => Number(id) === userIdNum);
  }, [userId, missionData?.player1_id, missionData?.player2_id, missionData?.player3_id, missionData?.player4_id]);

  // Compare as numbers to handle type mismatches - memoize to prevent unnecessary recalculations
  const isPlayer1 = useMemo(() => {
    return userId ? Number(missionData?.player1_id) === Number(userId) : false;
  }, [userId, missionData?.player1_id]);

  // Viewer's purpose (for tooltip, HQ detection, etc.). FFA/2v2: use viewerSlot; 1v1: use isPlayer1.
  const viewerPurpose = useMemo(() => {
    const slot = viewerSlot;
    const mode = missionData?.mission_mode;
    if ((mode === 'ffa' || mode === '2v2') && slot >= 1 && slot <= 4) {
      return missionData?.[`player${slot}_purpose`] ?? null;
    }
    return isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose;
  }, [viewerSlot, isPlayer1, missionData?.mission_mode, missionData?.player1_purpose, missionData?.player2_purpose, missionData?.player3_purpose, missionData?.player4_purpose]);

  // Engineer count for UI: use viewer_engineers_available or derive from player{N}_engineers_available. Never default to 3 so we only show 3 when it is really 3.
  const engineersForDisplay = useMemo(() => {
    if (!missionData) return undefined;
    const v = missionData.viewer_engineers_available;
    if (v != null && v !== '') return Number(v);
    if (viewerSlot != null) {
      const p = missionData[`player${viewerSlot}_engineers_available`];
      if (p != null && p !== '') return Number(p);
    }
    return undefined;
  }, [missionData?.viewer_engineers_available, missionData?.player1_engineers_available, missionData?.player2_engineers_available, missionData?.player3_engineers_available, missionData?.player4_engineers_available, viewerSlot]);

  // 2v2: show "Transfer Energy" when current user has an ally (other teammate)
  const showTransferEnergy = useMemo(() => {
    if (missionData?.mission_mode !== '2v2' || !userId) return false;
    const uid = Number(userId);
    const p1 = missionData.player1_id != null ? Number(missionData.player1_id) : null;
    const p2 = missionData.player2_id != null ? Number(missionData.player2_id) : null;
    const p3 = missionData.player3_id != null ? Number(missionData.player3_id) : null;
    const p4 = missionData.player4_id != null ? Number(missionData.player4_id) : null;
    return (uid === p1 && p2 != null) || (uid === p2 && p1 != null) || (uid === p3 && p4 != null) || (uid === p4 && p3 != null);
  }, [missionData?.mission_mode, missionData?.player1_id, missionData?.player2_id, missionData?.player3_id, missionData?.player4_id, userId]);
  
  // Calculate command center eligible cells based on current + pending owners
  const calculateDevCommandCenterCells = useCallback(() => {
    if (!devShowFortifications || !warEffortData?.cells) return { byPurpose: {}, allCells: [] };
    
    // Build merged owner map: base data + pending changes
    const mergedOwners = {};
    warEffortData.cells.forEach(cell => {
      const cellId = String(cell.cell_id);
      if (devPendingOwners.hasOwnProperty(cellId)) {
        mergedOwners[cellId] = devPendingOwners[cellId];
      } else {
        mergedOwners[cellId] = cell.owner_name || null;
      }
    });
    
    const commandCenterCells = {};
    const purposes = ['Consume', 'Eradicate', 'Colonize', 'Protect', 'Survive'];
    
    for (const purpose of purposes) {
      const factionName = DEV_PURPOSE_TO_FACTION[purpose];
      commandCenterCells[purpose] = [];
      
      for (const [cellIdStr, ownerFaction] of Object.entries(mergedOwners)) {
        if (ownerFaction !== factionName) continue;
        
        const cellId = Number(cellIdStr);
        const neighbourCellIds = getNeighbourCellIds(cellId);
        const ownedNeighbourCount = neighbourCellIds.filter(nId => 
          mergedOwners[String(nId)] === factionName
        ).length;
        
        if (ownedNeighbourCount >= 3) {
          commandCenterCells[purpose].push(cellId);
        }
      }
    }
    
    const allCells = [...new Set(Object.values(commandCenterCells).flat())];
    return { byPurpose: commandCenterCells, allCells };
  }, [devShowFortifications, warEffortData?.cells, devPendingOwners]);
  
  // Update command center cells when dependencies change
  useEffect(() => {
    if (devModeActive && devShowFortifications) {
      const result = calculateDevCommandCenterCells();
      setDevCommandCenterCells(result);
    }
  }, [devModeActive, devShowFortifications, calculateDevCommandCenterCells]);
  
  // Dev mode: set country purpose (frontend only - no DB write)
  const handleDevSetCountryPurpose = useCallback((cellId) => {
    if (!devModeActive) return;
    
    // Map purpose to faction name, 'clear' maps to null
    const factionName = devSelectedPurpose === 'clear' ? null : DEV_PURPOSE_TO_FACTION[devSelectedPurpose];
    
    // Update pending owners (frontend state only)
    setDevPendingOwners(prev => ({
      ...prev,
      [String(cellId)]: factionName
    }));
  }, [devModeActive, devSelectedPurpose]);
  
  // Dev mode: restore default (clear pending changes and refresh from DB)
  const handleDevRestoreDefault = useCallback(async () => {
    if (!devModeActive || devLoading) return;
    if (!window.confirm('Discard all pending changes and restore from stored default?')) return;
    try {
      setDevLoading(true);
      // Clear pending changes
      setDevPendingOwners({});
      // Refresh war effort data from the DB (which reflects war_effort_status)
      const token = localStorage.getItem('token');
      const statusResponse = await fetch(`${API_URL}/war-effort/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (statusResponse.ok) {
        const data = await statusResponse.json();
        setWarEffortData(data);
      }
    } catch (err) {
      console.error('Error restoring default:', err);
    } finally {
      setDevLoading(false);
    }
  }, [devModeActive, devLoading]);
  
  // Dev mode: store as default (persist all pending changes to DB)
  const handleDevStoreAsDefault = useCallback(async () => {
    if (!devModeActive || devLoading) return;
    const pendingCount = Object.keys(devPendingOwners).length;
    if (pendingCount === 0) {
      alert('No pending changes to store.');
      return;
    }
    if (!window.confirm(`Store ${pendingCount} pending changes as the new default? This will affect all future missions.`)) return;
    try {
      setDevLoading(true);
      const token = localStorage.getItem('token');
      
      // Send all pending changes to backend
      const response = await fetch(`${API_URL}/war-effort/dev/store-pending-as-default`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ pendingOwners: devPendingOwners })
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(data.message || 'Stored as default successfully');
        // Clear pending changes since they're now persisted
        setDevPendingOwners({});
        // Refresh war effort data
        const statusResponse = await fetch(`${API_URL}/war-effort/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          setWarEffortData(statusData);
        }
      } else {
        const errorData = await response.json();
        alert('Error: ' + (errorData.error || 'Failed to store as default'));
      }
    } catch (err) {
      console.error('Error storing as default:', err);
      alert('Error storing as default: ' + err.message);
    } finally {
      setDevLoading(false);
    }
  }, [devModeActive, devLoading, devPendingOwners]);

  // Dev mode: randomize (frontend only - no DB write)
  const handleDevRandomize = useCallback(() => {
    if (!devModeActive || !warEffortData?.cells) return;
    if (!window.confirm('Randomize country ownership across all purposes? (Changes are not saved until you click Store as Default)')) return;

    const allCellIds = warEffortData.cells.map(c => c.cell_id);
    const purposes = ['Consume', 'Eradicate', 'Colonize', 'Protect', 'Survive'];
    
    // Sort cells by neighbour count (ascending) to minimize command center clusters
    const cellNeighbourCounts = allCellIds.map(cellId => ({
      cellId,
      neighbourCount: getNeighbourCellIds(cellId).length
    }));
    
    // Shuffle cells randomly
    const shuffledCells = [...cellNeighbourCounts].sort(() => Math.random() - 0.5);
    
    // Assign in round-robin to distribute evenly
    const newPendingOwners = {};
    shuffledCells.forEach((cell, index) => {
      const purposeIndex = index % 5;
      const purpose = purposes[purposeIndex];
      const factionName = DEV_PURPOSE_TO_FACTION[purpose];
      newPendingOwners[String(cell.cellId)] = factionName;
    });
    
    // Replace all pending owners with randomized assignment
    setDevPendingOwners(newPendingOwners);
  }, [devModeActive, warEffortData?.cells]);

  // Dev mode: simulate opponent (join + fortify 3 random owned countries)
  const handleSimulateOpponent = useCallback(async () => {
    if (!devModeActive || devSimulateOpponentLoading || !missionData?.id || missionData?.player2_id) return;
    try {
      setDevSimulateOpponentLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/war-effort/mission/${missionData.id}/dev-simulate-opponent`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to simulate opponent');
      }
      const fullRes = await fetch(`${API_URL}/war-effort/mission/${missionData.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (fullRes.ok) {
        const fullMission = await fullRes.json();
        if (!Array.isArray(fullMission.viewer_command_center_boost_cell_ids)) {
          fullMission.viewer_command_center_boost_cell_ids = [];
        } else {
          fullMission.viewer_command_center_boost_cell_ids = fullMission.viewer_command_center_boost_cell_ids.map((id) => Number(id)).filter((id) => !isNaN(id));
        }
        if (!Array.isArray(fullMission.opponent_command_center_boost_cell_ids)) {
          fullMission.opponent_command_center_boost_cell_ids = [];
        } else {
          fullMission.opponent_command_center_boost_cell_ids = fullMission.opponent_command_center_boost_cell_ids.map((id) => Number(id)).filter((id) => !isNaN(id));
        }
        setMissionData(fullMission);
      }
    } catch (err) {
      console.error('Simulate opponent error:', err);
      alert(err.message || 'Failed to simulate opponent');
    } finally {
      setDevSimulateOpponentLoading(false);
    }
  }, [devModeActive, devSimulateOpponentLoading, missionData?.id, missionData?.player2_id]);

  // Debug logging - DISABLED to prevent infinite re-renders
  // Arrays/objects in dependencies cause infinite loops
  // Uncomment only for debugging specific issues
  /*
  useEffect(() => {
    console.log('🔍 User ID detection:', {
      user,
      userId,
      userType: typeof user,
      localStorageUserId: localStorage.getItem('userId'),
      missionPlayer1Id: missionData?.player1_id,
      missionPlayer2Id: missionData?.player2_id,
      isPlayer1,
      player1_fortified: missionData?.player1_fortified_countries,
      player2_fortified: missionData?.player2_fortified_countries
    });
  }, [userId, missionData?.player1_id, missionData?.player2_id, isPlayer1]);
  */
  
  // Get fortified countries array, ensuring it's an array and converting to numbers
  // Handle PostgreSQL array format (string like "{251,252,253}") or array
  const getFortifiedCountriesArray = (countries) => {
    if (!countries) return [];
    if (Array.isArray(countries)) {
      return countries.map(id => Number(id)).filter(id => !isNaN(id));
    }
    // Handle PostgreSQL array format (string like "{251,252,253}")
    if (typeof countries === 'string') {
      const cleaned = countries.replace(/[{}]/g, '');
      if (cleaned.trim() === '') return [];
      return cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id));
    }
    return [];
  };
  
  // Memoize to prevent recalculation on every render
  // Player's own fortified countries (for display and validation). Use viewerSlot for FFA/2v2 so my fortifications show as mine, not neutral.
  const playerFortifiedCountries = useMemo(() => {
    const mode = missionData?.mission_mode;
    const slot = viewerSlot;
    if ((mode === 'ffa' || mode === '2v2') && slot >= 1 && slot <= 4) {
      const key = `player${slot}_fortified_countries`;
      return getFortifiedCountriesArray(missionData?.[key]);
    }
    return getFortifiedCountriesArray(
      isPlayer1 ? missionData?.player1_fortified_countries : missionData?.player2_fortified_countries
    );
  }, [viewerSlot, isPlayer1, missionData?.mission_mode, missionData?.player1_fortified_countries, missionData?.player2_fortified_countries, missionData?.player3_fortified_countries, missionData?.player4_fortified_countries]);

  // Opponent's fortified countries (for display). FFA: all other slots; 2v2: other team; 1v1: other player.
  const opponentFortifiedCountries = useMemo(() => {
    const mode = missionData?.mission_mode;
    const slot = viewerSlot;
    if (mode === 'ffa' && slot >= 1 && slot <= 4) {
      const merged = [];
      for (let s = 1; s <= 4; s++) {
        if (s === slot) continue;
        const key = `player${s}_fortified_countries`;
        merged.push(...getFortifiedCountriesArray(missionData?.[key]));
      }
      return [...new Set(merged)];
    }
    if (mode === '2v2' && slot >= 1 && slot <= 4) {
      const otherTeamSlots = slot <= 2 ? [3, 4] : [1, 2];
      const merged = [];
      for (const s of otherTeamSlots) {
        merged.push(...getFortifiedCountriesArray(missionData?.[`player${s}_fortified_countries`]));
      }
      return [...new Set(merged)];
    }
    return getFortifiedCountriesArray(
      isPlayer1 ? missionData?.player2_fortified_countries : missionData?.player1_fortified_countries
    );
  }, [viewerSlot, isPlayer1, missionData?.mission_mode, missionData?.player1_fortified_countries, missionData?.player2_fortified_countries, missionData?.player3_fortified_countries, missionData?.player4_fortified_countries]);

  // Fortification slots used (lifetime limit). Use viewerSlot for FFA/2v2.
  const playerFortificationSlotsUsed = useMemo(() => {
    const mode = missionData?.mission_mode;
    const slot = viewerSlot;
    if ((mode === 'ffa' || mode === '2v2') && slot >= 1 && slot <= 4) {
      const raw = missionData?.[`player${slot}_fortification_slots_used`];
      return raw != null ? Number(raw) : playerFortifiedCountries.length;
    }
    return (isPlayer1 ? missionData?.player1_fortification_slots_used : missionData?.player2_fortification_slots_used) ?? playerFortifiedCountries.length;
  }, [viewerSlot, isPlayer1, missionData?.mission_mode, missionData?.player1_fortification_slots_used, missionData?.player2_fortification_slots_used, missionData?.player3_fortification_slots_used, missionData?.player4_fortification_slots_used, playerFortifiedCountries.length]);
  
  // Cell IDs that get the Command Center fortified visual (own 3+ neighbours; used for tooltip)
  const commandCenterBoostCellIds = useMemo(() => {
    const fromApi = missionData?.viewer_command_center_boost_cell_ids;
    return Array.isArray(fromApi) ? fromApi.map((id) => Number(id)).filter((id) => !isNaN(id)) : [];
  }, [missionData?.viewer_command_center_boost_cell_ids]);
  // Opponent's Command Center boost cells (bot/opponent has building + owns 3+ neighbours) — show same fortified stripe
  const opponentCommandCenterBoostCellIds = useMemo(() => {
    const fromApi = missionData?.opponent_command_center_boost_cell_ids;
    return Array.isArray(fromApi) ? fromApi.map((id) => Number(id)).filter((id) => !isNaN(id)) : [];
  }, [missionData?.opponent_command_center_boost_cell_ids]);
  // Opponent action-fortified cells only (fortify country action) — faction stripe
  const opponentFortifiedCellsForMap = useMemo(() => {
    return opponentFortifiedCountries.map((id) => Number(id)).filter((id) => !isNaN(id));
  }, [opponentFortifiedCountries]);
  // Opponent command-center-fortified cells only (own 3+ neighbours) — distinct fill; exclude cells that also have action fortification (action takes priority)
  const opponentCommandCenterFortifiedCellsForMap = useMemo(() => {
    const actionSet = new Set(opponentFortifiedCellsForMap);
    return opponentCommandCenterBoostCellIds.filter((id) => !actionSet.has(id));
  }, [opponentCommandCenterBoostCellIds, opponentFortifiedCellsForMap]);

  // Per-opponent list for FFA/2v2 mission box: each opponent gets main mission + up to 3 submissions (their fortified countries)
  const opponentsForBox = useMemo(() => {
    const mode = missionData?.mission_mode;
    const slot = viewerSlot;
    if (mode !== 'ffa' && mode !== '2v2' || slot < 1 || slot > 4) return [];
    const otherTeamSlots = mode === '2v2' ? (slot <= 2 ? [3, 4] : [1, 2]) : [1, 2, 3, 4].filter(s => s !== slot);
    const list = [];
    let nameIdx = 0;
    for (const s of otherTeamSlots) {
      const pid = missionData?.[`player${s}_id`];
      if (!pid) continue;
      const isBot = missionData?.[`player${s}_is_bot`] === true;
      const username = isBot ? 'Bot' : (Array.isArray(opponentUsernames) && opponentUsernames[nameIdx] != null ? opponentUsernames[nameIdx] : `Player ${pid}`);
      nameIdx++;
      const purpose = missionData?.[`player${s}_purpose`];
      const targetCountry = missionData?.[`player${s}_mission_target_country`] || missionData?.mission_target_country;
      const missionType = purpose ? PURPOSE_TO_MISSION_TYPE[purpose] : null;
      const missionName = (missionType && targetCountry) ? `${missionType} in ${targetCountry}` : null;
      const fortified = getFortifiedCountriesArray(missionData?.[`player${s}_fortified_countries`]).slice(0, 3);
      const hqCellId = purpose ? PURPOSE_TO_HQ_CELL_ID[purpose] : null;
      const hqCountry = hqCellId != null ? getCountryFromCellId(hqCellId) : null;
      const factionName = purpose ? DEV_PURPOSE_TO_FACTION[purpose] : null;
      list.push({ slot: s, userId: pid, username, purpose, missionName, fortifiedCountries: fortified, hqCellId, hqCountry, factionName });
    }
    return list;
  }, [missionData?.mission_mode, viewerSlot, missionData?.player1_id, missionData?.player2_id, missionData?.player3_id, missionData?.player4_id, missionData?.player1_is_bot, missionData?.player2_is_bot, missionData?.player3_is_bot, missionData?.player4_is_bot, missionData?.mission_target_country, missionData?.player1_purpose, missionData?.player2_purpose, missionData?.player3_purpose, missionData?.player4_purpose, missionData?.player1_mission_target_country, missionData?.player2_mission_target_country, missionData?.player3_mission_target_country, missionData?.player4_mission_target_country, missionData?.player1_fortified_countries, missionData?.player2_fortified_countries, missionData?.player3_fortified_countries, missionData?.player4_fortified_countries, opponentUsernames]);
  // Cells fortified by action (fortify country) - WorldMap shows these with faction stripe.
  // When a country has both action + command center fortification, we pass action-only so WorldMap prefers the action visual.
  const fortifiedCellsForMap = useMemo(() => playerFortifiedCountries, [playerFortifiedCountries]);

  // Tectonic Boredrive: cells where it was used (rupture indicator on map)
  const tectonicBoredriveCellsForMap = useMemo(() => {
    const raw = missionData?.tectonic_boredrive_cell_ids;
    if (!Array.isArray(raw)) return [];
    return raw.map((id) => Number(id)).filter((n) => !isNaN(n));
  }, [missionData?.tectonic_boredrive_cell_ids]);

  // Neutral fortifications: cellId -> count (1, 2, or 3) for map star display (blue/purple/golden).
  // Use stable ref when state is briefly non-displayable so fortifications never flash off (e.g. on tick).
  const neutralFortificationCountMap = useMemo(() => {
    const raw = missionData?.neutral_fortifications;
    const source = hasDisplayableNeutrals(raw) ? raw : (hasDisplayableNeutrals(neutralFortificationsStableRef.current) ? neutralFortificationsStableRef.current : null);
    if (hasDisplayableNeutrals(raw)) neutralFortificationsStableRef.current = raw;
    if (!source || typeof source !== 'object') return {};
    const map = {};
    Object.entries(source).forEach(([cellIdStr, data]) => {
      const cellId = Number(cellIdStr);
      const count = data?.count;
      if (!isNaN(cellId) && count >= 1 && count <= 3) {
        map[cellId] = count;
      }
    });
    return map;
  }, [missionData?.neutral_fortifications]);
  
  // Debug logging - DISABLED to prevent infinite re-renders
  // Arrays/objects in dependencies cause infinite loops
  // Uncomment only for debugging specific issues
  /*
  useEffect(() => {
    console.log('🔍 Fortified countries debug:', {
      isPlayer1,
      player1_id: missionData?.player1_id,
      player2_id: missionData?.player2_id,
      user_id: userId,
      playerFortifiedCountries: playerFortifiedCountries.length,
      missionDataExists: !!missionData
    });
  }, [userId, missionData?.player1_id, missionData?.player2_id, isPlayer1]);
  */
  
  // Get headquarters image path for a faction
  const getHeadquartersImage = (factionName) => {
    const imageMap = {
      'Remnants of Humanity': '/images/remnants_headquarter.jpg',
      'Exolot Dominion': '/images/exolot_landing.jpg',
      'League of Shadows': '/images/league_portal.jpg',
      "Ra's Children": '/images/ra_seat.jpg',
      'Asrah': '/images/asrah_ground.jpg',
      'The Sentients': '/images/sentient_serverfarm.jpg'
    };
    return imageMap[factionName] || null;
  };
  
  // Get cell data for tooltip (normalize types so owned countries match: API may return cell_id as number or string)
  const getCellData = (cellId) => {
    if (!warEffortData?.cells) return null;
    const id = Number(cellId);
    if (Number.isNaN(id)) return null;
    return warEffortData.cells.find(c => Number(c.cell_id) === id);
  };
  
  // Check if a cell is a headquarters (includes The Sentients HQ in Madagascar)
  const isHeadquarters = (cellId) => {
    const cellIdNum = Number(cellId);
    if (cellIdNum === SENTIENTS_HQ_CELL_ID) return true;
    const cell = getCellData(cellId);
    return cell?.is_headquarters === true || cell?.is_headquarters === 'true' || cell?.is_headquarters === 't';
  };
  
  // Handle country hover for tooltip - position so tooltip stays fully visible (e.g. Australia at map edge)
  const handleCountryHover = useCallback((cellId, e) => {
    if (!cellId) return;
    setHoveredCell(cellId);
    
    const tooltipWidth = 282;   // 250px HQ image + 15px padding * 2 + 2px border
    const tooltipMaxHeight = Math.min(600, window.innerHeight - 48); // Tall HQ tooltips; cap by viewport
    const padding = 12;
    
    // Horizontal: prefer right of cursor, flip to left if would overflow right
    let x = e.clientX + padding;
    if (x + tooltipWidth > window.innerWidth - padding) {
      x = e.clientX - tooltipWidth - padding;
    }
    x = Math.max(padding, Math.min(window.innerWidth - tooltipWidth - padding, x));
    
    // Vertical: prefer below cursor, flip above if would overflow bottom
    let y = e.clientY + padding;
    if (y + tooltipMaxHeight > window.innerHeight - padding) {
      y = e.clientY - tooltipMaxHeight - padding;
    }
    y = Math.max(padding, Math.min(window.innerHeight - tooltipMaxHeight - padding, y));
    
    setTooltipPosition({ x, y });
  }, []);
  
  // Handle country mouse leave
  const handleCountryLeave = useCallback(() => {
    setHoveredCell(null);
  }, []);
  
  // Set missionData from prop only when mission id changes; do not overwrite after fetch (fetched data has viewer_command_center_boost_cell_ids)
  useEffect(() => {
    if (!mission) return;
    setMissionData((prev) => {
      if (prev && prev.id === mission.id && Array.isArray(prev.viewer_command_center_boost_cell_ids)) return prev;
      const formattedMission = { ...mission };
      if (formattedMission.player1_fortified_countries && !Array.isArray(formattedMission.player1_fortified_countries)) {
        if (typeof formattedMission.player1_fortified_countries === 'string') {
          const cleaned = formattedMission.player1_fortified_countries.replace(/[{}]/g, '');
          formattedMission.player1_fortified_countries = cleaned ? cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id)) : [];
        } else {
          formattedMission.player1_fortified_countries = [];
        }
      }
      if (formattedMission.player2_fortified_countries && !Array.isArray(formattedMission.player2_fortified_countries)) {
        if (typeof formattedMission.player2_fortified_countries === 'string') {
          const cleaned = formattedMission.player2_fortified_countries.replace(/[{}]/g, '');
          formattedMission.player2_fortified_countries = cleaned ? cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id)) : [];
        } else {
          formattedMission.player2_fortified_countries = [];
        }
      }
      for (const key of ['player3_fortified_countries', 'player4_fortified_countries']) {
        if (formattedMission[key] && !Array.isArray(formattedMission[key])) {
          if (typeof formattedMission[key] === 'string') {
            const cleaned = formattedMission[key].replace(/[{}]/g, '');
            formattedMission[key] = cleaned ? cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id)) : [];
          } else {
            formattedMission[key] = [];
          }
        }
      }
      if (formattedMission.viewer_engineers_available == null && userId) {
        const uid = Number(userId);
        const slot = uid === Number(formattedMission.player1_id) ? 1 : uid === Number(formattedMission.player2_id) ? 2 : uid === Number(formattedMission.player3_id) ? 3 : uid === Number(formattedMission.player4_id) ? 4 : null;
        if (slot != null && formattedMission[`player${slot}_engineers_available`] != null) formattedMission.viewer_engineers_available = Number(formattedMission[`player${slot}_engineers_available`]);
        else if (prev?.viewer_engineers_available != null) formattedMission.viewer_engineers_available = prev.viewer_engineers_available;
      }
      return formattedMission;
    });
  }, [mission?.id, userId]);
  
  // Refetch full mission data (Eclipse Array visibility, Command Center cells, etc.). Call after place/upgrade/delete HQ building.
  const refetchMission = useCallback(async () => {
    const missionId = mission?.id ?? missionData?.id;
    if (!missionId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/mission/${missionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) return;
      const data = await response.json();
      if (data.player1_fortified_countries && !Array.isArray(data.player1_fortified_countries)) {
        if (typeof data.player1_fortified_countries === 'string') {
          const cleaned = data.player1_fortified_countries.replace(/[{}]/g, '');
          data.player1_fortified_countries = cleaned ? cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id)) : [];
        } else {
          data.player1_fortified_countries = [];
        }
      }
      if (data.player2_fortified_countries && !Array.isArray(data.player2_fortified_countries)) {
        if (typeof data.player2_fortified_countries === 'string') {
          const cleaned = data.player2_fortified_countries.replace(/[{}]/g, '');
          data.player2_fortified_countries = cleaned ? cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id)) : [];
        } else {
          data.player2_fortified_countries = [];
        }
      }
      for (const key of ['player3_fortified_countries', 'player4_fortified_countries']) {
        if (data[key] && !Array.isArray(data[key])) {
          if (typeof data[key] === 'string') {
            const cleaned = data[key].replace(/[{}]/g, '');
            data[key] = cleaned ? cleaned.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id)) : [];
          } else {
            data[key] = [];
          }
        }
      }
      if (!Array.isArray(data.viewer_command_center_boost_cell_ids)) {
        data.viewer_command_center_boost_cell_ids = [];
      } else {
        data.viewer_command_center_boost_cell_ids = data.viewer_command_center_boost_cell_ids.map((id) => Number(id)).filter((id) => !isNaN(id));
      }
      if (!Array.isArray(data.opponent_command_center_boost_cell_ids)) {
        data.opponent_command_center_boost_cell_ids = [];
      } else {
        data.opponent_command_center_boost_cell_ids = data.opponent_command_center_boost_cell_ids.map((id) => Number(id)).filter((id) => !isNaN(id));
      }
      if (data.viewer_engineers_available == null && userId) {
        const uid = Number(userId);
        const slot = uid === Number(data.player1_id) ? 1 : uid === Number(data.player2_id) ? 2 : uid === Number(data.player3_id) ? 3 : uid === Number(data.player4_id) ? 4 : null;
        if (slot != null && data[`player${slot}_engineers_available`] != null) data.viewer_engineers_available = Number(data[`player${slot}_engineers_available`]);
      }
      setMissionData(data);
    } catch (error) {
      console.error('Error refetching mission:', error);
    }
  }, [mission?.id, userId]);

  // Refresh mission data on mount to ensure we have the latest data
  useEffect(() => {
    refetchMission();
  }, [mission.id, refetchMission]);
  
  // Cease fire countdown: tick every second when mission has active cease fire
  useEffect(() => {
    const endsAt = missionData?.cease_fire_ends_at;
    if (!endsAt) {
      setCeaseFireRemainingSeconds(0);
      return;
    }
    const endMs = new Date(endsAt).getTime();
    const update = () => {
      const remaining = Math.max(0, Math.floor((endMs - Date.now()) / 1000));
      setCeaseFireRemainingSeconds(remaining);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [missionData?.cease_fire_ends_at]);

  // Opponent-fortified-only cease fire countdown (10 min after join; only blocks attacking opponent-fortified countries)
  useEffect(() => {
    const endsAt = missionData?.opponent_fortified_cease_fire_ends_at;
    if (!endsAt) {
      setOpponentFortifiedCeaseFireRemainingSeconds(0);
      return;
    }
    const endMs = new Date(endsAt).getTime();
    const update = () => {
      const remaining = Math.max(0, Math.floor((endMs - Date.now()) / 1000));
      setOpponentFortifiedCeaseFireRemainingSeconds(remaining);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [missionData?.opponent_fortified_cease_fire_ends_at]);

  // Conqueror-only: after taking an opponent's fortified country, cannot chain opponent fort or HQ (other attacks OK)
  useEffect(() => {
    const uid = Number(userId);
    const restrictedUid = missionData?.conquest_attacker_cease_fire_user_id != null
      ? Number(missionData.conquest_attacker_cease_fire_user_id)
      : null;
    const endsAt = missionData?.conquest_attacker_cease_fire_ends_at;
    if (!endsAt || restrictedUid == null || uid !== restrictedUid) {
      setConquestAttackerCeaseFireRemainingSeconds(0);
      return;
    }
    const endMs = new Date(endsAt).getTime();
    const update = () => {
      const remaining = Math.max(0, Math.floor((endMs - Date.now()) / 1000));
      setConquestAttackerCeaseFireRemainingSeconds(remaining);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [missionData?.conquest_attacker_cease_fire_ends_at, missionData?.conquest_attacker_cease_fire_user_id, userId]);
  
  const isCeaseFireActive = ceaseFireRemainingSeconds > 0;
  const isOpponentFortifiedCeaseFireActive = opponentFortifiedCeaseFireRemainingSeconds > 0;
  const isConquestAttackerCeaseFireActive = conquestAttackerCeaseFireRemainingSeconds > 0;
  const formatCeaseFireCountdown = (seconds) => {
    if (seconds <= 0) return '0:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
  };

  // Dev mode: instantly end cease fire for this mission (defined after isCeaseFireActive)
  const handleEndCeaseFire = useCallback(async () => {
    if (!devModeActive || devEndCeaseFireLoading || !missionData?.id || (!isCeaseFireActive && !isOpponentFortifiedCeaseFireActive && !isConquestAttackerCeaseFireActive)) return;
    try {
      setDevEndCeaseFireLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/war-effort/mission/${missionData.id}/dev-end-cease-fire`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to end cease fire');
      }
      const fullRes = await fetch(`${API_URL}/war-effort/mission/${missionData.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (fullRes.ok) {
        const fullMission = await fullRes.json();
        if (!Array.isArray(fullMission.viewer_command_center_boost_cell_ids)) {
          fullMission.viewer_command_center_boost_cell_ids = [];
        } else {
          fullMission.viewer_command_center_boost_cell_ids = fullMission.viewer_command_center_boost_cell_ids.map((id) => Number(id)).filter((id) => !isNaN(id));
        }
        if (!Array.isArray(fullMission.opponent_command_center_boost_cell_ids)) {
          fullMission.opponent_command_center_boost_cell_ids = [];
        } else {
          fullMission.opponent_command_center_boost_cell_ids = fullMission.opponent_command_center_boost_cell_ids.map((id) => Number(id)).filter((id) => !isNaN(id));
        }
        setMissionData(fullMission);
      }
    } catch (err) {
      console.error('End cease fire error:', err);
      alert(err.message || 'Failed to end cease fire');
    } finally {
      setDevEndCeaseFireLoading(false);
    }
  }, [devModeActive, devEndCeaseFireLoading, missionData?.id, isCeaseFireActive, isOpponentFortifiedCeaseFireActive, isConquestAttackerCeaseFireActive]);

  // Dev mode: reset energy to 1000
  const handleDevResetEnergy = useCallback(async () => {
    if (!devModeActive || devResetEnergyLoading || !missionData?.id) return;
    try {
      setDevResetEnergyLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/war-effort/mission/${missionData.id}/dev-reset-energy`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to reset energy');
      }
      if (playerResource) {
        setPlayerResource((prev) => prev ? { ...prev, amount: 1000 } : null);
      }
    } catch (err) {
      console.error('Reset energy error:', err);
      alert(err.message || 'Failed to reset energy');
    } finally {
      setDevResetEnergyLoading(false);
    }
  }, [devModeActive, devResetEnergyLoading, missionData?.id]);

  // Dev mode: simulate "fortified country under attack" warning (red box for 5s)
  const handleDevSimulateUnderAttackWarning = useCallback(() => {
    if (!devModeActive) return;
    try {
      const audio = new Audio('/sounds/alarm_wareffort.mp3');
      audio.volume = 0.8;
      audio.play().catch(() => {});
    } catch (_) {}
    if (fortifiedCountryWarningTimerRef.current) {
      clearTimeout(fortifiedCountryWarningTimerRef.current);
      fortifiedCountryWarningTimerRef.current = null;
    }
    setFortifiedCountryUnderAttackWarning({ countryNames: ['Austria'] });
    fortifiedCountryWarningTimerRef.current = setTimeout(() => {
      setFortifiedCountryUnderAttackWarning(null);
      fortifiedCountryWarningTimerRef.current = null;
    }, 5000);
  }, [devModeActive]);

  // Faction colors for map display
  const factionColors = {
    'Remnants of Humanity': '#556B2F',
    'Exolot Dominion': '#4B0082',
    'League of Shadows': '#1A1A1A',
    'Ra\'s Children': '#F4A460',
    'Asrah': '#8B0000',
    'The Sentients': '#ADD8E6',
    'Combined': '#FFA500'
  };
  
  const getFactionBackground = (faction) => {
    if (!faction) return '#808080';
    const color = factionColors[faction];
    if (!color) return '#808080';

    const hexToRgba = (hex, alpha = 1.0) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };
    
    if (color.includes('gradient')) {
      return color.replace(/#([0-9A-Fa-f]{6})/g, (match, hex) => {
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, 1.0)`;
      });
    } else {
      return hexToRgba(color, 1.0);
    }
  };

  const viewerCommandCenterStripeColors = useMemo(() => {
    const playerPurpose = isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose;
    if (!playerPurpose) return null;
    return PURPOSE_COMMAND_CENTER_STRIPE_COLORS[playerPurpose] ?? null;
  }, [isPlayer1, missionData?.player1_purpose, missionData?.player2_purpose]);

  const opponentCommandCenterStripeColors = useMemo(() => {
    const opponentPurpose = isPlayer1 ? missionData?.player2_purpose : missionData?.player1_purpose;
    if (!opponentPurpose) return null;
    return PURPOSE_COMMAND_CENTER_STRIPE_COLORS[opponentPurpose] ?? null;
  }, [isPlayer1, missionData?.player1_purpose, missionData?.player2_purpose]);

  // Faction names whose purposes are selected by at least one player (for map: show these in faction color; all others neutral).
  // Same as 1v1: Protect countries sandy yellow when any player has Protect; Consume countries red when any player has Consume. Only show neutral when no player has selected that country's purpose.
  // FFA: always include The Sentients so their owned countries show in Sentients color (not dark gray).
  const factionsInGame = useMemo(() => {
    const list = [];
    const addFactionsFor = (purpose) => {
      if (!purpose) return;
      const factions = PURPOSE_TO_FACTION_MAPPING[purpose];
      if (Array.isArray(factions)) list.push(...factions);
    };
    if (missionData?.player1_id) addFactionsFor(missionData.player1_purpose);
    if (missionData?.player2_id) addFactionsFor(missionData.player2_purpose);
    if (missionData?.player3_id) addFactionsFor(missionData.player3_purpose);
    if (missionData?.player4_id) addFactionsFor(missionData.player4_purpose);
    if (missionData?.mission_mode === 'ffa' && !list.includes('The Sentients')) {
      list.push('The Sentients');
    }
    return list;
  }, [missionData?.mission_mode, missionData?.player1_id, missionData?.player1_purpose, missionData?.player2_id, missionData?.player2_purpose, missionData?.player3_id, missionData?.player3_purpose, missionData?.player4_id, missionData?.player4_purpose]);
  
  // Create cell owners map from war effort data, merged with dev mode pending changes
  const cellOwnersMap = useMemo(() => {
    if (!warEffortData?.cells) return {};
    const map = {};
    warEffortData.cells.forEach(cell => {
      const cellIdStr = String(cell.cell_id);
      // Check if there's a pending change in dev mode
      if (devModeActive && devPendingOwners.hasOwnProperty(cellIdStr)) {
        // Use pending value (can be null to clear ownership)
        if (devPendingOwners[cellIdStr]) {
          map[cellIdStr] = devPendingOwners[cellIdStr];
        }
        // If null, don't add to map (cleared ownership)
      } else if (cell.owner_name) {
        map[cellIdStr] = cell.owner_name;
      }
    });
    return map;
  }, [warEffortData, devModeActive, devPendingOwners]);

  // Per-cell opponent Command Center stripe colors (FFA/2v2: each cell colored by its owner's purpose so bot Survive = green, etc.)
  const opponentCommandCenterStripeByCellId = useMemo(() => {
    const cellIds = opponentCommandCenterBoostCellIds;
    if (!Array.isArray(cellIds) || cellIds.length === 0) return {};
    const byCell = {};
    for (const cellId of cellIds) {
      const owner = cellOwnersMap[String(cellId)];
      if (!owner) continue;
      const purpose = FACTION_TO_PURPOSE[owner];
      if (!purpose) continue;
      const colors = PURPOSE_COMMAND_CENTER_STRIPE_COLORS[purpose];
      if (colors) byCell[Number(cellId)] = colors;
    }
    return byCell;
  }, [opponentCommandCenterBoostCellIds, cellOwnersMap]);

  // Debug: log Chad (cell 465) color inputs so we can see why it gets its color
  const CHAD_CELL_ID = 465;
  useEffect(() => {
    const chadOwner = cellOwnersMap[String(CHAD_CELL_ID)];
    console.log('[WarEffort Chad]', {
      cellOwnersMapChad: chadOwner ?? '(not in map)',
      factionsInGame,
      chadInFactionsInGame: chadOwner != null && Array.isArray(factionsInGame) && factionsInGame.includes(chadOwner),
      missionMode: missionData?.mission_mode,
      player1_purpose: missionData?.player1_purpose,
      player2_purpose: missionData?.player2_purpose
    });
  }, [cellOwnersMap, factionsInGame, missionData?.mission_mode, missionData?.player1_purpose, missionData?.player2_purpose]);

  // Dev mode statistics: country counts and HQ info per purpose
  const devStatistics = useMemo(() => {
    if (!devModeActive) return null;
    
    const purposes = ['Consume', 'Eradicate', 'Colonize', 'Protect', 'Survive'];
    const stats = {};
    
    // Initialize stats for each purpose
    purposes.forEach(purpose => {
      const factionName = DEV_PURPOSE_TO_FACTION[purpose];
      const hqCellId = PURPOSE_TO_HQ_CELL_ID[purpose];
      stats[purpose] = {
        count: 0,
        factionName,
        hqCellId,
        hqCountryName: getCountryFromCellId(hqCellId),
        ownsHq: false
      };
    });
    
    // Count countries per purpose from merged cellOwnersMap
    Object.entries(cellOwnersMap).forEach(([cellIdStr, factionName]) => {
      const cellId = Number(cellIdStr);
      // Find which purpose this faction belongs to
      for (const purpose of purposes) {
        if (DEV_PURPOSE_TO_FACTION[purpose] === factionName) {
          stats[purpose].count++;
          // Check if this is the HQ
          if (cellId === PURPOSE_TO_HQ_CELL_ID[purpose]) {
            stats[purpose].ownsHq = true;
          }
          break;
        }
      }
    });
    
    // Calculate total
    const total = Object.values(stats).reduce((sum, s) => sum + s.count, 0);
    
    return { purposes: stats, total };
  }, [devModeActive, cellOwnersMap]);
  
  // Fetch controlled countries (faction-based) and war effort data
  // Use ref to track if we're already fetching to prevent infinite loops
  const fetchingRef = useRef(false);
  
  const opponentPurpose = isPlayer1 ? missionData?.player2_purpose : missionData?.player1_purpose;
  const opponentHqCellId = opponentPurpose ? PURPOSE_TO_HQ_CELL_ID[opponentPurpose] : null;

  // Resolve which opponent (defender) owns an HQ cell in FFA/2v2 (purpose -> slot -> player id)
  const getDefenderUserIdForHQCell = useCallback((mission, hqCellId) => {
    if (!mission || hqCellId == null) return null;
    const purpose = HQ_CELL_ID_TO_PURPOSE[hqCellId];
    if (!purpose) return null;
    for (let s = 1; s <= 4; s++) {
      const pid = mission[`player${s}_id`];
      const pPurpose = mission[`player${s}_purpose`];
      if (pid != null && pPurpose === purpose) return Number(pid);
    }
    return null;
  }, []);

  // Fetch enemy HQ attack info when in attack mode (1v1/2v2: single opponent; FFA: returns attackableHQs list)
  useEffect(() => {
    if (!missionData?.id || !attackFromCountry || !userId) {
      setEnemyHqAttackInfo(null);
      return;
    }
    const mode = missionData?.mission_mode;
    const hasOpponent = mode === 'ffa'
      ? [missionData.player1_id, missionData.player2_id, missionData.player3_id, missionData.player4_id].filter(Boolean).some(id => Number(id) !== Number(userId))
      : !!missionData?.player2_id;
    if (!hasOpponent) {
      setEnemyHqAttackInfo(null);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    const fromParam = attackFromCountry?.cellId != null ? `&fromCellId=${attackFromCountry.cellId}` : '';
    fetch(`${API_URL}/war-effort/enemy-hq-attack-info?missionId=${missionData.id}${fromParam}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (!cancelled && data) setEnemyHqAttackInfo(data);
      })
      .catch(() => {
        if (!cancelled) setEnemyHqAttackInfo(null);
      });
    return () => { cancelled = true; };
  }, [missionData?.id, missionData?.mission_mode, missionData?.player1_id, missionData?.player2_id, missionData?.player3_id, missionData?.player4_id, attackFromCountry?.cellId, userId]);

  // Valid attack target when in attack mode: hovered country that we don't own (exclude Sentients HQ); include opponent HQ when attackable
  const validAttackTargetCellId = useMemo(() => {
    if (!attackFromCountry || hoveredCell == null) return null;
    const hoveredNum = Number(hoveredCell);
    if (hoveredNum === SENTIENTS_HQ_CELL_ID) return null; // Trading hub cannot be attacked
    if (controlledCountries.includes(hoveredNum) || hoveredNum === attackFromCountry.cellId) return null;
    if (missionData?.mission_mode === 'ffa' && Array.isArray(enemyHqAttackInfo?.attackableHQs) && enemyHqAttackInfo.attackableHQs.some(h => h.hqCellId === hoveredNum)) return hoveredNum;
    if (enemyHqAttackInfo?.canAttack && opponentHqCellId != null && hoveredNum === opponentHqCellId) return hoveredNum;
    return hoveredNum;
  }, [attackFromCountry, hoveredCell, controlledCountries, enemyHqAttackInfo?.canAttack, enemyHqAttackInfo?.attackableHQs, missionData?.mission_mode, opponentHqCellId]);

  // Fetch movement cost when hovering a valid target in attack mode (use enemy HQ cost when targeting HQ)
  useEffect(() => {
    if (!missionData?.id || !attackFromCountry || !validAttackTargetCellId || !userId) {
      setAttackModeTargetCost(null);
      return;
    }
    const hqMatch = missionData?.mission_mode === 'ffa' && Array.isArray(enemyHqAttackInfo?.attackableHQs)
      ? enemyHqAttackInfo.attackableHQs.find(h => h.hqCellId === validAttackTargetCellId)
      : (enemyHqAttackInfo?.canAttack && validAttackTargetCellId === enemyHqAttackInfo?.hqCellId ? { movementCost: enemyHqAttackInfo.movementCost } : null);
    if (hqMatch) {
      setAttackModeTargetCost({ cost: hqMatch.movementCost, distanceKm: null, isHqAttack: true });
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    fetch(`${API_URL}/war-effort/movement-cost?missionId=${missionData.id}&toCellId=${validAttackTargetCellId}&fromCellId=${attackFromCountry.cellId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (!cancelled && data) setAttackModeTargetCost({ cost: data.cost, distanceKm: data.distanceKm, isHqAttack: false });
      })
      .catch(() => {
        if (!cancelled) setAttackModeTargetCost(null);
      });
    return () => { cancelled = true; };
  }, [missionData?.id, attackFromCountry?.cellId, validAttackTargetCellId, userId, enemyHqAttackInfo?.canAttack, enemyHqAttackInfo?.hqCellId, enemyHqAttackInfo?.movementCost]);
  
  const fetchControlledCountries = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (fetchingRef.current) {
      return;
    }
    
    if (!missionData?.id) {
      return;
    }
    
    fetchingRef.current = true;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWarEffortData(data);
        
        // Get player's purpose from mission data (needed for controlled countries).
        // FFA/2v2: use viewerSlot so player2/3/4 get their own purpose, not player2_purpose for everyone.
        let playerPurpose = null;
        if (missionData?.mission_mode === 'ffa' || missionData?.mission_mode === '2v2') {
          const slot = viewerSlot;
          if (slot >= 1 && slot <= 4) playerPurpose = missionData?.[`player${slot}_purpose`] ?? null;
        }
        if (playerPurpose == null) {
          playerPurpose = isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose;
        }
        
        // If purpose is missing, try to get it from participant data
        if (!playerPurpose && data.participant && data.participant.purpose) {
          playerPurpose = data.participant.purpose;
        }
        
        if (playerPurpose) {
          const allowedFactions = PURPOSE_TO_FACTION_MAPPING[playerPurpose] || [];
          const controlled = data.cells
            .filter(cell => {
              const matches = cell.owner_name && allowedFactions.includes(cell.owner_name);
              return matches;
            })
            .map(cell => Number(cell.cell_id));
          if (controlled.length === 0) {
            console.warn('⚠️ No controlled countries found! This means you cannot fortify any countries.');
          }
          setControlledCountries(controlled);
        } else {
          console.warn('⚠️ No purpose found for player');
          setError('Your purpose is not set. Please ensure you have joined the mission with a purpose.');
        }
      }
      
      // Also fetch mission status to get resource info
      const missionStatusResponse = await fetch(`${API_URL}/war-effort/mission-status/${missionData.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (missionStatusResponse.ok) {
        const statusData = await missionStatusResponse.json();
        // FFA/2v2: use only viewerResource so we never show another slot's energy (e.g. player3 seeing player2's 140).
        const resource = (missionData?.mission_mode === 'ffa' || missionData?.mission_mode === '2v2')
          ? (statusData.viewerResource ?? null)
          : (statusData.viewerResource ?? (statusData.isPlayer1 ? statusData.player1Resource : statusData.player2Resource));
        if (resource) setPlayerResource(resource);
        if (typeof statusData.buildingCostMultiplier === 'number') {
          setBuildingCostMultiplier(statusData.buildingCostMultiplier);
        }
        setHasInfrastructureSubsidy(statusData.hasInfrastructureSubsidy === true);
        if (statusData.protectedCellId != null) {
          setProtectedCellId(statusData.protectedCellId);
        } else {
          setProtectedCellId(null);
        }
        setReconquerCooldowns(Array.isArray(statusData.reconquerCooldowns) ? statusData.reconquerCooldowns : []);
        setCellsUnderAttack(Array.isArray(statusData.cellsUnderAttack) ? statusData.cellsUnderAttack : []);
        setTrainingGroundsOrdersToKeep(typeof statusData.trainingGroundsOrdersToKeep === 'number' ? Math.min(3, Math.max(1, statusData.trainingGroundsOrdersToKeep)) : 1);
        setLastPlayerAction(statusData.lastPlayerAction ?? null);
        setLastPlayerActionCellId(statusData.lastPlayerActionCellId ?? null);
        const nextOppAction = statusData.lastOpponentAction ?? null;
        const nextOppKey = getStableOpponentActionKey(nextOppAction);
        if (nextOppKey !== lastOpponentActionKeyRef.current) {
          lastOpponentActionKeyRef.current = nextOppKey;
          setLastOpponentAction(nextOppAction);
        }
        setLastOpponentActionCellId(statusData.lastOpponentActionCellId ?? null);
        setOpponentHqBuildingIds(Array.isArray(statusData.opponentHqBuildingIds) ? statusData.opponentHqBuildingIds : []);
        const byUser = statusData.opponentHqBuildingIdsByUser;
        setOpponentHqBuildingIdsByUser(byUser && typeof byUser === 'object' && !Array.isArray(byUser)
          ? Object.fromEntries(Object.entries(byUser).map(([k, v]) => [String(k), Array.isArray(v) ? v : []]))
          : {});
        setViewerHasIntelligenceAgency(statusData.viewerHasIntelligenceAgency === true);
        setOpponentAttackTargets(Array.isArray(statusData.opponentAttackTargets) ? statusData.opponentAttackTargets : []);
        setActiveShopEffects(Array.isArray(statusData.activeEffects) ? statusData.activeEffects : []);
        setDevHasBot(statusData.dev_has_bot === true);
        setDevBotEnergy(statusData.dev_bot_energy != null ? statusData.dev_bot_energy : null);
        setDevBotCurrentAttack(statusData.dev_bot_current_attack && typeof statusData.dev_bot_current_attack === 'object' ? statusData.dev_bot_current_attack : null);
        // Command Center: merge boost cell IDs from mission-status so tooltips/map stay correct. Do not overwrite prev's non-empty with empty.
        if (Array.isArray(statusData.viewer_command_center_boost_cell_ids) || Array.isArray(statusData.opponent_command_center_boost_cell_ids)) {
          setMissionData((prev) => {
            if (!prev?.id || prev.id !== missionData?.id) return prev;
            const next = { ...prev };
            const newViewer = Array.isArray(statusData.viewer_command_center_boost_cell_ids)
              ? statusData.viewer_command_center_boost_cell_ids.map((id) => Number(id)).filter((id) => !isNaN(id))
              : null;
            const newOpp = Array.isArray(statusData.opponent_command_center_boost_cell_ids)
              ? statusData.opponent_command_center_boost_cell_ids.map((id) => Number(id)).filter((id) => !isNaN(id))
              : null;
            if (newViewer !== null) {
              next.viewer_command_center_boost_cell_ids = newViewer.length > 0 ? newViewer : (Array.isArray(prev.viewer_command_center_boost_cell_ids) ? prev.viewer_command_center_boost_cell_ids : []);
            }
            if (newOpp !== null) {
              next.opponent_command_center_boost_cell_ids = newOpp.length > 0 ? newOpp : (Array.isArray(prev.opponent_command_center_boost_cell_ids) ? prev.opponent_command_center_boost_cell_ids : []);
            }
            return next;
          });
        }
        if (Array.isArray(statusData.tectonic_boredrive_cell_ids) || statusData.viewer_engineers_available != null) {
          setMissionData((prev) => {
            if (!prev?.id || prev.id !== missionData?.id) return prev;
            const next = { ...prev };
            if (Array.isArray(statusData.tectonic_boredrive_cell_ids)) {
              next.tectonic_boredrive_cell_ids = statusData.tectonic_boredrive_cell_ids.map((id) => Number(id)).filter((n) => !isNaN(n));
            }
            if (statusData.viewer_engineers_available != null) {
              next.viewer_engineers_available = Number(statusData.viewer_engineers_available);
            }
            return next;
          });
        }
        // Fortified/HQ/opponent warnings are shown globally by WarEffortWarnings.js (including alarm sound)
      }
    } catch (error) {
      console.error('Error fetching controlled countries:', error);
    } finally {
      fetchingRef.current = false;
    }
  }, [missionData?.id, missionData?.mission_mode, missionData?.player1_purpose, missionData?.player2_purpose, missionData?.player3_purpose, missionData?.player4_purpose, isPlayer1, viewerSlot]);

  // Listen for mission-update from WebSocket - refresh mission data and cell ownership (country changes)
  // When Trading Hub is open, refetch hub status so all players see the same slots and out-of-stock/countdown
  // Derive viewer_engineers_available from mission payload (broadcast has player{N}_engineers_available, not viewer-specific)
  useEffect(() => {
    const handler = (e) => {
      const m = e?.detail?.mission;
      if (!m || !missionData?.id || m.id !== missionData.id) return;
      setMissionData((prev) => {
        const formatted = { ...m };
        if (formatted.player1_fortified_countries && !Array.isArray(formatted.player1_fortified_countries)) {
          formatted.player1_fortified_countries = [];
        }
        if (formatted.player2_fortified_countries && !Array.isArray(formatted.player2_fortified_countries)) {
          formatted.player2_fortified_countries = [];
        }
        for (const key of ['player3_fortified_countries', 'player4_fortified_countries']) {
          if (formatted[key] && !Array.isArray(formatted[key])) {
            formatted[key] = [];
          }
        }
        // Broadcast has player1_engineers_available..player4_engineers_available; set viewer_engineers_available from viewer's slot so engineer counter and Tectonic Boredrive stay in sync. Only set when we have a real value (never default to 3).
        if (userId) {
          const uid = Number(userId);
          const slot = uid === Number(m.player1_id) ? 1 : uid === Number(m.player2_id) ? 2 : uid === Number(m.player3_id) ? 3 : uid === Number(m.player4_id) ? 4 : null;
          if (slot != null) {
            const raw = m[`player${slot}_engineers_available`];
            if (raw != null && raw !== '') formatted.viewer_engineers_available = Number(raw);
          }
        }
        // mission-update broadcast does not include viewer-specific Command Center cells; preserve from prev so tooltips and map fill stay correct (incoming may be undefined, null, or [])
        const incomingViewerCc = formatted.viewer_command_center_boost_cell_ids;
        const incomingOppCc = formatted.opponent_command_center_boost_cell_ids;
        const hasValidIncomingViewerCc = Array.isArray(incomingViewerCc) && incomingViewerCc.length > 0;
        const hasValidIncomingOppCc = Array.isArray(incomingOppCc) && incomingOppCc.length > 0;
        if (!hasValidIncomingViewerCc && Array.isArray(prev?.viewer_command_center_boost_cell_ids) && prev.viewer_command_center_boost_cell_ids.length > 0) {
          formatted.viewer_command_center_boost_cell_ids = prev.viewer_command_center_boost_cell_ids;
        }
        if (!hasValidIncomingOppCc && Array.isArray(prev?.opponent_command_center_boost_cell_ids) && prev.opponent_command_center_boost_cell_ids.length > 0) {
          formatted.opponent_command_center_boost_cell_ids = prev.opponent_command_center_boost_cell_ids;
        }
        // mission-update broadcast sends neutral_fortifications (count-only). Only treat incoming as valid if at least one cell has count 1–3 (map display requires that). Otherwise preserve prev so fortifications stay visible on tick.
        const incomingNeutrals = formatted.neutral_fortifications;
        const hasValidIncomingNeutrals = hasDisplayableNeutrals(incomingNeutrals);
        if (!hasValidIncomingNeutrals && prev?.neutral_fortifications && hasDisplayableNeutrals(prev.neutral_fortifications)) {
          formatted.neutral_fortifications = prev.neutral_fortifications;
        }
        return formatted;
      });
      fetchControlledCountries();
      // Refetch mission-status so lastPlayerAction/lastPlayerActionCellId update (e.g. "You have lost Chad to …") without waiting for the 25s poll
      const token = localStorage.getItem('token');
      fetch(`${API_URL}/war-effort/mission-status/${m.id}`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => res.ok ? res.json() : null)
        .then((data) => {
          if (data) {
            setLastPlayerAction(data.lastPlayerAction ?? null);
            if (data.lastPlayerActionCellId != null) setLastPlayerActionCellId(data.lastPlayerActionCellId);
          }
        })
        .catch(() => {});
      if (tradingHubOpen && m.id) {
        const token = localStorage.getItem('token');
        fetch(`${API_URL}/war-effort/trading-hub/status?missionId=${m.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
          .then((res) => res.ok ? res.json() : null)
          .then((data) => {
            if (data) {
              const cooldownEndsAtMs = data.nextClaimAt ? new Date(data.nextClaimAt).getTime() : null;
              setTradingHubStatus({ ...data, cooldownEndsAtMs });
            }
          })
          .catch(() => {});
      }
    };
    window.addEventListener('war-effort-mission-update', handler);
    return () => window.removeEventListener('war-effort-mission-update', handler);
  }, [missionData?.id, userId, fetchControlledCountries, tradingHubOpen]);
  
  // Sync protectedCellId from mission payload (GET mission includes it for 1v1/FFA/2v2 so tooltip shows on load)
  useEffect(() => {
    if (missionData?.protectedCellId != null) {
      setProtectedCellId(missionData.protectedCellId);
    }
  }, [missionData?.id, missionData?.protectedCellId]);

  // Fetch war effort data and mission status on mount / when mission changes (for tooltips, controlled countries, etc.)
  useEffect(() => {
    if (missionData?.id) {
      fetchControlledCountries();
    }
  }, [missionData?.id, missionData?.player1_purpose, missionData?.player2_purpose, isPlayer1, fetchControlledCountries]);

  // Poll mission status every 30s so energy bar updates soon after server grants energy (tick +10/min, Trading Hub, etc.)
  useEffect(() => {
    if (!missionData?.id) return;
    const token = localStorage.getItem('token');
    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/war-effort/mission-status/${missionData.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          // FFA/2v2: use only viewerResource so we never show another slot's energy after poll/return from background.
          const resource = (missionData?.mission_mode === 'ffa' || missionData?.mission_mode === '2v2')
            ? (data.viewerResource ?? null)
            : (data.viewerResource ?? (data.isPlayer1 ? data.player1Resource : data.player2Resource));
          if (resource) setPlayerResource(resource);
          // When Trading Hub is open, refresh its energy so buy buttons update after an energy tick
          if (tradingHubOpen && resource?.amount != null) {
            setTradingHubStatus(prev => prev ? { ...prev, energyBalance: resource.amount } : null);
          }
          if (data.viewer_engineers_available != null) {
            setMissionData((prev) => prev?.id ? { ...prev, viewer_engineers_available: Number(data.viewer_engineers_available) } : prev);
          }
          if (typeof data.buildingCostMultiplier === 'number') {
            setBuildingCostMultiplier(data.buildingCostMultiplier);
          }
          setHasInfrastructureSubsidy(data.hasInfrastructureSubsidy === true);
          if (data.protectedCellId != null) setProtectedCellId(data.protectedCellId);
          else setProtectedCellId(null);
          if (Array.isArray(data.reconquerCooldowns)) setReconquerCooldowns(data.reconquerCooldowns);
          if (Array.isArray(data.cellsUnderAttack)) setCellsUnderAttack(data.cellsUnderAttack);
          if (typeof data.trainingGroundsOrdersToKeep === 'number') setTrainingGroundsOrdersToKeep(Math.min(3, Math.max(1, data.trainingGroundsOrdersToKeep)));
          setLastPlayerAction(data.lastPlayerAction ?? null);
          if (data.lastPlayerActionCellId != null) setLastPlayerActionCellId(data.lastPlayerActionCellId);
          const nextOppAction = data.lastOpponentAction ?? null;
          const nextOppKey = getStableOpponentActionKey(nextOppAction);
          if (nextOppKey !== lastOpponentActionKeyRef.current) {
            lastOpponentActionKeyRef.current = nextOppKey;
            setLastOpponentAction(nextOppAction);
          }
          if (data.lastOpponentActionCellId != null) setLastOpponentActionCellId(data.lastOpponentActionCellId);
          if (Array.isArray(data.opponentHqBuildingIds)) setOpponentHqBuildingIds(data.opponentHqBuildingIds);
          const byUser = data.opponentHqBuildingIdsByUser;
          if (byUser && typeof byUser === 'object' && !Array.isArray(byUser)) {
            setOpponentHqBuildingIdsByUser(Object.fromEntries(Object.entries(byUser).map(([k, v]) => [String(k), Array.isArray(v) ? v : []])));
          }
          if (typeof data.viewerHasEclipseArrayUpgraded === 'boolean') setViewerHasEclipseArrayUpgraded(data.viewerHasEclipseArrayUpgraded);
          if (typeof data.viewerHasIntelligenceAgency === 'boolean') setViewerHasIntelligenceAgency(data.viewerHasIntelligenceAgency);
          if (Array.isArray(data.opponentAttackTargets)) setOpponentAttackTargets(data.opponentAttackTargets);
          if (Array.isArray(data.activeEffects)) setActiveShopEffects(data.activeEffects);
          setDevHasBot(data.dev_has_bot === true);
          setDevBotEnergy(data.dev_bot_energy != null ? data.dev_bot_energy : null);
          setDevBotCurrentAttack(data.dev_bot_current_attack && typeof data.dev_bot_current_attack === 'object' ? data.dev_bot_current_attack : null);
          // Command Center: keep tooltips and map fill in sync when mission-status returns boost cell IDs. Do not overwrite prev's non-empty with empty (avoids hiding after transient empty response).
          if (Array.isArray(data.viewer_command_center_boost_cell_ids) || Array.isArray(data.opponent_command_center_boost_cell_ids)) {
            setMissionData((prev) => {
              if (!prev?.id) return prev;
              const next = { ...prev };
              const newViewer = Array.isArray(data.viewer_command_center_boost_cell_ids)
                ? data.viewer_command_center_boost_cell_ids.map((id) => Number(id)).filter((id) => !isNaN(id))
                : null;
              const newOpp = Array.isArray(data.opponent_command_center_boost_cell_ids)
                ? data.opponent_command_center_boost_cell_ids.map((id) => Number(id)).filter((id) => !isNaN(id))
                : null;
              if (newViewer !== null) {
                next.viewer_command_center_boost_cell_ids = newViewer.length > 0 ? newViewer : (Array.isArray(prev.viewer_command_center_boost_cell_ids) ? prev.viewer_command_center_boost_cell_ids : []);
              }
              if (newOpp !== null) {
                next.opponent_command_center_boost_cell_ids = newOpp.length > 0 ? newOpp : (Array.isArray(prev.opponent_command_center_boost_cell_ids) ? prev.opponent_command_center_boost_cell_ids : []);
              }
              return next;
            });
          }
          // When mission completed (e.g. opponent left), mission-status now returns mission_end_game_id; merge so redirect to AfterBattle works. Preserve command center IDs (data.mission does not include them).
          if (data.mission?.status === 'completed' && data.mission?.mission_end_game_id) {
            setMissionData((prev) => {
              if (prev?.id !== data.mission?.id) return prev;
              const merged = { ...prev, ...data.mission };
              if (!Array.isArray(data.mission.viewer_command_center_boost_cell_ids) && Array.isArray(prev.viewer_command_center_boost_cell_ids)) {
                merged.viewer_command_center_boost_cell_ids = prev.viewer_command_center_boost_cell_ids;
              }
              if (!Array.isArray(data.mission.opponent_command_center_boost_cell_ids) && Array.isArray(prev.opponent_command_center_boost_cell_ids)) {
                merged.opponent_command_center_boost_cell_ids = prev.opponent_command_center_boost_cell_ids;
              }
              return merged;
            });
            if (onFortificationComplete) onFortificationComplete(data.mission);
          }
          // Fortified/HQ/opponent warnings are shown globally by WarEffortWarnings.js
        }
      } catch (e) {
        // ignore
      }
    };
    const intervalId = setInterval(poll, 25 * 1000); // 25s - balance between freshness and network load (WebSocket mission-update is primary)
    return () => {
      clearInterval(intervalId);
      if (fortifiedCountryWarningTimerRef.current) {
        clearTimeout(fortifiedCountryWarningTimerRef.current);
        fortifiedCountryWarningTimerRef.current = null;
      }
    };
  }, [missionData?.id, missionData?.mission_mode, tradingHubOpen]);

  // Fetch Trading Hub status when dialog opens
  useEffect(() => {
    if (!tradingHubOpen || !missionData?.id) return;
    const token = localStorage.getItem('token');
    const fetchStatus = async () => {
      setTradingHubLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_URL}/war-effort/trading-hub/status?missionId=${missionData.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const cooldownEndsAtMs = (data.remainingCooldownSeconds > 0) ? Date.now() + data.remainingCooldownSeconds * 1000 : null;
          setTradingHubStatus({ ...data, cooldownEndsAtMs });
          setEnergyCooldownDisplaySeconds(data.remainingCooldownSeconds > 0 ? data.remainingCooldownSeconds : null);
          setTradingHubProtectCellId(null);
          setTradingHubSubvertCellId(null);
          if (data.tradingBlocked) {
            playSoundEffect('/sounds/denied.mp3');
          } else {
            if (tradingHubAmbienceRef.current) {
              tradingHubAmbienceRef.current.pause();
              tradingHubAmbienceRef.current.currentTime = 0;
              tradingHubAmbienceRef.current = null;
            }
            const v = getEffectiveSoundOutputVolume(0.5);
            if (v !== null) {
              const ambient = new Audio('/sounds/trading_hub_ambience.mp3');
              ambient.loop = true;
              ambient.volume = v;
              tradingHubAmbienceRef.current = ambient;
              ambient.play().catch(() => {});
            }
          }
        } else {
          const errBody = await res.json().catch(() => ({}));
          setError(errBody.error || `Trading hub: ${res.status}`);
        }
      } catch (e) {
        console.error('Trading hub status:', e);
        setError(e.message || 'Trading hub failed to load');
      } finally {
        setTradingHubLoading(false);
      }
    };
    fetchStatus();
  }, [tradingHubOpen, missionData?.id]);

  // Live countdown for free energy: update every second when Trading Hub is open and cooldown is active
  useEffect(() => {
    if (!tradingHubOpen || !tradingHubStatus?.cooldownEndsAtMs) {
      setEnergyCooldownDisplaySeconds(null);
      return;
    }
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((tradingHubStatus.cooldownEndsAtMs - Date.now()) / 1000));
      setEnergyCooldownDisplaySeconds(remaining);
      if (remaining <= 0) {
        setTradingHubStatus(prev => prev ? { ...prev, canClaimEnergy: true, remainingCooldownSeconds: 0, cooldownEndsAtMs: null } : null);
      }
    };
    tick(); // run once immediately
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [tradingHubOpen, tradingHubStatus?.cooldownEndsAtMs]);

  // Global free restock (5 min) countdown: tick every 1s and refetch when auto-restock time is reached (per-slot countdowns removed from UI)
  useEffect(() => {
    if (!tradingHubOpen || !missionData?.id || !tradingHubStatus) return;
    const hasAutoRestock = tradingHubStatus.nextAutoRestockAtMs != null;
    if (!hasAutoRestock) return;
    setShopCountdownTick(Date.now());
    const token = localStorage.getItem('token');
    const interval = setInterval(async () => {
      const now = Date.now();
      setShopCountdownTick(now);
      const autoRestockExpired = tradingHubStatus.nextAutoRestockAtMs != null && tradingHubStatus.nextAutoRestockAtMs <= now;
      if (autoRestockExpired) {
        try {
          const res = await fetch(`${API_URL}/war-effort/trading-hub/status?missionId=${missionData.id}`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) {
            const data = await res.json();
            const cooldownEndsAtMs = (data.remainingCooldownSeconds > 0) ? Date.now() + data.remainingCooldownSeconds * 1000 : null;
            setTradingHubStatus({ ...data, cooldownEndsAtMs });
          }
        } catch (_) {}
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [tradingHubOpen, missionData?.id, tradingHubStatus?.nextAutoRestockAtMs]);

  // Fetch opponent username
  useEffect(() => {
    const fetchOpponentUsername = async () => {
      if (!missionData || !userId) {
        setOpponentUsername(null);
        return;
      }
      
      // Don't show opponent if mission is waiting for opponent
      if (missionData.status === 'waiting_opponent') {
        setOpponentUsername(null);
        setOpponentUsernames([]);
        return;
      }

      const mode = missionData.mission_mode;
      const uid = Number(userId);

      if (mode === 'ffa') {
        const opponentIds = [missionData.player1_id, missionData.player2_id, missionData.player3_id, missionData.player4_id]
          .filter(Boolean)
          .filter(id => Number(id) !== uid);
        if (opponentIds.length === 0) {
          setOpponentUsername(null);
          setOpponentUsernames([]);
          return;
        }
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_URL}/api/player-ranks`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ userIds: opponentIds })
          });
          if (response.ok) {
            const ranks = await response.json();
            const names = opponentIds.map(id => {
              const r = ranks.find(x => String(x.id) === String(id));
              return (r && r.username) ? r.username : `Player ${id}`;
            });
            setOpponentUsernames(names);
            setOpponentUsername(null);
          } else {
            setOpponentUsernames(opponentIds.map(id => `Player ${id}`));
            setOpponentUsername(null);
          }
        } catch (error) {
          console.error('Error fetching opponent usernames:', error);
          setOpponentUsernames(opponentIds.map(id => `Player ${id}`));
          setOpponentUsername(null);
        }
        return;
      }

      const opponentId = isPlayer1 ? missionData.player2_id : missionData.player1_id;
      if (!opponentId) {
        setOpponentUsername(null);
        setOpponentUsernames([]);
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/player-ranks`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ userIds: [opponentId] })
        });
        if (response.ok) {
          const ranks = await response.json();
          const opponentData = ranks.find(r => String(r.id) === String(opponentId));
          if (opponentData && opponentData.username) {
            setOpponentUsername(opponentData.username);
          } else {
            setOpponentUsername('Opponent');
          }
        } else {
          setOpponentUsername('Opponent');
        }
        setOpponentUsernames([]);
      } catch (error) {
        console.error('Error fetching opponent username:', error);
        setOpponentUsername('Opponent');
        setOpponentUsernames([]);
      }
    };
    fetchOpponentUsername();
  }, [missionData?.player1_id, missionData?.player2_id, missionData?.player3_id, missionData?.player4_id, missionData?.mission_mode, missionData?.status, isPlayer1, userId]);
  
  // Fetch user's strategies for their purpose
  useEffect(() => {
    // Safety check: ensure component is mounted and state setters are available
    if (typeof setUserStrategies !== 'function' || typeof setSelectedStrategyId !== 'function') {
      console.error('State setters not available');
      return;
    }
    
    const fetchUserStrategies = async () => {
      if (!missionData || !userId) return;
      
      let playerPurpose = null;
      if ((missionData.mission_mode === 'ffa' || missionData.mission_mode === '2v2') && viewerSlot >= 1 && viewerSlot <= 4) {
        playerPurpose = missionData[`player${viewerSlot}_purpose`] ?? null;
      }
      if (playerPurpose == null) playerPurpose = isPlayer1 ? missionData.player1_purpose : missionData.player2_purpose;
      if (!playerPurpose) return;
      
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const currentLanguage = window.i18n?.language || 'en-us';
        const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
        
        const response = await fetch(`${API_URL}/war-effort/strategies?lang=${encodeURIComponent(normalizedLanguage)}&purpose=${encodeURIComponent(playerPurpose)}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const strategies = await response.json();
          if (Array.isArray(strategies)) {
            setUserStrategies(strategies);
            // Auto-select first strategy if available (use functional update to avoid stale closure)
            if (strategies.length > 0) {
              setSelectedStrategyId(prev => {
                // Only set if not already set
                if (prev) return prev;
                return strategies[0].id;
              });
            }
          }
        }
      } catch (error) {
        console.error('Error fetching user strategies:', error);
      }
    };
    
    fetchUserStrategies();
  }, [missionData?.id, missionData?.mission_mode, missionData?.player1_purpose, missionData?.player2_purpose, missionData?.player3_purpose, missionData?.player4_purpose, isPlayer1, userId, viewerSlot]);

  // When owned country or HQ modal opens, fetch current defense strategy for that cell to show on button
  const ownedModalCellId = ownedCountryChoice?.cellId ?? ownedHQChoice?.cellId;
  useEffect(() => {
    if (!ownedModalCellId || !missionData?.id) {
      setOwnedCountryDefenseStrategyId(null);
      setOwnedCountryDefenseStrategyLoading(false);
      return;
    }
    if (ownedCountryChoice?.cellId === protectedCellId) {
      setOwnedCountryDefenseStrategyId(null);
      setOwnedCountryDefenseStrategyLoading(false);
      return;
    }
    let cancelled = false;
    setOwnedCountryDefenseStrategyLoading(true);
    const token = localStorage.getItem('token');
    fetch(
      `${API_URL}/war-effort/defense-strategy?missionId=${missionData.id}&countryCellId=${ownedModalCellId}`,
      { headers: { 'Authorization': `Bearer ${token}` } }
    )
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (cancelled) return;
        const id = data?.countryStrategyId ?? data?.defaultStrategyId ?? null;
        setOwnedCountryDefenseStrategyId(id != null ? id : null);
      })
      .catch(() => {
        if (!cancelled) setOwnedCountryDefenseStrategyId(null);
      })
      .finally(() => {
        if (!cancelled) setOwnedCountryDefenseStrategyLoading(false);
      });
    return () => { cancelled = true; };
  }, [ownedModalCellId, missionData?.id, protectedCellId, ownedCountryChoice?.cellId]);

  const ALL_HQ_CELL_IDS = Object.values(PURPOSE_TO_HQ_CELL_ID);
  // Fetch Tectonic Boredrive cost when owned country modal opens (for non-HQ, non-tectonic cell)
  useEffect(() => {
    if (!ownedCountryChoice?.cellId || !missionData?.id) {
      setTectonicBoredriveCost(null);
      setTectonicBoredriveResult(null);
      return;
    }
    const cellId = ownedCountryChoice.cellId;
    const tectonicIds = (missionData?.tectonic_boredrive_cell_ids || []).map((id) => Number(id)).filter((n) => !isNaN(n));
    const isTectonicCell = tectonicIds.includes(Number(cellId));
    const isHQ = ALL_HQ_CELL_IDS.includes(Number(cellId));
    if (isTectonicCell || isHQ || ownedCountryChoice.cellId === protectedCellId) {
      setTectonicBoredriveCost(null);
      setTectonicBoredriveResult(null);
      return;
    }
    setTectonicBoredriveResult(null);
    let cancelled = false;
    fetch(`${API_URL}/war-effort/tectonic-boredrive-cost?cellId=${cellId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (!cancelled && data && typeof data.engineerCost === 'number') {
          setTectonicBoredriveCost(data.engineerCost);
        } else {
          setTectonicBoredriveCost(null);
        }
      })
      .catch(() => {
        if (!cancelled) setTectonicBoredriveCost(null);
      });
    return () => { cancelled = true; };
  }, [ownedCountryChoice?.cellId, missionData?.id, missionData?.tectonic_boredrive_cell_ids, protectedCellId]);

  // Fetch completion bonus status for owned country tooltip (80 collected + 30 orders of faction)
  useEffect(() => {
    const cellId = hoveredCell != null ? Number(hoveredCell) : null;
    if (cellId == null || isNaN(cellId) || !missionData?.id || !controlledCountries.includes(cellId)) {
      setCompletionBonusLoadingCell(null);
      return;
    }
    setCompletionBonusLoadingCell(cellId);
    let cancelled = false;
    fetch(`${API_URL}/war-effort/mission/${missionData.id}/completion-bonus-status?cellId=${cellId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    })
      .then((res) => res.ok ? res.json() : { lavender: false, cattle: false, quarry: false, sewer: false, portal: false })
      .then((data) => {
        if (!cancelled) setCompletionBonusStatus((prev) => ({ ...prev, [cellId]: data }));
      })
      .catch(() => {
        if (!cancelled) setCompletionBonusStatus((prev) => ({ ...prev, [cellId]: { lavender: false, cattle: false, quarry: false, sewer: false, portal: false } }));
      })
      .finally(() => {
        if (!cancelled) setCompletionBonusLoadingCell((c) => (c === cellId ? null : c));
      });
    return () => { cancelled = true; };
  }, [hoveredCell, missionData?.id, controlledCountries]);

  // Building page placement: track mouse on document so ghost size updates (avoids missed events)
  useEffect(() => {
    if (!showBuildingPage || !buildingForPlacement) return;
    const onMove = (e) => setPlacementMouse({ x: e.clientX, y: e.clientY });
    document.addEventListener('mousemove', onMove, { passive: true });
    return () => document.removeEventListener('mousemove', onMove);
  }, [showBuildingPage, buildingForPlacement]);

  // World map: after 10 seconds, briefly show popup pointing to Madagascar (Trade with The Sentients)
  // Do not show if Madagascar will not trade (e.g. war criminal – "Madagascar will not trade with a war criminal")
  useEffect(() => {
    if (showBuildingPage) {
      if (madagascarHintTimerRef.current) {
        clearTimeout(madagascarHintTimerRef.current);
        madagascarHintTimerRef.current = null;
      }
      if (madagascarHintHideTimerRef.current) {
        clearTimeout(madagascarHintHideTimerRef.current);
        madagascarHintHideTimerRef.current = null;
      }
      setShowMadagascarTradeHint(false);
      return;
    }
    const missionId = missionData?.id;
    madagascarHintTimerRef.current = setTimeout(() => {
      madagascarHintTimerRef.current = null;
      const checkAndShowHint = async () => {
        try {
          const token = localStorage.getItem('token');
          if (!token || !missionId) return;
          const res = await fetch(`${API_URL}/war-effort/trading-hub/status?missionId=${missionId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (!res.ok) return;
          const data = await res.json();
          if (data.tradingBlocked) return; // do not show hint when "Madagascar will not trade with a war criminal"
          setShowMadagascarTradeHint(true);
          madagascarHintHideTimerRef.current = setTimeout(() => {
            madagascarHintHideTimerRef.current = null;
            setShowMadagascarTradeHint(false);
          }, 4500);
        } catch (e) {
          console.error('Trading hub status check for Madagascar hint:', e);
        }
      };
      checkAndShowHint();
    }, 10000);
    return () => {
      if (madagascarHintTimerRef.current) {
        clearTimeout(madagascarHintTimerRef.current);
        madagascarHintTimerRef.current = null;
      }
      if (madagascarHintHideTimerRef.current) {
        clearTimeout(madagascarHintHideTimerRef.current);
        madagascarHintHideTimerRef.current = null;
      }
    };
  }, [showBuildingPage, missionData?.id]);

  // Fetch unlocked HQ buildings when building page is shown
  useEffect(() => {
    if (!showBuildingPage) return;
    const fetchUnlocked = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch(`${API_URL}/war-effort/unlocked-buildings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data.unlockedBuildingIds)) {
            setUnlockedBuildingIds(data.unlockedBuildingIds);
          }
        }
      } catch (err) {
        console.error('Error fetching unlocked buildings:', err);
      }
    };
    fetchUnlocked();
  }, [showBuildingPage]);

  // Load persisted HQ placements on mount (for map tooltips) and when building page opens (same mission)
  useEffect(() => {
    if (!missionData?.id) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const loadPlacements = async () => {
      try {
        const res = await fetch(`${API_URL}/war-effort/mission/${missionData.id}/hq-placements`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        const placements = data.placements || [];
        const w = typeof window !== 'undefined' ? window.innerWidth : 1024;
        const h = typeof window !== 'undefined' ? window.innerHeight : 600;
        const converted = placements.map((p) => {
          const building = HQ_BUILDINGS.find((b) => b.id === p.building_id);
          if (!building) return null;
          const item = {
            building,
            x: (p.x_ratio ?? 0) * w,
            y: (p.y_ratio ?? 0) * h,
            size: Math.round((p.size_ratio ?? 0.1) * h),
            placementId: p.id
          };
          if (typeof p.produced_amount === 'number') {
            item.currentProduced = p.produced_amount;
            item.maxProduced = p.produced_max ?? 20;
            item.produced_per_minute = p.produced_per_minute ?? 1;
            item.last_ration_tick_at = p.last_ration_tick_at || null;
            if (typeof p.building_complete === 'boolean') item.building_complete = p.building_complete;
          }
          if (p.building_id === 'training_grounds') {
            item.trainingProgress = typeof p.training_progress === 'number' ? p.training_progress : 0;
            item.trainingReady = Boolean(p.training_ready);
            item.training_completed_at = p.training_completed_at || null;
          }
          if (p.building_id === 'barracks' || p.building_id === 'barracks_upgraded') {
            item.barracksProgress = typeof p.barracks_progress === 'number' ? p.barracks_progress : 0;
            item.barracksReady = Boolean(p.barracks_ready);
            item.barracks_completed_at = p.barracks_completed_at || null;
            item.barracks_pending_order_ids = Array.isArray(p.barracks_pending_order_ids) ? p.barracks_pending_order_ids : [];
            item.barracks_faction_id = p.barracks_faction_id != null ? parseInt(p.barracks_faction_id, 10) : null;
          }
          if (p.building_id === 'armory' || p.building_id === 'armory_upgraded') {
            item.armoryProgress = typeof p.armory_progress === 'number' ? p.armory_progress : 0;
            item.armoryReady = Boolean(p.armory_ready);
            item.armory_completed_at = p.armory_completed_at || null;
            item.armory_pending_order_ids = Array.isArray(p.armory_pending_order_ids) ? p.armory_pending_order_ids : [];
            item.armory_faction_id = p.armory_faction_id != null ? parseInt(p.armory_faction_id, 10) : null;
          }
          if (p.building_id === 'drone_hangar' || p.building_id === 'drone_hangar_upgraded') {
            item.droneHangarProgress = typeof p.drone_hangar_progress === 'number' ? p.drone_hangar_progress : 0;
            item.droneHangarReady = Boolean(p.drone_hangar_ready);
            item.drone_hangar_completed_at = p.drone_hangar_completed_at || null;
            item.drone_hangar_pending_order_ids = Array.isArray(p.drone_hangar_pending_order_ids) ? p.drone_hangar_pending_order_ids : [];
            item.drone_hangar_faction_id = p.drone_hangar_faction_id != null ? parseInt(p.drone_hangar_faction_id, 10) : null;
          }
          if (p.building_id === 'communications_hub' || p.building_id === 'communications_hub_upgraded') {
            item.comhubProgress = typeof p.comhub_progress === 'number' ? p.comhub_progress : 0;
            item.comhubReady = Boolean(p.comhub_ready);
            item.comhub_completed_at = p.comhub_completed_at || null;
            item.comhub_pending_order_ids = Array.isArray(p.comhub_pending_order_ids) ? p.comhub_pending_order_ids : [];
            item.communications_hub_faction_id = p.communications_hub_faction_id != null ? parseInt(p.communications_hub_faction_id, 10) : null;
          }
          return item;
        }).filter(Boolean);
        setPlacedBuildings(converted);
      } catch (err) {
        console.error('Error loading HQ placements:', err);
      }
    };
    loadPlacements();
    // Only poll for placement updates when building page is open
    if (!showBuildingPage) return;
    const interval = setInterval(loadPlacements, 60000); // 60s - was 20s, reduced network load
    return () => clearInterval(interval);
  }, [showBuildingPage, missionData?.id, placementRefreshTrigger]);

  const handleCountryClick = (cellId) => {
    playSoundEffect('/sounds/click.mp3');
    const cellIdNum = Number(cellId);
    
    // DEV MODE: If dev mode is active and a purpose is selected, set country ownership
    if (devModeActive && devSelectedPurpose !== null) {
      handleDevSetCountryPurpose(cellIdNum);
      return;
    }
    
    // If in attack mode, treat click as selecting target or cancel (must run before "direct click" message)
    if (attackFromCountry) {
      if (cellIdNum === attackFromCountry.cellId) {
        setAttackFromCountry(null);
        setAttackModeTargetCost(null);
        setError(null);
        return;
      }
      const oppHqCellId = opponentPurpose ? PURPOSE_TO_HQ_CELL_ID[opponentPurpose] : null;
      const isFfa = missionData?.mission_mode === 'ffa';
      const ffaHqEntry = isFfa && Array.isArray(enemyHqAttackInfo?.attackableHQs) && enemyHqAttackInfo.attackableHQs.find(h => h.hqCellId === cellIdNum);
      const isOpponentHQ = ffaHqEntry || (oppHqCellId != null && cellIdNum === oppHqCellId);
      if (isOpponentHQ) {
        if (isCeaseFireActive) {
          setError(`Cease fire in effect. No attacks allowed for ${formatCeaseFireCountdown(ceaseFireRemainingSeconds)}.`);
          return;
        }
        if (isConquestAttackerCeaseFireActive) {
          setError(`You cannot attack an enemy headquarters for ${formatCeaseFireCountdown(conquestAttackerCeaseFireRemainingSeconds)} after conquering a fortified country. Other attacks are allowed.`);
          return;
        }
        if (ffaHqEntry) {
          setAttackEnemyHQDialog({
            show: true,
            countryName: ffaHqEntry.countryName || getCountryFromCellId(cellIdNum),
            hqCellId: ffaHqEntry.hqCellId,
            movementCost: ffaHqEntry.movementCost,
            defenderUserId: ffaHqEntry.defenderUserId
          });
        } else {
          setAttackEnemyHQDialog({
            show: true,
            countryName: getCountryFromCellId(cellIdNum),
            hqCellId: oppHqCellId,
            movementCost: enemyHqAttackInfo?.movementCost ?? null,
            defenderUserId: null
          });
        }
        setError(null);
        return;
      }
      if (opponentFortifiedCountries.includes(cellIdNum) || opponentFortifiedCountries.includes(cellId)) {
        if (isCeaseFireActive) {
          setError(`Cease fire in effect. No countries can be attacked for ${formatCeaseFireCountdown(ceaseFireRemainingSeconds)}.`);
          return;
        }
        if (isConquestAttackerCeaseFireActive) {
          setError(`You cannot attack another opponent fortified country for ${formatCeaseFireCountdown(conquestAttackerCeaseFireRemainingSeconds)}. Neutrals and other targets are still allowed.`);
          return;
        }
        if (isOpponentFortifiedCeaseFireActive) {
          setError(`Opponent fortified countries are protected for ${formatCeaseFireCountdown(opponentFortifiedCeaseFireRemainingSeconds)}.`);
          return;
        }
        const reconquerEntry = reconquerCooldowns.find((r) => r.cell_id === cellIdNum);
        if (reconquerEntry && reconquerEntry.remaining_seconds > 0) {
          setError(`Cannot reconquer this country for ${formatCeaseFireCountdown(reconquerEntry.remaining_seconds)}.`);
          return;
        }
        let opponentFortifications = isPlayer1 ? missionData?.player2_fortifications : missionData?.player1_fortifications;
        if (missionData?.mission_mode === 'ffa' && missionData && viewerSlot >= 1 && viewerSlot <= 4) {
          for (let s = 1; s <= 4; s++) {
            if (s === viewerSlot) continue;
            const countries = missionData[`player${s}_fortified_countries`];
            const arr = Array.isArray(countries) ? countries : (countries ? [].concat(countries) : []);
            if (arr.includes(cellIdNum) || arr.includes(Number(cellIdNum))) {
              opponentFortifications = missionData[`player${s}_fortifications`] || {};
              break;
            }
          }
        }
        const fortificationData = opponentFortifications && (opponentFortifications[cellIdNum] || opponentFortifications[String(cellIdNum)]);
        const fortificationType = fortificationData?.type || 'unknown';
        const fortification = FORTIFICATION_TYPES.find(f => f.type === fortificationType);
        const fortificationName = fortificationType === 'eclipse_hidden' ? t('warEffort.fortificationTypes.eclipse_hidden') : (fortification ? t(`warEffort.fortificationTypes.${fortification.type}`) : fortificationType);
        setAttackDialog({
          show: true,
          countryCellId: cellIdNum,
          fortificationType: fortificationName,
          fortificationTypeHidden: fortificationType === 'eclipse_hidden',
          countryName: getCountryFromCellId(cellIdNum)
        });
        setError(null);
        return;
      }
      const cellOwnerInAttack = cellOwnersMap[String(cellIdNum)] || cellOwnersMap[String(cellId)];
      if (cellOwnerInAttack) {
        if (cellIdNum === SENTIENTS_HQ_CELL_ID) {
          setTradingHubOpen(true);
          setAttackFromCountry(null);
          setAttackModeTargetCost(null);
          setError(null);
          return;
        }
        const playerPurpose = isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose;
        const allowedFactions = playerPurpose ? PURPOSE_TO_FACTION_MAPPING[playerPurpose] || [] : [];
        const opponentPurpose = isPlayer1 ? missionData?.player2_purpose : missionData?.player1_purpose;
        const opponentAllowedFactions = opponentPurpose ? PURPOSE_TO_FACTION_MAPPING[opponentPurpose] || [] : [];
        const allOpponentFactions = (missionData?.mission_mode === 'ffa' && missionData)
          ? [1, 2, 3, 4]
              .filter(s => (s !== viewerSlot && missionData[`player${s}_id`] != null))
              .flatMap(s => PURPOSE_TO_FACTION_MAPPING[missionData[`player${s}_purpose`]] || [])
          : opponentAllowedFactions;
        const isPlayerFaction = allowedFactions.includes(cellOwnerInAttack);
        const isOpponentFaction = (missionData?.mission_mode === 'ffa' ? allOpponentFactions : opponentAllowedFactions).includes(cellOwnerInAttack);
        if (isOpponentFaction && Object.values(PURPOSE_TO_HQ_CELL_ID).includes(cellIdNum)) {
          const defId = getDefenderUserIdForHQCell(missionData, cellIdNum);
          if (missionData?.mission_mode === 'ffa' && defId != null && Number(defId) !== Number(userId)) {
            const inAttackable = Array.isArray(enemyHqAttackInfo?.attackableHQs) && enemyHqAttackInfo.attackableHQs.some(h => h.hqCellId === cellIdNum);
            if (!inAttackable) {
              setError('This opponent still has fortified countries. You can attack their HQ when they have none left.');
              return;
            }
          } else if (missionData?.mission_mode !== 'ffa' && oppHqCellId != null && cellIdNum !== oppHqCellId) {
            setError('Only the opponent\'s headquarters can be attacked (when they have no fortified countries left). This is another faction\'s HQ.');
            return;
          }
        }
        if (isOpponentFaction && (oppHqCellId == null || cellIdNum !== oppHqCellId) && (missionData?.mission_mode === 'ffa' || missionData?.player2_id)) {
          if (isCeaseFireActive) {
            setError(`Cease fire in effect. No countries can be attacked for ${formatCeaseFireCountdown(ceaseFireRemainingSeconds)}.`);
            return;
          }
          const reconquerEntry = reconquerCooldowns.find((r) => r.cell_id === cellIdNum);
          if (reconquerEntry && reconquerEntry.remaining_seconds > 0) {
            setError(`Cannot reconquer this country for ${formatCeaseFireCountdown(reconquerEntry.remaining_seconds)}.`);
            return;
          }
          setAttackOtherCountryDialog({
            show: true,
            countryCellId: cellIdNum,
            countryName: getCountryFromCellId(cellIdNum),
            ownerFaction: cellOwnerInAttack,
            isOpponentUnfortified: true
          });
          setError(null);
          return;
        }
        if (!isPlayerFaction && !isOpponentFaction && (missionData?.mission_mode === 'ffa' || missionData?.player2_id)) {
          if (Object.values(PURPOSE_TO_HQ_CELL_ID).includes(cellIdNum)) {
            setError("This is a faction headquarters. Only the opponent's HQ can be attacked (when they have no fortified countries left).");
            return;
          }
          if (isCeaseFireActive) {
            setError(`Cease fire in effect. No countries can be attacked for ${formatCeaseFireCountdown(ceaseFireRemainingSeconds)}.`);
            return;
          }
          setAttackOtherCountryDialog({
            show: true,
            countryCellId: cellIdNum,
            countryName: getCountryFromCellId(cellIdNum),
            ownerFaction: cellOwnerInAttack,
            isOpponentUnfortified: false
          });
          setError(null);
          return;
        }
      }
      setAttackFromCountry(null);
      setAttackModeTargetCost(null);
      setError(null);
      return;
    }
    
    // Opponent or other faction country clicked without attack mode: require "own country → Attack → then target"
    const isControlled = controlledCountries.includes(cellIdNum) || controlledCountries.includes(cellId);
    const cellOwner = cellOwnersMap[String(cellIdNum)] || cellOwnersMap[String(cellId)];
    if (cellOwner && !isControlled) {
      if (cellIdNum === SENTIENTS_HQ_CELL_ID) {
        setTradingHubOpen(true);
        setError(null);
        return;
      }
      const playerPurpose = isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose;
      const allowedFactions = playerPurpose ? PURPOSE_TO_FACTION_MAPPING[playerPurpose] || [] : [];
      const opponentPurpose = isPlayer1 ? missionData?.player2_purpose : missionData?.player1_purpose;
      const opponentAllowedFactions = opponentPurpose ? PURPOSE_TO_FACTION_MAPPING[opponentPurpose] || [] : [];
      const isPlayerFaction = allowedFactions.includes(cellOwner);
      const isOpponentFaction = opponentAllowedFactions.includes(cellOwner);
      if (isOpponentFaction) {
        setError('To attack, click one of your countries, choose Attack Country, then select a target.');
        return;
      }
      if (!isPlayerFaction && !isOpponentFaction) {
        setError('To attack, click one of your countries, choose Attack Country, then select a target.');
        return;
      }
    }
    if (!isControlled && (opponentFortifiedCountries.includes(cellIdNum) || opponentFortifiedCountries.includes(cellId))) {
      setError('To attack, click one of your countries, choose Attack Country, then select a target.');
      return;
    }
    
    // Debug: Log when click fails
    if (!isControlled && controlledCountries.length > 0) {
      console.warn('Country not controlled:', {
        cellId,
        cellIdNum,
        isInArray: controlledCountries.includes(cellIdNum),
        controlledCount: controlledCountries.length,
        sampleControlled: controlledCountries.slice(0, 5)
      });
    }
    
    // Check if country is controlled by player's faction (based on purpose)
    if (!isControlled) {
      setError(`You do not control this country. You can only fortify countries controlled by your faction. (Cell ID: ${cellId}, Controlled: [${controlledCountries.join(', ')}])`);
      return;
    }
    
    // Owned country: show Fortify vs Attack choice only when not headquarters
    const playerPurpose = isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose;
    const hqCellId = playerPurpose ? PURPOSE_TO_HQ_CELL_ID[playerPurpose] : null;
    if (hqCellId != null && cellIdNum !== hqCellId) {
      setOwnedCountryChoice({ cellId: cellIdNum, countryName: getCountryFromCellId(cellIdNum) });
      setSelectedCountry(null);
      setSelectedFortification(null);
      setError(null);
      return;
    }
    
    // Own HQ: show Attack Country / Show HQ choice dialog (do not instantly enter build mode)
    if (hqCellId != null && cellIdNum === hqCellId) {
      setOwnedHQChoice({ cellId: cellIdNum, countryName: getCountryFromCellId(cellIdNum) });
      setSelectedCountry(null);
      setSelectedFortification(null);
      setError(null);
      return;
    }
    
    // No HQ mapping: go straight to fortification selection for other owned cells
    if (playerFortifiedCountries.includes(cellId) || playerFortifiedCountries.includes(cellIdNum)) {
      setError('This country is already fortified');
      return;
    }
    if (playerFortificationSlotsUsed >= 3) {
      setError('You have already fortified the maximum of 3 countries');
      return;
    }
    setSelectedCountry(cellId);
    setSelectedFortification(null);
    setError(null);
  };
  
  const handleFortifyWithType = async (fortificationType) => {
    if (!selectedCountry || !fortificationType) {
      setError('Please select a country and fortification type');
      return;
    }

    const fortification = FORTIFICATION_TYPES.find(f => f.type === fortificationType);
    if (!fortification) {
      setError('Invalid fortification type');
      return;
    }

    if (playerResources < fortification.cost) {
      setError(`Insufficient resources. Need ${fortification.cost}, have ${playerResources}`);
      return;
    }

    if (playerFortificationSlotsUsed >= 3) {
      setError('You have already fortified the maximum of 3 countries');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSelectedFortification(fortificationType);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/fortify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          missionId: missionData.id,
          countryCellId: selectedCountry,
          fortificationType: fortificationType
        })
      });

      if (response.ok) {
        const result = await response.json();
        // Update mission data (use viewerSlot for FFA/2v2 so correct player slot is updated)
        const updatedMission = { ...missionData };
        const slot = (missionData?.mission_mode === 'ffa' || missionData?.mission_mode === '2v2') ? viewerSlot : (isPlayer1 ? 1 : 2);
        if (slot >= 1 && slot <= 4) {
          const countriesKey = `player${slot}_fortified_countries`;
          const resourcesKey = `player${slot}_resources`;
          const slotsKey = `player${slot}_fortification_slots_used`;
          const fortificationsKey = `player${slot}_fortifications`;
          updatedMission[resourcesKey] = result.remainingResources;
          updatedMission[countriesKey] = [...playerFortifiedCountries, selectedCountry];
          updatedMission[slotsKey] = playerFortificationSlotsUsed + 1;
          // Merge new fortification into player*_fortifications so tooltip shows manual fortification immediately
          // (e.g. when country already had Command Center bonus, tooltip reads from this object)
          const cellIdNum = Number(selectedCountry);
          const existingForts = updatedMission[fortificationsKey] && typeof updatedMission[fortificationsKey] === 'object' ? updatedMission[fortificationsKey] : {};
          const newFortEntry = { type: fortificationType };
          updatedMission[fortificationsKey] = {
            ...existingForts,
            [cellIdNum]: newFortEntry,
            [String(cellIdNum)]: newFortEntry
          };
        }
        setMissionData(updatedMission);

        // Reset selection
        setSelectedCountry(null);
        setSelectedFortification(null);

        // Refresh controlled countries
        fetchControlledCountries();

        if (onFortificationComplete) {
          onFortificationComplete(updatedMission);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fortify country');
        setSelectedFortification(null);
      }
    } catch (error) {
      console.error('Error fortifying country:', error);
      setError('Failed to fortify country');
      setSelectedFortification(null);
    } finally {
      setLoading(false);
    }
  };
  
  const playerResources = playerResource ? playerResource.amount : 0;
  const availableFortifications = FORTIFICATION_TYPES.filter(f => playerResources >= f.cost);
  
  // Handle attack on opponent's fortified country
  const handleAttackOpponentFortified = async () => {
    if (!attackDialog.countryCellId) {
      setError('Missing country information to launch attack');
      setAttackDialog({ show: false, countryCellId: null, fortificationType: null, countryName: null });
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/attack-fortified`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          missionId: missionData.id,
          countryCellId: attackDialog.countryCellId,
          ...(attackFromCountry?.cellId != null && { fromCellId: attackFromCountry.cellId })
        })
      });
      
      if (response.ok) {
        const { gameId, gameState } = await response.json();
        setAttackDialog({ show: false, countryCellId: null, fortificationType: null, countryName: null });
        setAttackFromCountry(null);
        setAttackModeTargetCost(null);
        navigate(`/battle/${gameId}`, { state: { gameState, gameId } });
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to attack fortified country');
        // Don't close dialog on error - let user try again
      }
    } catch (error) {
      console.error('Error attacking fortified country:', error);
      setError('Failed to attack fortified country');
      // Don't close dialog on error - let user try again
    } finally {
      setLoading(false);
    }
  };
  
  // Handle attack on other faction's country
  const handleAttackOtherCountry = async () => {
    if (!attackOtherCountryDialog.countryCellId) {
      setError('Missing country information to launch attack');
      setAttackOtherCountryDialog({ show: false, countryCellId: null, countryName: null, ownerFaction: null, isOpponentUnfortified: false });
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const endpoint = attackOtherCountryDialog.isOpponentUnfortified
        ? `${API_URL}/war-effort/attack-opponent-country`
        : `${API_URL}/war-effort/attack-other-country`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          missionId: missionData.id,
          countryCellId: attackOtherCountryDialog.countryCellId,
          ...(attackFromCountry?.cellId != null && { fromCellId: attackFromCountry.cellId })
        })
      });
      
      if (response.ok) {
        const { gameId, gameState } = await response.json();
        setAttackOtherCountryDialog({ show: false, countryCellId: null, countryName: null, ownerFaction: null, isOpponentUnfortified: false });
        setAttackFromCountry(null);
        setAttackModeTargetCost(null);
        navigate(`/battle/${gameId}`, { state: { gameState, gameId } });
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to attack country');
        // Don't close dialog on error - let user try again
      }
    } catch (error) {
      console.error('Error attacking other country:', error);
      setError('Failed to attack country');
      // Don't close dialog on error - let user try again
    } finally {
      setLoading(false);
    }
  };

  // Handle attack on enemy HQ (when opponent has no fortified countries left; cost = movement + 400; winner wins mission)
  const handleAttackEnemyHQ = async () => {
    if (!attackEnemyHQDialog.hqCellId || attackEnemyHQDialog.movementCost == null) {
      setError('Missing HQ attack information');
      setAttackEnemyHQDialog({ show: false, countryName: null, hqCellId: null, movementCost: null, defenderUserId: null });
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/attack-enemy-hq`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          missionId: missionData.id,
          ...(attackFromCountry?.cellId != null && { fromCellId: attackFromCountry.cellId }),
          ...(attackEnemyHQDialog?.hqCellId != null && { hqCellId: attackEnemyHQDialog.hqCellId }),
          ...(attackEnemyHQDialog?.defenderUserId != null && { defenderUserId: attackEnemyHQDialog.defenderUserId })
        })
      });
      if (response.ok) {
        const { gameId, gameState } = await response.json();
        setAttackEnemyHQDialog({ show: false, countryName: null, hqCellId: null, movementCost: null, defenderUserId: null });
        setAttackFromCountry(null);
        setAttackModeTargetCost(null);
        setEnemyHqAttackInfo(null);
        navigate(`/battle/${gameId}`, { state: { gameState, gameId } });
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to attack enemy HQ');
      }
    } catch (error) {
      console.error('Error attacking enemy HQ:', error);
      setError('Failed to attack enemy HQ');
    } finally {
      setLoading(false);
    }
  };
  
  const energyAmount = playerResource ? playerResource.amount : 0;
  const energyCap = (playerResource && typeof playerResource.cap === 'number') ? playerResource.cap : 1000;
  const energyFillPercent = energyCap > 0 ? Math.min(100, Math.max(0, (energyAmount / energyCap) * 100)) : 0;
  const getEffectiveBuildingCost = (baseCost) => Math.floor((baseCost || 0) * (buildingCostMultiplier ?? 1));

  const handleTransferEnergy = useCallback(async (amount) => {
    if (!missionData?.id || transferEnergyLoading) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    setTransferEnergyLoading(true);
    try {
      const res = await fetch(`${API_URL}/war-effort/mission/${missionData.id}/transfer-energy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ amount })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setTransferEnergyModalOpen(false);
        const transferred = data.transferred ?? 0;
        const kept = data.kept ?? 0;
        if (playerResource && typeof playerResource.amount === 'number') {
          setPlayerResource((prev) => prev ? { ...prev, amount: prev.amount - transferred } : null);
        }
        if (transferred > 0) {
          if (kept > 0) {
            setError(`Transferred ${transferred} energy to ally. ${kept} kept (ally at cap).`);
          } else {
            setError(`Transferred ${transferred} energy to ally.`);
          }
        } else if (kept > 0) {
          setError('Ally is at energy cap. No energy transferred.');
        }
        fetchControlledCountries();
      } else {
        setError(data.error || 'Failed to transfer energy');
      }
    } catch (err) {
      console.error('Transfer energy:', err);
      setError('Failed to transfer energy');
    } finally {
      setTransferEnergyLoading(false);
    }
  }, [missionData?.id, transferEnergyLoading, playerResource, fetchControlledCountries]);

  // Producer display: same formula as server so UI shows the amount that will be collected
  const [producerTickAt, setProducerTickAt] = useState(0);
  useEffect(() => {
    if (!showBuildingPage) return;
    const interval = setInterval(() => setProducerTickAt(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [showBuildingPage]);

  const powerplantPlacementsRef = useRef([]);
  powerplantPlacementsRef.current = showBuildingPage
    ? placedBuildings
        .filter((item) => item.building?.id === 'powerplant' || item.building?.id === 'powerplant_upgraded')
        .map((p) => ({ x: p.x, y: p.y, amount: p.building?.id === 'powerplant_upgraded' ? 16 : 8 }))
    : [];

  // Powerplant energy grant animation every 60s (server grants 8 energy per powerplant per minute, 16 for upgraded)
  // When the animation plays, optimistically update the energy bar so it stays in sync with the visual
  useEffect(() => {
    if (!showBuildingPage) return;
    const run = () => {
      const placements = powerplantPlacementsRef.current;
      if (placements.length === 0) return;
      const totalGrant = placements.reduce((sum, p) => sum + (p.amount ?? 0), 0);
      setPlayerResource((prev) => {
        if (!prev) return prev;
        const cap = typeof prev.cap === 'number' ? prev.cap : 1000;
        const current = typeof prev.amount === 'number' ? prev.amount : 0;
        return { ...prev, amount: Math.min(cap, current + totalGrant) };
      });
      setPowerplantFlashPlacements([...placements]);
      setTimeout(() => setPowerplantFlashPlacements([]), 900);
    };
    const interval = setInterval(run, 60000);
    return () => clearInterval(interval);
  }, [showBuildingPage]);

  // Fetch full order data for barracks "Choose an order" modal (OrderDesign cards)
  useEffect(() => {
    if (!barracksOrdersModal.show || !barracksOrdersModal.orders?.length) {
      setBarracksEnrichedOrders([]);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;
    const fetchAll = async () => {
      const enriched = await Promise.all(
        barracksOrdersModal.orders.map(async (ord) => {
          try {
            const res = await fetch(`${API_URL}/order/${ord.id}/full?${buildOrderDetailLangQuery(i18nInstance.language)}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error(`Order ${ord.id} not found`);
            const full = await res.json();
            full.faction = full.faction || full.faction_name;
            full.rarity = ord.rarity || full.rarity;
            return full;
          } catch (err) {
            console.error(`Failed to fetch order ${ord.id}:`, err);
            return null;
          }
        })
      );
      setBarracksEnrichedOrders(enriched.filter(Boolean));
    };
    fetchAll();
  }, [barracksOrdersModal.show, barracksOrdersModal.orders, i18nInstance.language]);

  function getDisplayedProduced(item) {
    if (item.building_complete) return item.currentProduced ?? item.maxProduced ?? 80;
    if (item.last_ration_tick_at == null) return item.currentProduced ?? 0;
    const ref = new Date(item.last_ration_tick_at).getTime();
    const minutesElapsed = Math.floor((Date.now() - ref) / 60000);
    const perMinute = item.produced_per_minute ?? 1;
    const max = item.maxProduced ?? 20;
    return Math.min(max, minutesElapsed * perMinute);
  }

  // Mission over: show win/loss screen for winner or loser (including when they were offline). Both can dismiss.
  // If we just came back from AfterBattle after dismissing, clear mission and avoid redirect loop.
  const justDismissedMissionId = missionData?.status === 'completed' && location.state?.dismissedMissionId != null && location.state.dismissedMissionId === missionData.id;

  // Top-level effect: when user landed with dismissedMissionId matching this completed mission, clear and replace state (breaks redirect loop).
  useEffect(() => {
    if (!justDismissedMissionId) return;
    skipRedirectForMissionIdRef.current = missionData?.id ?? null;
    onBack();
    navigate('/wareffort', { replace: true });
  }, [justDismissedMissionId, missionData?.id, location.state?.dismissedMissionId, onBack, navigate]);

  // Clear skip ref when we're no longer showing that mission (parent cleared it)
  useEffect(() => {
    if (missionData?.id != null && missionData.id !== skipRedirectForMissionIdRef.current) {
      skipRedirectForMissionIdRef.current = null;
    }
    if (missionData == null) {
      skipRedirectForMissionIdRef.current = null;
    }
  }, [missionData?.id, missionData]);

  if (justDismissedMissionId) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px', color: '#fff' }}>
        {t('common.loading', 'Loading...')}
      </div>
    );
  }

  // After dismiss, replace clears location.state so justDismissedMissionId becomes false before parent clears mission. Skip redirect until mission is cleared.
  const skipRedirectBecauseJustDismissed = missionData?.status === 'completed' && missionData?.id != null && skipRedirectForMissionIdRef.current === missionData.id;

  if (skipRedirectBecauseJustDismissed) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px', color: '#fff' }}>
        {t('common.loading', 'Loading...')}
      </div>
    );
  }

  // Winners with rewards: redirect to AfterBattle so they get the same look/feel as "Dev War Effort Rewards" (wareffort_success.jpg, message phase, flip cards, grant).
  if (missionData?.status === 'completed') {
    const playerWon = missionData.mission_mode === 'ffa'
      ? (userId != null && missionData.winner_id != null && Number(missionData.winner_id) === Number(userId))
      : ((isPlayer1 && missionData.player1_mission_complete) || (!isPlayer1 && missionData.player2_mission_complete));
    if (playerWon && missionData.mission_end_game_id) {
      return (
        <WarEffortRedirectToAfterBattle
          missionId={missionData.id}
          missionEndGameId={missionData.mission_end_game_id}
          result="win"
          navigate={navigate}
        />
      );
    }
    if (!playerWon && missionData.mission_end_game_id) {
      return (
        <WarEffortRedirectToAfterBattle
          missionId={missionData.id}
          missionEndGameId={missionData.mission_end_game_id}
          result="loss"
          navigate={navigate}
        />
      );
    }
    return (
      <WarEffortMissionOverScreen
        playerWon={playerWon}
        missionId={missionData.id}
        missionEndGameId={missionData.mission_end_game_id}
        onBack={onBack}
        API_URL={API_URL}
        t={t}
      />
    );
  }

  const fortifiedCountryWarningOverlay = fortifiedCountryUnderAttackWarning && fortifiedCountryUnderAttackWarning.countryNames?.length > 0 && typeof document !== 'undefined' && createPortal(
    <div
      className="fortified-country-under-attack-warning"
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 100001,
        padding: '24px 48px',
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        border: '4px solid #cc0000',
        borderRadius: '12px',
        color: '#fff',
        fontSize: '22px',
        fontWeight: 'bold',
        textAlign: 'center',
        boxShadow: '0 0 40px rgba(204, 0, 0, 0.6)',
        pointerEvents: 'none'
      }}
    >
      {fortifiedCountryUnderAttackWarning.countryNames.length === 1
        ? t('warEffort.fortifiedCountryUnderAttackSingle', { country: fortifiedCountryUnderAttackWarning.countryNames[0] })
        : t('warEffort.fortifiedCountryUnderAttackMultiple', { countries: `${fortifiedCountryUnderAttackWarning.countryNames.slice(0, -1).join(', ')} and ${fortifiedCountryUnderAttackWarning.countryNames[fortifiedCountryUnderAttackWarning.countryNames.length - 1]}` })}
    </div>,
    document.body
  );

  // Building page when clicking own HQ (cannot fortify headquarters).
  // Friends, Settings, and username are already shown by parent WareEffort (top-left).
  if (showBuildingPage) {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const h = typeof window !== 'undefined' ? window.innerHeight : 600;
    const yNorm = Math.max(0, Math.min(1, placementMouse.y / h));
    const zoomT = Math.pow(yNorm, 1.6);
    const placementGhostMin = 40;
    const placementGhostMax = 720;
    const placementGhostSize = buildingForPlacement
      ? Math.round(placementGhostMin + zoomT * (placementGhostMax - placementGhostMin))
      : 0;
    return (
      <>
        {achievementUnlocked && (
          <AchievementUnlockedToast
            titleId={achievementUnlocked.titleId}
            titleName={achievementUnlocked.titleName}
            color={achievementUnlocked.color}
            achievementKey={achievementUnlocked.achievementKey}
            onClose={() => setAchievementUnlocked(null)}
          />
        )}
        {pendingToast && (
          <AchievementUnlockedToast
            titleId={pendingToast.titleId}
            titleName={pendingToast.titleName}
            color={pendingToast.color}
            achievementKey={pendingToast.achievementKey}
            onClose={dismissPendingToast}
          />
        )}
        {fortifiedCountryWarningOverlay}
      <div
        className="war-effort-hq-building-page"
        style={{
          position: 'fixed',
          inset: 0,
          width: '100vw',
          height: '100vh',
          backgroundImage: 'url(/images/area.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          overflow: 'auto',
          cursor: buildingForPlacement ? 'none' : undefined
        }}
        onMouseMove={(e) => {
          if (buildingForPlacement) setPlacementMouse({ x: e.clientX, y: e.clientY });
        }}
        onClick={(e) => {
          if (buildingForPlacement && !e.target.closest('button')) {
            const h = typeof window !== 'undefined' ? window.innerHeight : 600;
            const yNorm = Math.max(0, Math.min(1, placementMouse.y / h));
            const zoomT = Math.pow(yNorm, 1.6);
            const placementGhostMin = 40;
            const placementGhostMax = 720;
            const sizeAtDrop = Math.round(placementGhostMin + zoomT * (placementGhostMax - placementGhostMin));
            const newX = placementMouse.x;
            const newY = placementMouse.y;
            const w = typeof window !== 'undefined' ? window.innerWidth : 1024;
            const halfSize = sizeAtDrop / 2;
            const fullyVisibleOnScreen =
              newX - halfSize >= 0 &&
              newX + halfSize <= w &&
              newY - halfSize >= 0 &&
              newY + halfSize <= h;
            const inBuildableZone = newY >= h / 4;
            // Improved overlap check: use circular collision (Euclidean distance) with stricter threshold
            // This prevents diagonal corner-to-corner placements that looked bad with box collision
            const overlapsExisting = placedBuildings.some((item) => {
              const s1 = sizeAtDrop;
              const s2 = item.size ?? 432;
              const halfSum = (s1 + s2) / 2;
              // Use 0.65 multiplier (was 0.35) - allows ~35% overlap instead of ~65%
              // Add 20px gap for visual breathing room
              const threshold = Math.max(60, halfSum * 0.65 + 20);
              // Circular collision using Euclidean distance (not box-based)
              const dx = newX - item.x;
              const dy = newY - item.y;
              const distance = Math.sqrt(dx * dx + dy * dy);
              return distance < threshold;
            });
            if (fullyVisibleOnScreen && inBuildableZone && !overlapsExisting) {
              const building = buildingForPlacement;
              const cost = getEffectiveBuildingCost(building.cost);
              const { groupIds, cap } = getBuildingCapInfo(building.id);
              const builtOfType = placedBuildings.filter((p) => groupIds.includes(p.building.id)).length;
              if (builtOfType >= cap) {
                setBuildingForPlacement(null);
                setError(`Maximum number of ${building.name}s (${cap}) already built.`);
                return;
              }
              if (energyAmount < cost) {
                setBuildingForPlacement(null);
                setError(`Insufficient energy. Need ${cost}, have ${energyAmount}.`);
                return;
              }
              if (missionData?.id) {
                const x_ratio = newX / w;
                const y_ratio = newY / h;
                const size_ratio = sizeAtDrop / h;
                fetch(`${API_URL}/war-effort/mission/${missionData.id}/hq-placements`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`
                  },
                  body: JSON.stringify({
                    building_id: building.id,
                    cost,
                    x_ratio,
                    y_ratio,
                    size_ratio
                  })
                })
                  .then((res) => {
                    if (!res.ok) return res.json().then((body) => Promise.reject(new Error(body.error || 'Failed to save')));
                    return res.json();
                  })
                  .then((data) => {
                    playSoundEffect('/sounds/build.mp3');
                    const isProducer = ['cattle_ranch', 'quarry', 'sewers', 'portal', 'lavender_field', 'bank'].includes(building.id);
                    const newItem = {
                      building,
                      x: newX,
                      y: newY,
                      size: sizeAtDrop,
                      placementId: data.id
                    };
                    if (isProducer) {
                      newItem.currentProduced = 0;
                      const isCompletion = COMPLETION_BUILDING_IDS.includes(building.id);
                      newItem.maxProduced = building.id === 'bank' ? 100 : (isCompletion ? 80 : 20);
                      newItem.produced_per_minute = building.id === 'bank' ? 5 : 1;
                      newItem.last_ration_tick_at = new Date().toISOString();
                      if (isCompletion) newItem.building_complete = false;
                    }
                    if (building.id === 'training_grounds') {
                      newItem.trainingProgress = 0;
                      newItem.trainingReady = false;
                      newItem.training_completed_at = null;
                    }
                    if (building.id === 'barracks') {
                      newItem.barracksProgress = 0;
                      newItem.barracksReady = false;
                      newItem.barracks_completed_at = null;
                      newItem.barracks_pending_order_ids = [];
                      if (data.barracks_faction_id != null) newItem.barracks_faction_id = data.barracks_faction_id;
                    }
                    if (building.id === 'armory') {
                      newItem.armoryProgress = 0;
                      newItem.armoryReady = false;
                      newItem.armory_completed_at = null;
                      newItem.armory_pending_order_ids = [];
                      if (data.armory_faction_id != null) newItem.armory_faction_id = data.armory_faction_id;
                    }
                    if (building.id === 'drone_hangar') {
                      newItem.droneHangarProgress = 0;
                      newItem.droneHangarReady = false;
                      newItem.drone_hangar_completed_at = null;
                      newItem.drone_hangar_pending_order_ids = [];
                      if (data.drone_hangar_faction_id != null) newItem.drone_hangar_faction_id = data.drone_hangar_faction_id;
                    }
                    if (building.id === 'communications_hub') {
                      newItem.comhubProgress = 0;
                      newItem.comhubReady = false;
                      newItem.comhub_completed_at = null;
                      newItem.comhub_pending_order_ids = [];
                      if (data.communications_hub_faction_id != null) newItem.communications_hub_faction_id = data.communications_hub_faction_id;
                    }
                    setPlacedBuildings((prev) => [...prev, newItem]);
                    setBuildingForPlacement(null);
                    if (typeof data.remaining_energy === 'number' && playerResource) {
                      setPlayerResource((prev) => prev ? { ...prev, amount: data.remaining_energy } : null);
                    }
                    refetchMission();
                    fetchControlledCountries(); // refresh buildingCostMultiplier / hasInfrastructureSubsidy after subsidy consumed
                  })
                  .catch((err) => {
                    console.error('Error saving HQ placement:', err);
                    setError(err.message || 'Failed to save building.');
                  });
              } else {
                setPlacedBuildings((prev) => [...prev, {
                  building,
                  x: newX,
                  y: newY,
                  size: sizeAtDrop
                }]);
                setBuildingForPlacement(null);
              }
            }
          } else if (buildingForPlacement && !fullyVisibleOnScreen) {
            setError(t('warEffort.buildingMustBeFullyVisible'));
          } else if (selectedPlacementId != null && !e.target.closest('[data-hq-placement]') && !e.target.closest('button')) {
            setSelectedPlacementId(null);
          }
        }}
      >
        {/* Energy: floating transparent cylinder, below header to avoid overlap with Friends/Settings/username */}
        <div
          className="war-effort-energy-bar war-effort-map-chrome--energy"
        >
          <span className="war-effort-map-chrome--energy-label" style={{ fontSize: '10px', color: '#4ECDC4', marginTop: '0', fontWeight: 'bold', letterSpacing: '0.5px' }}>Energy</span>
          <span className="war-effort-map-chrome--energy-cap" style={{ fontSize: '11px', color: '#aaa', marginTop: '4px', fontWeight: 'bold' }}>{energyCap}</span>
          <div
            className="war-effort-energy-cylinder war-effort-map-chrome--energy-cylinder"
          >
            <div
              style={{
                width: '100%',
                height: `${energyFillPercent}%`,
                minHeight: energyAmount > 0 ? '4px' : 0,
                backgroundColor: energyAmount > 0 ? '#4ECDC4' : 'transparent',
                borderRadius: '0 0 10px 10px',
                transition: 'height 0.3s ease'
              }}
            />
          </div>
          <span className="war-effort-map-chrome--energy-zero" style={{ fontSize: '11px', color: '#aaa', marginBottom: '8px', fontWeight: 'bold' }}>0</span>
          <span className="war-effort-map-chrome--energy-amount" style={{ fontSize: '12px', color: '#4ECDC4', fontWeight: 'bold', marginBottom: '0' }}>
            {energyAmount}
          </span>
        </div>
        {showTransferEnergy && (
          <div className="war-effort-map-chrome--transfer">
            <button
              type="button"
              className="war-effort-transfer-energy-btn"
              onClick={() => { playSoundEffect('/sounds/click.mp3'); setTransferEnergyModalOpen(true); }}
              style={{
                fontSize: '9px',
                padding: '4px 2px',
                cursor: 'pointer',
                backgroundColor: 'rgba(78, 205, 196, 0.25)',
                border: '1px solid rgba(78, 205, 196, 0.6)',
                borderRadius: '6px',
                color: '#4ECDC4',
                fontWeight: 'bold',
                whiteSpace: 'normal',
                lineHeight: 1.2,
                width: '100%'
              }}
            >
              {t('warEffort.transferEnergy', 'Transfer Energy')}
            </button>
          </div>
        )}
        {transferEnergyModalOpen && createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="transfer-energy-title"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0,0,0,0.7)',
              pointerEvents: 'auto'
            }}
            onClick={(e) => { if (e.target === e.currentTarget) setTransferEnergyModalOpen(false); }}
          >
            <div
              style={{
                backgroundColor: '#2a2a2a',
                border: '2px solid rgba(78, 205, 196, 0.6)',
                borderRadius: 12,
                padding: 20,
                minWidth: 220,
                boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 id="transfer-energy-title" style={{ margin: '0 0 16px', color: '#4ECDC4', fontSize: 16 }}>{t('warEffort.transferEnergy', 'Transfer Energy')}</h3>
              <p style={{ margin: '0 0 12px', color: '#aaa', fontSize: 12 }}>{t('warEffort.transferEnergyToAlly', 'Give energy to your ally:')}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                {[50, 100, 200].map((n) => (
                  <button
                    key={n}
                    type="button"
                    disabled={transferEnergyLoading || energyAmount < n}
                    onClick={() => handleTransferEnergy(n)}
                    style={{
                      padding: '8px 14px',
                      backgroundColor: (transferEnergyLoading || energyAmount < n) ? '#444' : 'rgba(78, 205, 196, 0.25)',
                      border: `1px solid ${energyAmount >= n ? 'rgba(78, 205, 196, 0.6)' : '#555'}`,
                      borderRadius: 8,
                      color: energyAmount >= n ? '#4ECDC4' : '#888',
                      cursor: (transferEnergyLoading || energyAmount < n) ? 'not-allowed' : 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    {n}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={transferEnergyLoading || energyAmount <= 0}
                  onClick={() => handleTransferEnergy('all')}
                  style={{
                    padding: '8px 14px',
                    backgroundColor: (transferEnergyLoading || energyAmount <= 0) ? '#444' : 'rgba(78, 205, 196, 0.25)',
                    border: `1px solid ${energyAmount > 0 ? 'rgba(78, 205, 196, 0.6)' : '#555'}`,
                    borderRadius: 8,
                    color: energyAmount > 0 ? '#4ECDC4' : '#888',
                    cursor: (transferEnergyLoading || energyAmount <= 0) ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  All
                </button>
              </div>
              <button
                type="button"
                onClick={() => setTransferEnergyModalOpen(false)}
                style={{ padding: '8px 16px', background: '#555', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', width: '100%' }}
              >
                Cancel
              </button>
            </div>
          </div>,
          document.body
        )}
        {/* Placement ghost: bigger when lower; far-away effect = more transparent + blur when higher */}
        {buildingForPlacement && (() => {
          const ghostYNorm = Math.max(0, Math.min(1, placementMouse.y / h));
          const ghostOpacity = 0.6 + 0.4 * ghostYNorm;
          const ghostBlur = (1 - ghostYNorm) * 1;
          return (
            <div
              style={{
                position: 'fixed',
                left: placementMouse.x,
                top: placementMouse.y,
                width: placementGhostSize,
                height: placementGhostSize,
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                zIndex: 2500,
                opacity: ghostOpacity,
                filter: ghostBlur > 0 ? `blur(${ghostBlur}px)` : 'none'
              }}
            >
              <img
                src={buildingForPlacement.image}
                alt={buildingForPlacement.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  ...(buildingForPlacement.id === 'hospital' ? { transform: 'scale(0.9)' } : (buildingForPlacement.id === 'powerplant' || buildingForPlacement.id === 'powerplant_upgraded') ? { transform: 'scale(0.8)' } : {})
                }}
              />
            </div>
          );
        })()}
        {/* Placed buildings - far-away effect (opacity + blur by Y); Destroy in lower half */}
        {placedBuildings.map((item, idx) => {
          const itemYNorm = Math.max(0, Math.min(1, item.y / h));
          const itemOpacity = 0.6 + 0.4 * itemYNorm;
          const itemBlur = (1 - itemYNorm) * 1;
          return (
          <div
            data-hq-placement
            key={item.placementId != null ? item.placementId : `placement-${idx}`}
            role="button"
            tabIndex={buildingForPlacement ? -1 : 0}
            onClick={(e) => {
              e.stopPropagation();
              if (item.placementId != null) {
                const soundPath = item.building?.id && BUILDING_CLICK_SOUNDS[item.building.id];
                if (soundPath) playSoundEffect(soundPath);
                setSelectedPlacementId((prev) => (prev === item.placementId ? null : item.placementId));
              }
            }}
            style={{
              position: 'fixed',
              left: item.x,
              top: item.y,
              width: item.size ?? 432,
              height: item.size ?? 432,
              transform: 'translate(-50%, -50%)',
              pointerEvents: buildingForPlacement ? 'none' : 'auto',
              cursor: buildingForPlacement ? 'none' : 'pointer',
              zIndex: selectedPlacementId === item.placementId ? 150 : 100,
              borderRadius: 4,
              opacity: itemOpacity,
              filter: itemBlur > 0 ? `blur(${itemBlur}px)` : 'none',
              display: (typeof item.currentProduced === 'number' || item.last_ration_tick_at != null || BUILDINGS_WITH_PRODUCER_UI.includes(item.building?.id)) ? 'flex' : 'block',
              flexDirection: (typeof item.currentProduced === 'number' || item.last_ration_tick_at != null || BUILDINGS_WITH_PRODUCER_UI.includes(item.building?.id)) ? 'column' : undefined,
              overflow: (typeof item.currentProduced === 'number' || item.last_ration_tick_at != null || BUILDINGS_WITH_PRODUCER_UI.includes(item.building?.id)) ? 'visible' : 'hidden'
            }}
          >
            <div
              style={{
                flex: (typeof item.currentProduced === 'number' || item.last_ration_tick_at != null || BUILDINGS_WITH_PRODUCER_UI.includes(item.building?.id)) ? 0 : undefined,
                flexShrink: 0,
                width: '100%',
                height: (typeof item.currentProduced === 'number' || item.last_ration_tick_at != null || BUILDINGS_WITH_PRODUCER_UI.includes(item.building?.id)) ? undefined : '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: (typeof item.currentProduced === 'number' || item.last_ration_tick_at != null || BUILDINGS_WITH_PRODUCER_UI.includes(item.building?.id)) ? 'flex-start' : 'center',
                pointerEvents: 'none'
              }}
            >
              <img
                src={item.building.image}
                alt={item.building.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  pointerEvents: 'none',
                  display: 'block',
                  ...(item.building?.id === 'hospital' ? { transform: 'scale(0.9)' } : (item.building?.id === 'powerplant' || item.building?.id === 'powerplant_upgraded') ? { transform: 'scale(0.8)' } : {})
                }}
                onLoad={(e) => {
                  const img = e.target;
                  if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                    const ratio = img.naturalWidth / img.naturalHeight;
                    setBuildingImageAspectRatio((prev) => (prev[item.building.image] === ratio ? prev : { ...prev, [item.building.image]: ratio }));
                  }
                }}
              />
            </div>
            {/* Vertical progress bar: right edge of image, inside, green cylinder style (like energy bar) */}
            {(typeof item.currentProduced === 'number' || item.last_ration_tick_at != null || BUILDINGS_WITH_PRODUCER_UI.includes(item.building?.id)) && (() => {
              const size = item.size ?? 432;
              const ratio = buildingImageAspectRatio[item.building.image] ?? 1;
              const imageHeight = size / ratio;
              const barHeight = Math.max(24, Math.min(120, Math.round(imageHeight * 0.75)));
              const rightInset = Math.max(4, Math.round(size * 0.02));
              const barWidth = Math.max(12, Math.min(20, Math.round(size * 0.06)));
              let fillPercent = 0;
              let label = '';
              const isBarracks = item.building?.id === 'barracks' || item.building?.id === 'barracks_upgraded';
              if (isBarracks) {
                fillPercent = Math.min(100, (item.barracksProgress ?? 0) * 100);
                label = `${Math.round(fillPercent)}%`;
              } else {
                const BARRACKS_STYLE_CFG = [
                  { ids: ['armory', 'armory_upgraded'], progressKey: 'armoryProgress' },
                  { ids: ['drone_hangar', 'drone_hangar_upgraded'], progressKey: 'droneHangarProgress' },
                  { ids: ['communications_hub', 'communications_hub_upgraded'], progressKey: 'comhubProgress' }
                ];
                const cfg = BARRACKS_STYLE_CFG.find((c) => c.ids.includes(item.building?.id));
                if (cfg) {
                  fillPercent = Math.min(100, (item[cfg.progressKey] ?? 0) * 100);
                  label = `${Math.round(fillPercent)}%`;
                } else {
                  const displayed = getDisplayedProduced(item);
                  const maxProduced = item.maxProduced ?? 20;
                  fillPercent = maxProduced > 0 ? (displayed / maxProduced) * 100 : 0;
                  label = item.building_complete ? 'Complete' : `${displayed}/${maxProduced}`;
                }
              }
              return (
                <div
                  key="bar"
                  className="war-effort-producer-progress-bar"
                  style={{
                    position: 'absolute',
                    right: rightInset,
                    top: (imageHeight - barHeight) / 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    pointerEvents: 'none'
                  }}
                >
                  <span style={{ fontSize: Math.max(8, Math.min(10, Math.round(size * 0.025))), color: '#6b8e23', fontWeight: 'bold', marginBottom: 2 }}>{label}</span>
                  <div
                    style={{
                      width: barWidth,
                      height: barHeight,
                      backgroundColor: 'transparent',
                      border: '1px solid rgba(107, 142, 35, 0.5)',
                      borderRadius: Math.min(10, Math.round(barWidth / 2)),
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end'
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        height: `${fillPercent}%`,
                        minHeight: fillPercent > 0 ? 2 : 0,
                        backgroundColor: fillPercent >= 100 ? '#4a7c2c' : '#6b8e23',
                        borderRadius: `0 0 ${Math.min(10, Math.round(barWidth / 2))}px ${Math.min(10, Math.round(barWidth / 2))}px`,
                        transition: 'height 0.3s ease'
                      }}
                    />
                  </div>
                </div>
              );
            })()}
            {/* Upgrade/Destroy buttons rendered via portal - centered on building, full size, not clipped by building overflow */}
          </div>
          );
        })}
        {/* Upgrade/Destroy buttons rendered via portal - fixed size, centered on building, not clipped by building overflow */}
        {typeof document !== 'undefined' && (() => {
          const sel = placedBuildings.find((p) => selectedPlacementId === p.placementId && p.placementId != null);
          if (!sel) return null;
          const item = sel;
          const isCompletionBuilding = COMPLETION_BUILDING_IDS.includes(item.building?.id);
          const hasProducerUI = !isCompletionBuilding && (typeof item.currentProduced === 'number' || item.last_ration_tick_at != null || BUILDINGS_WITH_PRODUCER_UI.includes(item.building?.id));
          const renderCollectOrComplete = () => {
            if (isCompletionBuilding) {
              return null;
            }
            const isBarracks = item.building?.id === 'barracks' || item.building?.id === 'barracks_upgraded';
            if (isBarracks) {
              const hasPending = item.barracks_pending_order_ids && item.barracks_pending_order_ids.length > 0;
              const ready = Boolean(item.barracksReady) && !hasPending;
              return (
                <button type="button" disabled={!ready} title={ready ? 'Click to complete' : 'Waiting...'}
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (item.placementId == null || !missionData?.id) return;
                    try {
                      const res = await fetch(`${API_URL}/war-effort/mission/${missionData.id}/hq-placements/${item.placementId}/complete-barracks`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                      const data = await res.json().catch(() => ({}));
                      if (!res.ok) { setError(data.error || 'Failed to complete barracks'); return; }
                      if (data.orders?.length > 0) {
                        setBarracksOrdersModal({ show: true, placementId: item.placementId, buildingId: 'barracks', orders: data.orders, loading: false });
                        setPlacedBuildings((prev) => prev.map((p) => p.placementId === item.placementId ? { ...p, barracks_pending_order_ids: data.orders.map((o) => o.id) } : p));
                        setError(null);
                      } else { setError('No orders available.'); setPlacementRefreshTrigger((t) => t + 1); }
                    } catch (err) { setError(err.message || 'Failed to complete barracks'); }
                  }}
                  style={{ padding: '8px 16px', backgroundColor: ready ? '#4a7c2c' : '#555', color: '#fff', border: '2px solid rgba(255,255,255,0.3)', borderRadius: 6, cursor: ready ? 'pointer' : 'not-allowed', fontWeight: 'bold', fontSize: 14 }}
                >Complete</button>
              );
            }
            const BARRACKS_STYLE_CFG = [
              { ids: ['armory', 'armory_upgraded'], pendingKey: 'armory_pending_order_ids', completeEndpoint: 'complete-armory', readyKey: 'armoryReady' },
              { ids: ['drone_hangar', 'drone_hangar_upgraded'], pendingKey: 'drone_hangar_pending_order_ids', completeEndpoint: 'complete-drone-hangar', readyKey: 'droneHangarReady' },
              { ids: ['communications_hub', 'communications_hub_upgraded'], pendingKey: 'comhub_pending_order_ids', completeEndpoint: 'complete-comhub', readyKey: 'comhubReady' }
            ];
            const cfg = BARRACKS_STYLE_CFG.find((c) => c.ids.includes(item.building?.id));
            if (cfg) {
              const hasPending = (item[cfg.pendingKey] && item[cfg.pendingKey].length > 0);
              const isReady = Boolean(item[cfg.readyKey]) && !hasPending;
              return (
                <button type="button" disabled={!isReady}
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (item.placementId == null || !missionData?.id) return;
                    try {
                      const res = await fetch(`${API_URL}/war-effort/mission/${missionData.id}/hq-placements/${item.placementId}/${cfg.completeEndpoint}`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                      const data = await res.json().catch(() => ({}));
                      if (!res.ok) { setError(data.error || 'Failed'); return; }
                      if (data.orders?.length > 0) {
                        setBarracksOrdersModalSelectedIds([]);
                        setBarracksOrdersModal({ show: true, placementId: item.placementId, buildingId: item.building?.id || cfg.ids[0], orders: data.orders, loading: false });
                        const pu = {}; pu[cfg.pendingKey] = data.orders.map((o) => o.id);
                        setPlacedBuildings((prev) => prev.map((p) => p.placementId === item.placementId ? { ...p, ...pu } : p));
                        setError(null);
                      } else { setError('No orders available.'); setPlacementRefreshTrigger((t) => t + 1); }
                    } catch (err) { setError(err.message); }
                  }}
                  style={{ padding: '8px 16px', backgroundColor: isReady ? '#4a7c2c' : '#555', color: '#fff', border: '2px solid rgba(255,255,255,0.3)', borderRadius: 6, cursor: isReady ? 'pointer' : 'not-allowed', fontWeight: 'bold', fontSize: 14 }}
                >Complete</button>
              );
            }
            const displayed = getDisplayedProduced(item);
            return (
              <button type="button" disabled={displayed <= 0}
                onClick={async (e) => {
                  e.stopPropagation();
                  if (item.placementId == null || !missionData?.id) return;
                  try {
                    const res = await fetch(`${API_URL}/war-effort/mission/${missionData.id}/hq-placements/${item.placementId}/collect-resource`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                    if (!res.ok) { const errData = await res.json().catch(() => ({})); setError(errData.error || 'Failed'); return; }
                    const data = await res.json();
                    setPlacedBuildings((prev) => prev.map((p) => p.placementId === item.placementId ? { ...p, currentProduced: 0, maxProduced: data.produced_max ?? 20, last_ration_tick_at: new Date().toISOString() } : p));
                    setError(null);
                    setCollectSparkPlacementId(item.placementId);
                    setCollectSparkAmount(data.collected ?? 0);
                    setTimeout(() => { setCollectSparkPlacementId(null); setCollectSparkAmount(null); }, 900);
                  } catch (err) { setError(err.message); }
                }}
                style={{ padding: '8px 16px', backgroundColor: displayed > 0 ? '#4a7c2c' : '#555', color: '#fff', border: '2px solid rgba(255,255,255,0.3)', borderRadius: 6, cursor: displayed > 0 ? 'pointer' : 'not-allowed', fontWeight: 'bold', fontSize: 14 }}
              >Collect</button>
            );
          };
          return createPortal(
            <div
              role="group"
              aria-label="Building actions"
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'fixed',
                left: item.x,
                top: item.y,
                transform: 'translate(-50%, -50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                pointerEvents: 'auto',
                zIndex: 1000,
                isolation: 'isolate'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {BUILDING_UPGRADES[item.building?.id] && (() => {
                  const upgrade = BUILDING_UPGRADES[item.building.id];
                  const canAfford = energyAmount >= upgrade.cost;
                  return (
                    <button
                      type="button"
                      disabled={!canAfford}
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!canAfford || item.placementId == null || !missionData?.id) return;
                        try {
                          const url = item.building.id === 'powerplant'
                            ? `${API_URL}/war-effort/mission/${missionData.id}/hq-placements/${item.placementId}/upgrade-powerplant`
                            : `${API_URL}/war-effort/mission/${missionData.id}/hq-placements/${item.placementId}/upgrade-building`;
                          const res = await fetch(url, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
                            body: item.building.id === 'powerplant' ? undefined : JSON.stringify({ building_id: item.building.id })
                          });
                          const data = await res.json().catch(() => ({}));
                          if (!res.ok) { setError(data.error || 'Upgrade failed'); return; }
                          playSoundEffect('/sounds/build.mp3');
                          const upgradedBuilding = HQ_BUILDINGS.find((b) => b.id === upgrade.upgradedId);
                          setPlacedBuildings((prev) => prev.map((p) => p.placementId === item.placementId ? { ...p, building: upgradedBuilding } : p));
                          if (typeof data.remaining_energy === 'number' && playerResource) {
                            setPlayerResource((prev) => prev ? { ...prev, amount: data.remaining_energy } : null);
                          }
                          setSelectedPlacementId(null);
                          refetchMission();
                        } catch (err) { setError('Upgrade failed'); }
                      }}
                      style={{ padding: '8px 16px', backgroundColor: canAfford ? '#27ae60' : '#555', color: '#fff', border: '2px solid rgba(255,255,255,0.3)', borderRadius: 6, cursor: canAfford ? 'pointer' : 'not-allowed', fontWeight: 'bold', fontSize: 14 }}
                    >
                      Upgrade ({upgrade.cost})
                    </button>
                  );
                })()}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    playSoundEffect('/sounds/demolish.mp3');
                    if (item.placementId != null && missionData?.id) {
                      fetch(`${API_URL}/war-effort/mission/${missionData.id}/hq-placements/${item.placementId}`, {
                        method: 'DELETE',
                        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                      })
                        .then(() => refetchMission())
                        .catch((err) => console.error('Error deleting HQ placement:', err));
                    }
                    setPlacedBuildings((prev) => prev.filter((p) => p.placementId !== item.placementId));
                    setSelectedPlacementId(null);
                  }}
                  style={{ padding: '8px 16px', backgroundColor: '#c0392b', color: '#fff', border: '2px solid rgba(255,255,255,0.3)', borderRadius: 6, cursor: 'pointer', fontWeight: 'bold', fontSize: 14 }}
                >
                  Destroy
                </button>
              </div>
              {hasProducerUI && (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  {renderCollectOrComplete()}
                </div>
              )}
            </div>,
            document.body
          );
        })()}
        <div style={{ position: 'fixed', inset: 0, zIndex: 500, pointerEvents: 'none' }}>
          {collectSparkPlacementId != null && collectSparkAmount != null && (() => {
            const item = placedBuildings.find((p) => p.placementId === collectSparkPlacementId);
            if (!item) return null;
            return (
              <div
                className="war-effort-collect-float"
                style={{
                  position: 'fixed',
                  left: item.x,
                  top: item.y,
                  transform: 'translate(-50%, -50%)'
                }}
                aria-hidden
              >
                +{collectSparkAmount}
              </div>
            );
          })()}
          {trainingResult != null && (() => {
            const item = placedBuildings.find((p) => p.placementId === trainingResult.placementId);
            if (!item) return null;
            const msg = trainingResult.outcome === 'nothing' || !trainingResult.order
              ? 'Nothing'
              : `${trainingResult.order.rarity}: ${trainingResult.order.name}`;
            return (
              <div
                className="war-effort-collect-float"
                style={{
                  position: 'fixed',
                  left: item.x,
                  top: item.y,
                  transform: 'translate(-50%, -50%)',
                  fontSize: 12,
                  color: trainingResult.order ? '#9acd32' : '#aaa'
                }}
                aria-hidden
              >
                {msg}
              </div>
            );
          })()}
          {powerplantFlashPlacements.map((p, idx) => (
            <div
              key={`powerplant-${idx}-${p.x}-${p.y}`}
              className="war-effort-collect-float war-effort-collect-float--energy"
              style={{
                position: 'fixed',
                left: p.x,
                top: p.y,
                transform: 'translate(-50%, -50%)'
              }}
              aria-hidden
            >
              +{p.amount}
            </div>
          ))}
          {barracksOrdersModal.show && barracksOrdersModal.orders.length > 0 && (() => {
            // Order-generating buildings (Barracks, Armory, Drone Hangar, ComHub) always allow 1 reward only; Training Grounds applies only to conquest/subversion
            const ordersToKeep = 1;
            const isMultiSelect = ordersToKeep > 1;
            const selectedIds = barracksOrdersModalSelectedIds;
            const toggleSelection = (id) => {
              if (selectedIds.includes(id)) {
                setBarracksOrdersModalSelectedIds((prev) => prev.filter((x) => x !== id));
              } else if (selectedIds.length < ordersToKeep) {
                setBarracksOrdersModalSelectedIds((prev) => [...prev, id]);
              }
            };
            const submitSelection = async (orderIdsToSubmit) => {
              if (barracksOrdersModal.loading || !missionData?.id || !barracksOrdersModal.placementId) return;
              const bid = barracksOrdersModal.buildingId;
              const selectEndpoint = (bid === 'barracks' || bid === 'barracks_upgraded') ? 'select-barracks-order'
                : (bid === 'armory' || bid === 'armory_upgraded') ? 'select-armory-order'
                : (bid === 'drone_hangar' || bid === 'drone_hangar_upgraded') ? 'select-drone-hangar-order'
                : (bid === 'communications_hub' || bid === 'communications_hub_upgraded') ? 'select-comhub-order'
                : 'select-barracks-order';
              setBarracksOrdersModal((prev) => ({ ...prev, loading: true }));
              try {
                const body = orderIdsToSubmit.length === 1 ? { orderId: orderIdsToSubmit[0] } : { orderIds: orderIdsToSubmit };
                const res = await fetch(
                  `${API_URL}/war-effort/mission/${missionData.id}/hq-placements/${barracksOrdersModal.placementId}/${selectEndpoint}`,
                  {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
                    body: JSON.stringify(body)
                  }
                );
                if (!res.ok) {
                  const errData = await res.json().catch(() => ({}));
                  setError(errData.error || 'Failed to select order');
                  setBarracksOrdersModal({ show: false, placementId: null, buildingId: null, orders: [], loading: false });
                  return;
                }
                setBarracksOrdersModal({ show: false, placementId: null, buildingId: null, orders: [], loading: false });
                setBarracksOrdersModalSelectedIds([]);
                setError(null);
                setPlacementRefreshTrigger((t) => t + 1);
              } catch (err) {
                console.error('Error selecting order:', err);
                setError(err.message || 'Failed to select order');
                setBarracksOrdersModal({ show: false, placementId: null, buildingId: null, orders: [], loading: false });
                setBarracksOrdersModalSelectedIds([]);
              }
            };
            const handleCardClick = (ord) => {
              if (isMultiSelect) {
                toggleSelection(ord.id);
              } else {
                submitSelection([ord.id]);
              }
            };
            const canConfirm = selectedIds.length >= 1 && selectedIds.length <= ordersToKeep;
            return (
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  zIndex: 6000,
                  background: 'rgba(0,0,0,0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'auto'
                }}
                onClick={(e) => { if (e.target === e.currentTarget) { setBarracksOrdersModal({ show: false, placementId: null, buildingId: null, orders: [], loading: false }); setBarracksOrdersModalSelectedIds([]); } }}
              >
                <div
                  style={{
                    background: 'transparent',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: 24
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 style={{ margin: '0 0 8px 0', color: '#fff' }}>
                    {isMultiSelect ? `Choose up to ${ordersToKeep} orders to add to your attack strategy` : 'Choose an order to add to your attack strategy'}
                  </h3>
                  <p style={{ fontSize: 12, color: '#aaa', marginBottom: 20 }}>These orders will be added for this mission only.</p>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'nowrap',
                      gap: 40,
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    {barracksOrdersModal.orders.map((ord) => {
                      const enriched = barracksEnrichedOrders.find((e) => e && e.id === ord.id);
                      const isSelected = selectedIds.includes(ord.id);
                      return (
                        <div
                          key={ord.id}
                          style={{
                            width: '390px',
                            height: '390px',
                            flexShrink: 0,
                            cursor: barracksOrdersModal.loading ? 'not-allowed' : 'pointer',
                            position: 'relative',
                            overflow: 'visible',
                            outline: isMultiSelect && isSelected ? '4px solid #4ECDC4' : 'none',
                            borderRadius: 8
                          }}
                          onClick={() => handleCardClick(ord)}
                        >
                          <div
                            className={`reward-order-container rarity-${(ord.rarity || 'common').toLowerCase()}`}
                            style={{
                              width: '390px',
                              height: '390px',
                              position: 'relative',
                              margin: 0,
                              padding: 0
                            }}
                          >
                            {enriched ? (
                              <OrderDesign
                                order={enriched}
                                minimal={false}
                                noMargin={true}
                                gameState={null}
                              />
                            ) : (
                              <div
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: 'transparent',
                                  color: '#aaa',
                                  fontSize: 14
                                }}
                              >
                                Loading…
                              </div>
                            )}
                            <OrderCardBubbles rarity={enriched?.rarity || ord.rarity || 'common'} />
                          </div>
                          {isMultiSelect && isSelected && (
                            <div style={{ position: 'absolute', top: 8, right: 8, background: '#4ECDC4', color: '#000', padding: '4px 8px', borderRadius: 4, fontWeight: 'bold' }}>
                              Selected
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {isMultiSelect && (
                    <div style={{ display: 'flex', gap: 16, marginTop: 20, alignItems: 'center' }}>
                      {ordersToKeep === 3 && (
                        <button
                          type="button"
                          onClick={() => submitSelection(barracksOrdersModal.orders.map((o) => o.id))}
                          disabled={barracksOrdersModal.loading}
                          style={{ padding: '10px 20px', background: '#4ECDC4', color: '#000', border: 'none', borderRadius: 8, cursor: barracksOrdersModal.loading ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}
                        >
                          Keep all 3
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => submitSelection(selectedIds)}
                        disabled={!canConfirm || barracksOrdersModal.loading}
                        style={{
                          padding: '10px 20px',
                          background: canConfirm ? '#4ECDC4' : '#555',
                          color: canConfirm ? '#000' : '#999',
                          border: 'none',
                          borderRadius: 8,
                          cursor: canConfirm && !barracksOrdersModal.loading ? 'pointer' : 'not-allowed',
                          fontWeight: 'bold'
                        }}
                      >
                        Confirm ({selectedIds.length}/{ordersToKeep})
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
        <div className="war-effort-bottom-buttons">
          <button
            type="button"
            className="war-effort-bottom-btn"
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              onBuildingPageChange?.(false);
            }}
            aria-label="World map"
          >
            <img src="/images/world_button.png" alt="" className="war-effort-bottom-btn__image" />
          </button>
          <button
            type="button"
            className="war-effort-bottom-btn"
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              setShowBuildMenu(true);
            }}
            aria-label="Build"
          >
            <img src="/images/build_button.png" alt="" className="war-effort-bottom-btn__image" />
          </button>
          <button
            type="button"
            className="war-effort-bottom-btn"
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              openTradingHubAfterMapRef.current = true;
              onBuildingPageChange?.(false);
            }}
            aria-label="Trading Hub"
          >
            <img src="/images/trading_button.png" alt="" className="war-effort-bottom-btn__image" />
          </button>
          <button
            type="button"
            className="war-effort-bottom-btn"
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              setAttackStrategyModal({ show: true, loading: true, strategy: null, deactivatedSlots: [] });
              const token = localStorage.getItem('token');
              if (!token || !missionData?.id) {
                setAttackStrategyModal({ show: true, loading: false, strategy: null, deactivatedSlots: [] });
                return;
              }
              fetch(`${API_URL}/war-effort/mission/${missionData.id}/attack-strategy`, {
                headers: { Authorization: `Bearer ${token}` }
              })
                .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Failed to fetch'))))
                .then((data) => setAttackStrategyModal({ show: true, loading: false, strategy: data.strategy, deactivatedSlots: data.deactivatedSlots || [] }))
                .catch(() => setAttackStrategyModal({ show: true, loading: false, strategy: null, deactivatedSlots: [] }));
            }}
            aria-label="Attack strategy"
          >
            <img src="/images/strategy_button.png" alt="" className="war-effort-bottom-btn__image" />
          </button>
        </div>
        {attackStrategyModal.show && typeof document !== 'undefined' && createPortal(
          <div
            className="war-effort-attack-strategy-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) setAttackStrategyModal({ show: false, loading: false, strategy: null, deactivatedSlots: [] }); setAttackStrategyTooltipRect(null); }}
          >
            <div
              className="war-effort-attack-strategy-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="war-effort-attack-strategy-sidebar war-effort-attack-strategy-sidebar--hq"
              >
                <div style={{ flexShrink: 0, marginBottom: 12 }}>
                  <h3 style={{ margin: '0 0 8px 0', color: '#fff' }}>Attack strategy</h3>
                  {attackStrategyModal.loading ? (
                    <p style={{ color: '#aaa' }}>Loading...</p>
                  ) : attackStrategyModal.strategy?.orders?.length > 0 ? (
                    (() => {
                      const deactivatedSet = new Set(attackStrategyModal.deactivatedSlots || []);
                      const slots = attackStrategyModal.strategy.orders.flatMap((order, i) =>
                        Array.from({ length: order.count || 1 }, (_, j) => ({
                          order,
                          key: `${order.id ?? order.order_id ?? i}-${j}`,
                          orderId: order.id ?? order.order_id,
                          occurrence: j
                        }))
                      );
                      const total = slots.length;
                      const activeCount = slots.filter((s) => !deactivatedSet.has(`${s.orderId}:${s.occurrence}`)).length;
                      return (
                        <>
                          <div style={{ fontWeight: 'bold', color: '#aaa' }}>
                            Active: {activeCount} / {total} orders (need 30+ for battle)
                          </div>
                          {activeCount < 30 && (
                            <div style={{ marginTop: 8, padding: 8, background: 'rgba(255,100,0,0.2)', borderRadius: 4, color: '#ffa', fontSize: '0.85em' }}>
                              Activate more orders to attack. Click an order to toggle.
                            </div>
                          )}
                        </>
                      );
                    })()
                  ) : (
                    <p style={{ color: '#aaa' }}>No orders in attack strategy.</p>
                  )}
                </div>
                {attackStrategyModal.loading ? null : attackStrategyModal.strategy?.orders?.length > 0 ? (
                  (() => {
                    const deactivatedSet = new Set(attackStrategyModal.deactivatedSlots || []);
                    const slots = attackStrategyModal.strategy.orders.flatMap((order, i) =>
                      Array.from({ length: order.count || 1 }, (_, j) => ({
                        order,
                        key: `${order.id ?? order.order_id ?? i}-${j}`,
                        orderId: order.id ?? order.order_id,
                        occurrence: j
                      }))
                    );
                    return (
                      <div className="war-effort-attack-strategy-list">
                        {slots.map(({ order, key, orderId, occurrence }) => (
                          <AttackStrategySlotRow
                            key={key}
                            order={order}
                            orderId={orderId}
                            occurrence={occurrence}
                            isDeactivated={deactivatedSet.has(`${orderId}:${occurrence}`)}
                            costLabel={t('strategy.cost')}
                            onToggle={handleAttackStrategyToggleSlot}
                            setTooltipRect={setAttackStrategyTooltipRect}
                          />
                        ))}
                      </div>
                  );
                })()
              ) : null}
              <button
                type="button"
                onClick={() => { setAttackStrategyModal({ show: false, loading: false, strategy: null, deactivatedSlots: [] }); setAttackStrategyTooltipRect(null); }}
                style={{ flexShrink: 0, marginTop: 0, padding: '8px 16px', background: '#555', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
              >
                Close
              </button>
            </div>
            {!attackStrategyModal.loading && attackStrategyModal.strategy?.orders?.length > 0 && (
              <AttackStrategyStatisticsPanel
                orders={getActiveAttackStrategyOrders(
                  attackStrategyModal.strategy.orders,
                  attackStrategyModal.deactivatedSlots
                )}
                layout="column"
              />
            )}
          </div>
          </div>
        , document.body)}
        {attackStrategyTooltipRect && attackStrategyModal.strategy && createPortal(
          <div
            style={{
              position: 'fixed',
              left: `${attackStrategyTooltipRect.left}px`,
              ...(attackStrategyTooltipRect.showAbove
                ? { bottom: `${typeof window !== 'undefined' ? window.innerHeight - attackStrategyTooltipRect.top + 5 : 0}px` }
                : { top: `${attackStrategyTooltipRect.bottom + 5}px` }),
              zIndex: 99999,
              pointerEvents: 'none',
              transform: 'scale(0.8)',
              transformOrigin: attackStrategyTooltipRect.showAbove ? 'bottom left' : 'top left',
              maxHeight: 400,
              overflow: 'auto'
            }}
          >
            <OrderDesign
              order={attackStrategyTooltipRect.order}
              count={0}
              minimal={false}
              noMargin={true}
              lazyLoad={false}
              isOOR={false}
            />
          </div>,
          document.body
        )}
        {showBuildMenu && typeof document !== 'undefined' && createPortal(
          <div
            className="war-effort-build-menu-overlay"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 100000,
              backgroundColor: 'rgba(0, 0, 0, 0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'auto'
            }}
            onClick={(e) => e.target === e.currentTarget && setShowBuildMenu(false)}
          >
            <div
              className="war-effort-build-menu-dialog"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-label={t('warEffort.buildMenuTitle', 'Build headquarters')}
            >
              <button
                type="button"
                className="war-effort-build-menu-close"
                aria-label={t('warEffort.tradingHub.close', 'Close')}
                onClick={() => { playSoundEffect('/sounds/click.mp3'); setShowBuildMenu(false); }}
              >
                ✕
              </button>
              <div className="war-effort-build-menu-dialog__tabs" role="tablist" aria-label={t('warEffort.buildMenuTabsLabel', 'Building categories')}>
                {BUILD_MENU_TAB_KEYS.map((tabKey) => (
                  <button
                    key={tabKey}
                    type="button"
                    role="tab"
                    id={`war-effort-build-tab-${tabKey}`}
                    aria-selected={buildMenuTab === tabKey}
                    aria-controls="war-effort-build-tab-panel"
                    className={`war-effort-build-menu-tab${buildMenuTab === tabKey ? ' war-effort-build-menu-tab--active' : ''}`}
                    onClick={() => {
                      playSoundEffect('/sounds/click.mp3');
                      setBuildMenuTab(tabKey);
                    }}
                  >
                    <WarEffortBuildMenuTabIcon tabKey={tabKey} />
                    <span className="war-effort-build-menu-tab__label">{t(`warEffort.buildMenuTabs.${tabKey}`)}</span>
                  </button>
                ))}
              </div>
              <div
                id="war-effort-build-tab-panel"
                className="war-effort-build-menu-dialog__cards"
                role="tabpanel"
                aria-labelledby={`war-effort-build-tab-${buildMenuTab}`}
              >
                {buildMenuBuildingsForTab.map((building) => {
                  const isUnlocked = unlockedBuildingIds.includes(building.id);
                  const effectiveCost = getEffectiveBuildingCost(building.cost);
                  const canAfford = energyAmount >= effectiveCost;
                  const { groupIds, cap } = getBuildingCapInfo(building.id);
                  const builtCount = placedBuildings.filter((p) => groupIds.includes(p.building.id)).length;
                  const atCapacity = builtCount >= cap;
                  const canSelect = isUnlocked && canAfford && !atCapacity;
                  return (
                    <div
                      key={building.id}
                      className={`war-effort-build-menu-card ${canSelect ? 'war-effort-build-menu-card--selectable' : ''}`}
                      role={canSelect ? 'button' : undefined}
                      title={!isUnlocked ? `${building.name} (Locked)` : atCapacity ? `${building.name} (max ${cap} reached)` : !canAfford ? `Not enough energy (need ${effectiveCost})` : building.name}
                      onClick={canSelect ? (e) => {
                        e.stopPropagation();
                        playSoundEffect('/sounds/click.mp3');
                        setPlacementMouse({ x: e.clientX, y: e.clientY });
                        setBuildingForPlacement(building);
                        setShowBuildMenu(false);
                      } : undefined}
                    >
                      {!isUnlocked && (
                        <span className="war-effort-build-menu-card__lock" aria-hidden>
                          🔒
                        </span>
                      )}
                      <img
                        src={building.image}
                        alt={building.name}
                        className={`war-effort-build-menu-card__image${building.id === 'hospital' ? ' war-effort-build-menu-card__image--hospital' : ''}`}
                      />
                      <h4 className="war-effort-build-menu-card__name">{building.name}</h4>
                      {building.description && (
                        <p className="war-effort-build-menu-card__description">{building.description}</p>
                      )}
                      <div className="war-effort-build-menu-card__meta">
                        <span className={canAfford ? 'war-effort-build-menu-card__cost' : 'war-effort-build-menu-card__cost--insufficient'}>
                          {effectiveCost} energy{hasInfrastructureSubsidy ? ` (55% off)` : buildingCostMultiplier < 1 ? ` (20% off)` : ''}
                        </span>
                        <span className="war-effort-build-menu-card__count">
                          {builtCount} / {cap} built
                        </span>
                      </div>
                      {isUnlocked && !canAfford && (
                        <span className="war-effort-build-menu-card__hint">Not enough energy</span>
                      )}
                      {isUnlocked && atCapacity && (
                        <span className="war-effort-build-menu-card__hint">Maximum reached</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
      </>
    );
  }

  return (
    <div className="war-effort-fortification" style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden' }}>
      {achievementUnlocked && (
        <AchievementUnlockedToast
          titleId={achievementUnlocked.titleId}
          titleName={achievementUnlocked.titleName}
          color={achievementUnlocked.color}
          achievementKey={achievementUnlocked.achievementKey}
          onClose={() => setAchievementUnlocked(null)}
        />
      )}
      {pendingToast && (
        <AchievementUnlockedToast
          titleId={pendingToast.titleId}
          titleName={pendingToast.titleName}
          color={pendingToast.color}
          achievementKey={pendingToast.achievementKey}
          onClose={dismissPendingToast}
        />
      )}
      {fortifiedCountryWarningOverlay}
      {/* Unified bottom buttons - world map: Build, Trading, Strategy */}
      <div className="war-effort-bottom-buttons">
        <button
          type="button"
          className="war-effort-bottom-btn"
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            setWorldMapResetTrigger((t) => t + 1);
          }}
          aria-label="World map"
        >
          <img src="/images/world_button.png" alt="" className="war-effort-bottom-btn__image" />
        </button>
        <button
          type="button"
          className="war-effort-bottom-btn"
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            openBuildMenuAfterBuildingPageRef.current = true;
            onBuildingPageChange?.(true);
          }}
          aria-label="Build"
        >
          <img src="/images/build_button.png" alt="" className="war-effort-bottom-btn__image" />
        </button>
        <button
          type="button"
          className="war-effort-bottom-btn"
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            setTradingHubOpen(true);
          }}
          aria-label="Trading Hub"
        >
          <img src="/images/trading_button.png" alt="" className="war-effort-bottom-btn__image" />
        </button>
        <button
          type="button"
          className="war-effort-bottom-btn"
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            setAttackStrategyModal({ show: true, loading: true, strategy: null, deactivatedSlots: [] });
            const token = localStorage.getItem('token');
            if (!token || !missionData?.id) {
              setAttackStrategyModal({ show: true, loading: false, strategy: null, deactivatedSlots: [] });
              return;
            }
            fetch(`${API_URL}/war-effort/mission/${missionData.id}/attack-strategy`, {
              headers: { Authorization: `Bearer ${token}` }
            })
              .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Failed to fetch'))))
              .then((data) => setAttackStrategyModal({ show: true, loading: false, strategy: data.strategy, deactivatedSlots: data.deactivatedSlots || [] }))
              .catch(() => setAttackStrategyModal({ show: true, loading: false, strategy: null, deactivatedSlots: [] }));
          }}
          aria-label="Attack strategy"
        >
          <img src="/images/strategy_button.png" alt="" className="war-effort-bottom-btn__image" />
        </button>
      </div>
      {attackStrategyModal.show && (
        <div
          className="war-effort-attack-strategy-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setAttackStrategyModal({ show: false, loading: false, strategy: null, deactivatedSlots: [] }); setAttackStrategyTooltipRect(null); }}
        >
          <div
            className="war-effort-attack-strategy-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="war-effort-attack-strategy-sidebar strategy-sidebar"
            >
              <h3 style={{ margin: '0 0 16px 0', color: '#fff' }}>Attack strategy</h3>
              {attackStrategyModal.loading ? (
                <p style={{ color: '#aaa' }}>Loading...</p>
              ) : attackStrategyModal.strategy?.orders?.length > 0 ? (
                (() => {
                  const deactivatedSet = new Set(attackStrategyModal.deactivatedSlots || []);
                  const slots = attackStrategyModal.strategy.orders.flatMap((order, i) =>
                    Array.from({ length: order.count || 1 }, (_, j) => ({
                      order,
                      key: `${order.id ?? order.order_id ?? i}-${j}`,
                      orderId: order.id ?? order.order_id,
                      occurrence: j
                    }))
                  );
                  const total = slots.length;
                  const activeCount = slots.filter((s) => !deactivatedSet.has(`${s.orderId}:${s.occurrence}`)).length;
                  return (
                    <>
                      <div style={{ marginBottom: 12, fontWeight: 'bold', color: '#aaa' }}>
                        Active: {activeCount} / {total} orders (need 30+ for battle)
                      </div>
                      {activeCount < 30 && (
                        <div style={{ marginBottom: 8, padding: 8, background: 'rgba(255,100,0,0.2)', borderRadius: 4, color: '#ffa', fontSize: '0.85em' }}>
                          Activate more orders to attack. Click an order to toggle.
                        </div>
                      )}
                      <div className="war-effort-attack-strategy-list">
                        {slots.map(({ order, key, orderId, occurrence }) => (
                          <AttackStrategySlotRow
                            key={key}
                            order={order}
                            orderId={orderId}
                            occurrence={occurrence}
                            isDeactivated={deactivatedSet.has(`${orderId}:${occurrence}`)}
                            costLabel={t('strategy.cost')}
                            onToggle={handleAttackStrategyToggleSlot}
                            setTooltipRect={setAttackStrategyTooltipRect}
                          />
                        ))}
                      </div>
                  </>
                );
              })()
            ) : (
              <p style={{ color: '#aaa' }}>No orders in attack strategy.</p>
            )}
              <button
                type="button"
                onClick={() => { setAttackStrategyModal({ show: false, loading: false, strategy: null, deactivatedSlots: [] }); setAttackStrategyTooltipRect(null); }}
                style={{ marginTop: 16, padding: '8px 16px', background: '#555', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
              >
                Close
              </button>
            </div>
            {!attackStrategyModal.loading && attackStrategyModal.strategy?.orders?.length > 0 && (
              <AttackStrategyStatisticsPanel
                orders={getActiveAttackStrategyOrders(
                  attackStrategyModal.strategy.orders,
                  attackStrategyModal.deactivatedSlots
                )}
                layout="column"
              />
            )}
          </div>
        </div>
      )}
      {attackStrategyTooltipRect && attackStrategyModal.strategy && createPortal(
        <div
          style={{
            position: 'fixed',
            left: `${attackStrategyTooltipRect.left}px`,
            ...(attackStrategyTooltipRect.showAbove
              ? { bottom: `${typeof window !== 'undefined' ? window.innerHeight - attackStrategyTooltipRect.top + 5 : 0}px` }
              : { top: `${attackStrategyTooltipRect.bottom + 5}px` }),
            zIndex: 99999,
            pointerEvents: 'none',
            transform: 'scale(0.8)',
            transformOrigin: attackStrategyTooltipRect.showAbove ? 'bottom left' : 'top left',
            maxHeight: 400,
            overflow: 'auto'
          }}
        >
          <OrderDesign
            order={attackStrategyTooltipRect.order}
            count={0}
            minimal={false}
            noMargin={true}
            lazyLoad={false}
            isOOR={false}
          />
        </div>,
        document.body
      )}
      {/* Energy: floating transparent cylinder, below header to avoid overlap with Friends/Settings/username */}
      <div
        className="war-effort-energy-bar war-effort-map-chrome--energy"
      >
        <span className="war-effort-map-chrome--energy-label" style={{ fontSize: '10px', color: '#4ECDC4', marginTop: '0', fontWeight: 'bold', letterSpacing: '0.5px' }}>Energy</span>
        <span className="war-effort-map-chrome--energy-cap" style={{ fontSize: '11px', color: '#aaa', marginTop: '4px', fontWeight: 'bold' }}>{energyCap}</span>
        <div
          className="war-effort-energy-cylinder war-effort-map-chrome--energy-cylinder"
        >
          <div
            style={{
              width: '100%',
              height: `${energyFillPercent}%`,
              minHeight: energyAmount > 0 ? '4px' : 0,
              backgroundColor: energyAmount > 0 ? '#4ECDC4' : 'transparent',
              borderRadius: '0 0 10px 10px',
              transition: 'height 0.3s ease'
            }}
          />
        </div>
        <span className="war-effort-map-chrome--energy-zero" style={{ fontSize: '11px', color: '#aaa', marginBottom: '8px', fontWeight: 'bold' }}>0</span>
        <span className="war-effort-map-chrome--energy-amount" style={{ fontSize: '12px', color: '#4ECDC4', fontWeight: 'bold', marginBottom: '0' }}>
          {energyAmount}
        </span>
      </div>
      {showTransferEnergy && (
        <div className="war-effort-map-chrome--transfer">
          <button
            type="button"
            className="war-effort-transfer-energy-btn"
            onClick={() => { playSoundEffect('/sounds/click.mp3'); setTransferEnergyModalOpen(true); }}
            style={{
              fontSize: '9px',
              padding: '4px 2px',
              cursor: 'pointer',
              backgroundColor: 'rgba(78, 205, 196, 0.25)',
              border: '1px solid rgba(78, 205, 196, 0.6)',
              borderRadius: '6px',
              color: '#4ECDC4',
              fontWeight: 'bold',
              whiteSpace: 'normal',
              lineHeight: 1.2,
              width: '100%'
            }}
          >
            {t('warEffort.transferEnergy', 'Transfer Energy')}
          </button>
        </div>
      )}
      {transferEnergyModalOpen && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="transfer-energy-title-map"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.7)',
            pointerEvents: 'auto'
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setTransferEnergyModalOpen(false); }}
        >
          <div
            style={{
              backgroundColor: '#2a2a2a',
              border: '2px solid rgba(78, 205, 196, 0.6)',
              borderRadius: 12,
              padding: 20,
              minWidth: 220,
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="transfer-energy-title-map" style={{ margin: '0 0 16px', color: '#4ECDC4', fontSize: 16 }}>{t('warEffort.transferEnergy', 'Transfer Energy')}</h3>
            <p style={{ margin: '0 0 12px', color: '#aaa', fontSize: 12 }}>{t('warEffort.transferEnergyToAlly', 'Give energy to your ally:')}</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
              {[50, 100, 200].map((n) => (
                <button
                  key={n}
                  type="button"
                  disabled={transferEnergyLoading || energyAmount < n}
                  onClick={() => handleTransferEnergy(n)}
                  style={{
                    padding: '8px 14px',
                    backgroundColor: (transferEnergyLoading || energyAmount < n) ? '#444' : 'rgba(78, 205, 196, 0.25)',
                    border: `1px solid ${energyAmount >= n ? 'rgba(78, 205, 196, 0.6)' : '#555'}`,
                    borderRadius: 8,
                    color: energyAmount >= n ? '#4ECDC4' : '#888',
                    cursor: (transferEnergyLoading || energyAmount < n) ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  {n}
                </button>
              ))}
              <button
                type="button"
                disabled={transferEnergyLoading || energyAmount <= 0}
                onClick={() => handleTransferEnergy('all')}
                style={{
                  padding: '8px 14px',
                  backgroundColor: (transferEnergyLoading || energyAmount <= 0) ? '#444' : 'rgba(78, 205, 196, 0.25)',
                  border: `1px solid ${energyAmount > 0 ? 'rgba(78, 205, 196, 0.6)' : '#555'}`,
                  borderRadius: 8,
                  color: energyAmount > 0 ? '#4ECDC4' : '#888',
                  cursor: (transferEnergyLoading || energyAmount <= 0) ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold'
                }}
              >
                All
              </button>
            </div>
            <button
              type="button"
              onClick={() => setTransferEnergyModalOpen(false)}
              style={{ padding: '8px 16px', background: '#555', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', width: '100%' }}
            >
              Cancel
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* World map - full screen, no borders or background content */}
      <div className="world-map-container" style={{ border: 'none', margin: 0, padding: 0, position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 1 }}>
        {/* Engineers count: below back button (upper right), format Engineers: X/10 */}
        {missionData?.id != null && (
          <div className="war-effort-map-chrome--engineers">
            {t('warEffort.engineersCount', { current: engineersForDisplay != null ? engineersForDisplay : '—' })}
          </div>
        )}
        <WorldMap
          resetViewTrigger={worldMapResetTrigger}
          cellOwnersMap={cellOwnersMap}
          getFactionBackground={getFactionBackground}
          factionsInGame={factionsInGame}
          onCountryClick={handleCountryClick}
          onCountryHover={handleCountryHover}
          onCountryLeave={handleCountryLeave}
          highlightedCells={controlledCountries}
          fortifiedCells={fortifiedCellsForMap}
          commandCenterFortifiedCells={commandCenterBoostCellIds}
          viewerCommandCenterStripeBaseColor={viewerCommandCenterStripeColors?.base}
          viewerCommandCenterStripeDarkerColor={viewerCommandCenterStripeColors?.darker}
          opponentFortifiedCells={opponentFortifiedCellsForMap}
          opponentCommandCenterFortifiedCells={opponentCommandCenterFortifiedCellsForMap}
          opponentCommandCenterStripeBaseColor={opponentCommandCenterStripeColors?.base}
          opponentCommandCenterStripeDarkerColor={opponentCommandCenterStripeColors?.darker}
          opponentCommandCenterStripeByCellId={opponentCommandCenterStripeByCellId}
          selectedCell={selectedCountry}
          missionSubmissionHoverCellId={missionSubmissionHoverCellId}
          attackArrowFromCellId={attackFromCountry?.cellId ?? null}
          attackArrowToCellId={validAttackTargetCellId}
          devModeHighlightCells={devShowFortifications ? devCommandCenterCells.allCells : []}
          neutralFortificationCountMap={neutralFortificationCountMap}
          hintPopup={showMadagascarTradeHint && !tradingHubStatus?.tradingBlocked ? { cellId: 457, message: t('warEffort.tradeWithSentientsMadagascarHint', 'Trade with The Sentients in Madagascar.') } : null}
          cellsUnderAttack={cellsUnderAttack}
          tectonicBoredriveCells={tectonicBoredriveCellsForMap}
        />

        {/* Dev-only: Bot log when player2 is simulated */}
        {devModeActive && isPlayer1 && missionData?.player2_simulated_at && missionData?.id && (
          <WarEffortBotLog missionId={missionData.id} />
        )}
        
        {/* Mission and Opponent Info Overlay */}
        {(() => {
          // First, verify that the current user is part of this mission
          if (!isUserInMission) {
            // User is not part of this mission - don't show anything
            return null;
          }
          
          // Only show mission info when opponent exists. 1v1: mission must be active and other slot filled. FFA/2v2: any other slot filled (including bot), even in waiting_crew.
          const hasOpponent = missionData?.mission_mode === 'ffa' || missionData?.mission_mode === '2v2'
            ? [1, 2, 3, 4].some(s => s !== viewerSlot && missionData?.[`player${s}_id`])
            : (missionData?.status === 'active' && (isPlayer1 ? missionData?.player2_id : missionData?.player1_id));
          
          // Don't show mission until opponent exists - mission target depends on opponent's headquarters
          if (!hasOpponent) {
            const playerPurpose = (missionData?.mission_mode === 'ffa' || missionData?.mission_mode === '2v2') && viewerSlot >= 1 && viewerSlot <= 4
              ? (missionData?.[`player${viewerSlot}_purpose`] ?? null)
              : (isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose);
            const viewerName = (typeof user === 'string' ? user : user?.username) || (isPlayer1 ? 'Player 1' : 'Player 2');
            const hqCellId = playerPurpose ? PURPOSE_TO_HQ_CELL_ID[playerPurpose] : null;
            const hqCountry = hqCellId != null ? getCountryFromCellId(hqCellId) : null;
            const playerFactionName = playerPurpose ? DEV_PURPOSE_TO_FACTION[playerPurpose] : null;
            const hqBg = playerFactionName ? getHeadquartersImage(playerFactionName) : null;
            return playerPurpose && (
              <div
                className="war-effort-info-box war-effort-info-box--on-map war-effort-map-chrome--info-single"
                style={{
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  background: hqBg
                    ? `linear-gradient(135deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.72) 100%), url(${hqBg}) center/cover`
                    : 'rgba(12, 14, 18, 0.88)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255,255,255,0.04)'
                }}
              >
                <div style={{
                  padding: '10px 14px',
                  color: 'rgba(255, 255, 255, 0.92)',
                  fontSize: '13px',
                  fontFamily: "'Exo 2', 'Segoe UI', system-ui, sans-serif"
                }}>
                  <div className="war-effort-info-box-header">
                    <span><span style={{ fontWeight: '600', marginRight: '6px' }}>{viewerName} HQ:</span>{hqCountry || '—'}</span>
                    <span style={{ color: PURPOSE_TO_COLOR[playerPurpose] || '#4ECDC4', fontWeight: '600', fontSize: '12px' }}>{playerPurpose}</span>
                  </div>
                  <div className="war-effort-info-box-value war-effort-info-box-value--muted" style={{ marginTop: '8px', fontSize: '12px' }}>
                    Waiting for opponent to join...
                  </div>
                </div>
              </div>
            );
          }
          
          // Calculate the correct mission name for this player (only when opponent exists). FFA/2v2: use viewerSlot.
          const playerPurpose = (missionData?.mission_mode === 'ffa' || missionData?.mission_mode === '2v2') && viewerSlot >= 1 && viewerSlot <= 4
            ? (missionData?.[`player${viewerSlot}_purpose`] ?? null)
            : (isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose);
          const playerTargetCountry = (missionData?.mission_mode === 'ffa' || missionData?.mission_mode === '2v2') && viewerSlot >= 1 && viewerSlot <= 4
            ? (missionData?.[`player${viewerSlot}_mission_target_country`] || missionData?.mission_target_country)
            : (isPlayer1 ? (missionData?.player1_mission_target_country || missionData?.mission_target_country) : (missionData?.player2_mission_target_country || missionData?.mission_target_country));
          const playerMissionType = playerPurpose ? PURPOSE_TO_MISSION_TYPE[playerPurpose] : null;
          const playerMissionName = (playerMissionType && playerTargetCountry)
            ? `${playerMissionType} in ${playerTargetCountry}`
            : missionData?.mission_name; // Fallback to stored mission name
          // Country for hover: use explicit target, or parse from "MissionType in Country" when from mission_name fallback
          const missionCountryForHover = playerTargetCountry || ((playerMissionName && / in /.test(playerMissionName))
            ? playerMissionName.replace(/^.*\s+in\s+/, '').trim()
            : null);
          const playerTargetCellId = missionCountryForHover ? getCellIdFromCountry(missionCountryForHover) : null;

          const lastOpponentDisplay = getLastOpponentActionDisplay(lastOpponentAction, lastOpponentActionCellId, t);
          
          // Single-opponent (1v1) mission name for legacy display
          const opponentPurpose = isPlayer1 ? missionData?.player2_purpose : missionData?.player1_purpose;
          const opponentTargetCountry = isPlayer1
            ? (missionData?.player2_mission_target_country || missionData?.mission_target_country)
            : (missionData?.player1_mission_target_country || missionData?.mission_target_country);
          const opponentMissionType = opponentPurpose ? PURPOSE_TO_MISSION_TYPE[opponentPurpose] : null;
          const opponentMissionName = (opponentMissionType && opponentTargetCountry)
            ? `${opponentMissionType} in ${opponentTargetCountry}`
            : null;
          
          const showMultiOpponent = opponentsForBox.length > 0;
          const playerHqCellId = playerPurpose ? PURPOSE_TO_HQ_CELL_ID[playerPurpose] : null;
          const playerHqCountry = playerHqCellId != null ? getCountryFromCellId(playerHqCellId) : null;
          const viewerName = (typeof user === 'string' ? user : user?.username) || (isPlayer1 ? 'Player 1' : 'Player 2');
          const playerFactionName = playerPurpose ? DEV_PURPOSE_TO_FACTION[playerPurpose] : null;
          const playerHqBg = playerFactionName ? getHeadquartersImage(playerFactionName) : null;
          const opponentHqCellId = opponentPurpose ? PURPOSE_TO_HQ_CELL_ID[opponentPurpose] : null;
          const opponentHqCountry = opponentHqCellId != null ? getCountryFromCellId(opponentHqCellId) : null;
          const opponentName = opponentUsername || (isPlayer1 ? 'Player 2' : 'Player 1');
          const opponentFactionName = opponentPurpose ? DEV_PURPOSE_TO_FACTION[opponentPurpose] : null;
          const opponentHqBg = opponentFactionName ? getHeadquartersImage(opponentFactionName) : null;
          const hqBoxStyle = (bgImage) => ({
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '10px',
            overflow: 'hidden',
            position: 'relative',
            background: bgImage
              ? `linear-gradient(135deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.72) 100%), url(${bgImage}) center/cover`
              : 'rgba(12, 14, 18, 0.88)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255,255,255,0.04)',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column'
          });
          const hqOverlayStyle = {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            padding: '10px 14px',
            color: 'rgba(255, 255, 255, 0.92)',
            fontSize: '13px',
            fontFamily: "'Exo 2', 'Segoe UI', system-ui, sans-serif",
            minHeight: '44px',
            flex: '1 1 auto'
          };
          const sectionLabel = 'war-effort-info-box-label';
          const sectionValue = 'war-effort-info-box-value';
          const sectionValueMuted = 'war-effort-info-box-value war-effort-info-box-value--muted';
          const renderConquerList = (items, purposeForConquered) => (
            <div className="war-effort-info-box-conquer-list">
              {items.map(({ cellId, countryName, conquered }) => (
                <div
                  key={cellId}
                  className="war-effort-info-box-conquer-row"
                  onMouseEnter={() => setMissionSubmissionHoverCellId(cellId)}
                  onMouseLeave={() => setMissionSubmissionHoverCellId(null)}
                  role="listitem"
                  aria-label={`Conquer ${countryName}${conquered ? ', conquered' : ''}`}
                >
                  {conquered && <span className="war-effort-info-box-conquer-done" aria-label="Conquered">✓</span>}
                  <span>Conquer {countryName}</span>
                </div>
              ))}
            </div>
          );
          const hasAnyBox = playerHqCountry || (showMultiOpponent ? opponentsForBox.some(o => o.hqCountry) : opponentHqCountry);
          return (playerMissionName || opponentUsername || (Array.isArray(opponentUsernames) && opponentUsernames.length > 0) || playerPurpose) && hasAnyBox && (
            <div className="war-effort-map-chrome--info-stack">
              {/* Viewer's single info box: header + purpose in top-right; expand on click for mission, submission, last action */}
              {playerHqCountry && (
                <div
                  className="war-effort-info-box war-effort-info-box--on-map"
                  style={hqBoxStyle(playerHqBg)}
                  onClick={() => setExpandedInfoSlots(prev => prev.includes(viewerSlot) ? prev.filter(s => s !== viewerSlot) : [...prev, viewerSlot])}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedInfoSlots(prev => prev.includes(viewerSlot) ? prev.filter(s => s !== viewerSlot) : [...prev, viewerSlot]); } }}
                  aria-expanded={expandedInfoSlots.includes(viewerSlot)}
                  aria-label={`${viewerName} HQ ${playerHqCountry}. ${expandedInfoSlots.includes(viewerSlot) ? 'Collapse' : 'Expand'} details`}
                >
                  <div style={hqOverlayStyle}>
                    <div className="war-effort-info-box-header">
                      <span
                        className="war-effort-info-box-hq-link"
                        onMouseEnter={() => setMissionSubmissionHoverCellId(playerHqCellId)}
                        onMouseLeave={() => setMissionSubmissionHoverCellId(null)}
                        role="img"
                        aria-label={`Highlight ${playerHqCountry} on map`}
                      >
                        <span style={{ fontWeight: '600', marginRight: '6px' }}>{viewerName} HQ:</span>
                        {playerHqCountry}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {playerPurpose && <span style={{ color: PURPOSE_TO_COLOR[playerPurpose] || '#4ECDC4', fontWeight: '600', fontSize: '12px' }}>{playerPurpose}</span>}
                        <span className="war-effort-info-box-chevron" title={expandedInfoSlots.includes(viewerSlot) ? 'Click to collapse' : 'Click to expand'}>
                          {expandedInfoSlots.includes(viewerSlot) ? '▼' : '▶'}
                        </span>
                      </span>
                    </div>
                    {expandedInfoSlots.includes(viewerSlot) && (
                      <div className="war-effort-info-box-expanded">
                        {playerMissionName && (
                          <div className="war-effort-info-box-section">
                            <div className={sectionLabel}>Mission</div>
                            <div className={sectionValue}>
                              {playerTargetCellId != null ? (
                                <span
                                  className="war-effort-info-box-hq-link"
                                  onMouseEnter={() => setMissionSubmissionHoverCellId(playerTargetCellId)}
                                  onMouseLeave={() => setMissionSubmissionHoverCellId(null)}
                                  role="img"
                                  aria-label={`Highlight ${missionCountryForHover || getCountryFromCellId(playerTargetCellId)} on map`}
                                >
                                  {playerMissionName ?? playerMissionType}
                                </span>
                              ) : (
                                (playerMissionName ?? playerMissionType)
                              )}
                            </div>
                            {!showMultiOpponent && Array.isArray(opponentFortifiedCountries) && opponentFortifiedCountries.length > 0 && (() => {
                              const allowedFactions = playerPurpose ? PURPOSE_TO_FACTION_MAPPING[playerPurpose] || [] : [];
                              const subMissions = opponentFortifiedCountries.slice(0, 3).map((cellId) => {
                                const countryName = getCountryFromCellId(cellId);
                                const owner = cellOwnersMap[String(cellId)];
                                const conquered = owner && allowedFactions.includes(owner);
                                return { cellId, countryName, conquered };
                              });
                              return renderConquerList(subMissions);
                            })()}
                          </div>
                        )}
                        {lastPlayerAction && (() => {
                          const lastActionCellIdForHover = lastPlayerActionCellId ?? getCellIdFromLastActionMessage(lastPlayerAction);
                          return (
                            <div className="war-effort-info-box-section">
                              <div className={sectionLabel}>Last action</div>
                              <div className={sectionValueMuted} style={{ color: '#fbbf24' }}>
                                {lastActionCellIdForHover != null ? (
                                  <span
                                    className="war-effort-info-box-hq-link"
                                    onMouseEnter={() => setMissionSubmissionHoverCellId(lastActionCellIdForHover)}
                                    onMouseLeave={() => setMissionSubmissionHoverCellId(null)}
                                    role="img"
                                    aria-label={`Highlight ${getCountryFromCellId(lastActionCellIdForHover)} on map`}
                                  >
                                    {lastPlayerAction}
                                  </span>
                                ) : (
                                  lastPlayerAction
                                )}
                              </div>
                            </div>
                          );
                        })()}
                        {opponentAttackTargets && opponentAttackTargets.length > 0 && (
                          <div className="war-effort-info-box-section">
                            <div className={sectionLabel}>Opponents attacking</div>
                            <div className="war-effort-info-box-conquer-list">
                              {opponentAttackTargets.map((at) => (
                                <div
                                  key={at.userId}
                                  className="war-effort-info-box-conquer-row"
                                  onMouseEnter={() => setMissionSubmissionHoverCellId(at.countryCellId)}
                                  onMouseLeave={() => setMissionSubmissionHoverCellId(null)}
                                  role="listitem"
                                  aria-label={`${at.username} attacking ${at.countryName}`}
                                >
                                  {at.username} → {at.countryName}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {(isCeaseFireActive || isOpponentFortifiedCeaseFireActive || isConquestAttackerCeaseFireActive) && (
                          <div className="war-effort-info-box-section">
                            <div className={sectionLabel}>Status</div>
                            <div className="war-effort-info-box-status war-effort-info-box-status--warning">
                              {isCeaseFireActive && (
                                <span>Cease fire: {formatCeaseFireCountdown(ceaseFireRemainingSeconds)} (no attacks)</span>
                              )}
                              {!isCeaseFireActive && isOpponentFortifiedCeaseFireActive && (
                                <span>Opponent fortified protected: {formatCeaseFireCountdown(opponentFortifiedCeaseFireRemainingSeconds)} (neutral & other targets attackable)</span>
                              )}
                              {!isCeaseFireActive && !isOpponentFortifiedCeaseFireActive && isConquestAttackerCeaseFireActive && (
                                <span>Conquest cooldown: {formatCeaseFireCountdown(conquestAttackerCeaseFireRemainingSeconds)} (no opponent fortified or HQ; other attacks OK)</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* Opponent info box(es): one per opponent, same pattern */}
              {showMultiOpponent && opponentsForBox.filter(o => o.hqCountry).map((opp) => (
                <div
                  key={opp.slot}
                  className="war-effort-info-box war-effort-info-box--on-map"
                  style={hqBoxStyle(opp.factionName ? getHeadquartersImage(opp.factionName) : null)}
                  onClick={() => setExpandedInfoSlots(prev => prev.includes(opp.slot) ? prev.filter(s => s !== opp.slot) : [...prev, opp.slot])}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedInfoSlots(prev => prev.includes(opp.slot) ? prev.filter(s => s !== opp.slot) : [...prev, opp.slot]); } }}
                  aria-expanded={expandedInfoSlots.includes(opp.slot)}
                  aria-label={`${opp.username} HQ ${opp.hqCountry}. ${expandedInfoSlots.includes(opp.slot) ? 'Collapse' : 'Expand'} details`}
                >
                  <div style={hqOverlayStyle}>
                    <div className="war-effort-info-box-header">
                      <span
                        className="war-effort-info-box-hq-link"
                        onMouseEnter={() => setMissionSubmissionHoverCellId(opp.hqCellId)}
                        onMouseLeave={() => setMissionSubmissionHoverCellId(null)}
                        role="img"
                        aria-label={`Highlight ${opp.hqCountry} on map`}
                      >
                        <span style={{ fontWeight: '600', marginRight: '6px' }}>{opp.username} HQ:</span>
                        {opp.hqCountry}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {opp.purpose && <span style={{ color: PURPOSE_TO_COLOR[opp.purpose] || '#4ECDC4', fontWeight: '600', fontSize: '12px' }}>{opp.purpose}</span>}
                        <span className="war-effort-info-box-chevron" title={expandedInfoSlots.includes(opp.slot) ? 'Click to collapse' : 'Click to expand'}>
                          {expandedInfoSlots.includes(opp.slot) ? '▼' : '▶'}
                        </span>
                      </span>
                    </div>
                    {expandedInfoSlots.includes(opp.slot) && (
                      <div className="war-effort-info-box-expanded">
                        {opp.missionName && (
                          <div className="war-effort-info-box-section">
                            <div className={sectionLabel}>Mission</div>
                            <div className={sectionValue}>{opp.missionName}</div>
                          </div>
                        )}
                        {opponentsForBox[0]?.slot === opp.slot && lastOpponentDisplay?.message && (
                          <div className="war-effort-info-box-section">
                            <div className={sectionLabel}>Last action</div>
                            <div
                              className={sectionValueMuted}
                              {...(lastOpponentDisplay.cellId != null
                                ? {
                                    style: { cursor: 'pointer' },
                                    onMouseEnter: () => setMissionSubmissionHoverCellId(lastOpponentDisplay.cellId),
                                    onMouseLeave: () => setMissionSubmissionHoverCellId(null)
                                  }
                                : {})}
                              role={lastOpponentDisplay.cellId != null ? 'listitem' : undefined}
                              aria-label={lastOpponentDisplay.cellId != null ? lastOpponentDisplay.message : undefined}
                            >
                              {lastOpponentDisplay.message}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {!showMultiOpponent && opponentHqCountry && (
                <div
                  className="war-effort-info-box war-effort-info-box--on-map"
                  style={hqBoxStyle(opponentHqBg)}
                  onClick={() => { const slot = isPlayer1 ? 2 : 1; setExpandedInfoSlots(prev => prev.includes(slot) ? prev.filter(s => s !== slot) : [...prev, slot]); }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); const slot = isPlayer1 ? 2 : 1; setExpandedInfoSlots(prev => prev.includes(slot) ? prev.filter(s => s !== slot) : [...prev, slot]); } }}
                  aria-expanded={expandedInfoSlots.includes(isPlayer1 ? 2 : 1)}
                  aria-label={`${opponentName} HQ ${opponentHqCountry}. ${expandedInfoSlots.includes(isPlayer1 ? 2 : 1) ? 'Collapse' : 'Expand'} details`}
                >
                  <div style={hqOverlayStyle}>
                    <div className="war-effort-info-box-header">
                      <span
                        className="war-effort-info-box-hq-link"
                        onMouseEnter={() => setMissionSubmissionHoverCellId(opponentHqCellId)}
                        onMouseLeave={() => setMissionSubmissionHoverCellId(null)}
                        role="img"
                        aria-label={`Highlight ${opponentHqCountry} on map`}
                      >
                        <span style={{ fontWeight: '600', marginRight: '6px' }}>{opponentName} HQ:</span>
                        {opponentHqCountry}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {opponentPurpose && <span style={{ color: PURPOSE_TO_COLOR[opponentPurpose] || '#4ECDC4', fontWeight: '600', fontSize: '12px' }}>{opponentPurpose}</span>}
                        <span className="war-effort-info-box-chevron" title={expandedInfoSlots.includes(isPlayer1 ? 2 : 1) ? 'Click to collapse' : 'Click to expand'}>
                          {expandedInfoSlots.includes(isPlayer1 ? 2 : 1) ? '▼' : '▶'}
                        </span>
                      </span>
                    </div>
                    {expandedInfoSlots.includes(isPlayer1 ? 2 : 1) && (
                      <div className="war-effort-info-box-expanded">
                        {opponentMissionName && (
                          <div className="war-effort-info-box-section">
                            <div className={sectionLabel}>Mission</div>
                            <div className={sectionValue}>{opponentMissionName}</div>
                          </div>
                        )}
                        {lastOpponentDisplay?.message && (
                          <div className="war-effort-info-box-section">
                            <div className={sectionLabel}>Last action</div>
                            <div
                              className={sectionValueMuted}
                              {...(lastOpponentDisplay.cellId != null
                                ? {
                                    style: { cursor: 'pointer' },
                                    onMouseEnter: () => setMissionSubmissionHoverCellId(lastOpponentDisplay.cellId),
                                    onMouseLeave: () => setMissionSubmissionHoverCellId(null)
                                  }
                                : {})}
                              role={lastOpponentDisplay.cellId != null ? 'listitem' : undefined}
                              aria-label={lastOpponentDisplay.cellId != null ? lastOpponentDisplay.message : undefined}
                            >
                              {lastOpponentDisplay.message}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Active shop effects (right side) - thumbnails with tooltip (name + description) on hover */}
        {isUserInMission && activeShopEffects.length > 0 && (
          <ActiveShopEffectsThumbnails
            effects={activeShopEffects}
            effectTypeToKey={SHOP_EFFECT_TYPE_TO_KEY}
            effectTypeToImage={SHOP_EFFECT_TYPE_TO_IMAGE}
            t={t}
          />
        )}

        {/* Attack mode: distance/cost bar and cancel - below header to avoid overlap with Friends/Settings/username */}
        {attackFromCountry && (
          <div className="war-effort-map-chrome--attack-bar">
            <span style={{ color: '#FFD700' }}>Attacking from {attackFromCountry.countryName}</span>
            {attackModeTargetCost && validAttackTargetCellId && (
              <>
                <span>→ {getCountryFromCellId(validAttackTargetCellId)}</span>
                {attackModeTargetCost.distanceKm != null && (
                  <span style={{ color: '#4ECDC4' }}>{attackModeTargetCost.distanceKm} km</span>
                )}
                <span style={{ color: '#FFD700', fontWeight: 'bold' }}>
                  {attackModeTargetCost.cost} Energy
                  {attackModeTargetCost.isHqAttack && ' (base + 400)'}
                </span>
                {playerResources < attackModeTargetCost.cost && (
                  <span style={{ color: '#ff6b6b' }}>Insufficient energy</span>
                )}
              </>
            )}
            {(!validAttackTargetCellId || !attackModeTargetCost) && !isCeaseFireActive && (
              <span style={{ color: '#aaa' }}>Hover a country you don't own to see cost</span>
            )}
            {isCeaseFireActive && (
              <span style={{ color: '#FFB347' }}>Cease fire — attacks disabled ({formatCeaseFireCountdown(ceaseFireRemainingSeconds)})</span>
            )}
            {!isCeaseFireActive && isOpponentFortifiedCeaseFireActive && (
              <span style={{ color: '#FFB347' }}>Opponent fortified protected ({formatCeaseFireCountdown(opponentFortifiedCeaseFireRemainingSeconds)}) — other targets attackable</span>
            )}
            {!isCeaseFireActive && !isOpponentFortifiedCeaseFireActive && isConquestAttackerCeaseFireActive && (
              <span style={{ color: '#FFB347' }}>Conquest cooldown — opponent fortified & HQ blocked ({formatCeaseFireCountdown(conquestAttackerCeaseFireRemainingSeconds)})</span>
            )}
            <button
              type="button"
              onClick={() => { setAttackFromCountry(null); setAttackModeTargetCost(null); setError(null); }}
              style={{
                marginLeft: '8px',
                padding: '8px 16px',
                backgroundColor: '#555',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
      
      {/* Owned country choice: Fortifications vs Attack */}
      {ownedCountryChoice && (
        <div
          className="war-effort-owned-choice-overlay"
          onClick={() => setOwnedCountryChoice(null)}
        >
          <div
            className="war-effort-owned-choice-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="war-effort-owned-choice-title">
              {ownedCountryChoice.cellId === protectedCellId
                ? `Fortify ${ownedCountryChoice.countryName} — Country is protected and needs no further fortification`
                : `What are your orders for our forces in ${ownedCountryChoice.countryName}?`}
            </h3>
            <div className="war-effort-owned-choice-actions">
              {ownedCountryChoice.cellId === protectedCellId ? (
                <p className="war-effort-owned-choice-note">
                  {t('warEffort.tradingHub.countryProtectedNoFortify', 'This country is protected by the Trading Hub and cannot be attacked or fortified.')}
                </p>
              ) : (
                <>
                  {(() => {
                    const tectonicIds = (missionData?.tectonic_boredrive_cell_ids || []).map((id) => Number(id)).filter((n) => !isNaN(n));
                    const isTectonicCell = tectonicIds.includes(Number(ownedCountryChoice.cellId));
                    const isHQCell = ALL_HQ_CELL_IDS.includes(Number(ownedCountryChoice.cellId));
                    const fortifyDisabled = playerFortificationSlotsUsed >= 3 || isTectonicCell;
                    return (
                      <button
                        type="button"
                        className="war-effort-owned-option-btn"
                        disabled={fortifyDisabled}
                        onClick={() => {
                          if (fortifyDisabled) return;
                          setSelectedCountry(ownedCountryChoice.cellId);
                          setSelectedFortification(null);
                          setOwnedCountryChoice(null);
                          setError(null);
                        }}
                        title={isTectonicCell ? t('warEffort.cannotFortifyTectonicBoredrive') : (playerFortificationSlotsUsed >= 3 ? t('warEffort.mission.maxFortifyHint') : undefined)}
                        style={{
                          padding: '20px 24px',
                          fontSize: '18px',
                          fontWeight: 'bold',
                          backgroundColor: fortifyDisabled ? '#555' : '#333',
                          color: fortifyDisabled ? '#999' : 'white',
                          border: '2px solid rgba(255,255,255,0.3)',
                          borderRadius: '10px',
                          cursor: fortifyDisabled ? 'not-allowed' : 'pointer',
                          opacity: fortifyDisabled ? 0.8 : 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '4px',
                          backgroundImage: `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.6)), url(/images/fortify_button.jpg)`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }}
                      >
                        <span>Fortify {ownedCountryChoice.countryName}</span>
                        {playerFortificationSlotsUsed >= 3 && !isTectonicCell && (
                          <span style={{ fontSize: '13px', fontWeight: 400, opacity: 0.95 }}>
                            Already fortified 3 countries
                          </span>
                        )}
                        {isTectonicCell && (
                          <span style={{ fontSize: '13px', fontWeight: 400, opacity: 0.95 }}>
                            {t('warEffort.tectonicBoredriveWasUsedHere')}
                          </span>
                        )}
                      </button>
                    );
                  })()}
                  {!ALL_HQ_CELL_IDS.includes(Number(ownedCountryChoice.cellId)) && ownedCountryChoice.cellId !== protectedCellId && !(missionData?.tectonic_boredrive_cell_ids || []).map((id) => Number(id)).includes(Number(ownedCountryChoice.cellId)) && (
                    <button
                      type="button"
                      className="war-effort-owned-option-btn"
                      disabled={tectonicBoredriveLoading || tectonicBoredriveCost == null || (engineersForDisplay ?? 0) < (tectonicBoredriveCost ?? 0)}
                      onClick={async () => {
                        if (tectonicBoredriveLoading || tectonicBoredriveCost == null || (engineersForDisplay ?? 0) < tectonicBoredriveCost) return;
                        setTectonicBoredriveLoading(true);
                        setTectonicBoredriveResult(null);
                        try {
                          const token = localStorage.getItem('token');
                          const res = await fetch(`${API_URL}/war-effort/tectonic-boredrive`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                            body: JSON.stringify({ missionId: missionData?.id, cellId: ownedCountryChoice.cellId })
                          });
                          const data = await res.json().catch(() => ({}));
                          if (!res.ok) throw new Error(data.error || t('warEffort.tectonicBoredriveFailed'));
                          setTectonicBoredriveResult(data.energyGranted != null ? t('warEffort.tectonicBoredriveEnergyGained', { amount: data.energyGranted }) : null);
                          fetchControlledCountries();
                        } catch (err) {
                          setError(err.message || t('warEffort.tectonicBoredriveFailed'));
                        } finally {
                          setTectonicBoredriveLoading(false);
                        }
                      }}
                      title={tectonicBoredriveCost != null && (engineersForDisplay ?? 0) < tectonicBoredriveCost ? t('warEffort.tectonicBoredriveNeedEngineers', { need: tectonicBoredriveCost, have: engineersForDisplay ?? 0 }) : undefined}
                      style={{
                        padding: '20px 24px',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        backgroundColor: (tectonicBoredriveLoading || tectonicBoredriveCost == null || (engineersForDisplay ?? 0) < (tectonicBoredriveCost ?? 0)) ? '#555' : '#333',
                        color: 'white',
                        border: '2px solid rgba(255,255,255,0.3)',
                        borderRadius: '10px',
                        cursor: (tectonicBoredriveLoading || tectonicBoredriveCost == null || (engineersForDisplay ?? 0) < (tectonicBoredriveCost ?? 0)) ? 'not-allowed' : 'pointer',
                        opacity: (tectonicBoredriveLoading || tectonicBoredriveCost == null || (engineersForDisplay ?? 0) < (tectonicBoredriveCost ?? 0)) ? 0.8 : 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '4px',
                        backgroundImage: `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.6)), url(/images/tectonic_boredrive.jpg)`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }}
                    >
                      <span>{tectonicBoredriveCost != null ? t('warEffort.tectonicBoredriveWithCost', { count: tectonicBoredriveCost }) : t('warEffort.tectonicBoredriveCostLoading')}</span>
                      {tectonicBoredriveResult && (
                        <span style={{ fontSize: '13px', fontWeight: 400, opacity: 0.95, color: '#7fdb7f' }}>
                          {tectonicBoredriveResult}
                        </span>
                      )}
                    </button>
                  )}
                  {(() => {
                    const noOpponentYet = missionData?.mission_mode === 'ffa'
                      ? [missionData.player1_id, missionData.player2_id, missionData.player3_id, missionData.player4_id].filter(Boolean).filter(id => Number(id) !== Number(userId)).length === 0
                      : !missionData?.player2_id;
                    const attackDisabled = noOpponentYet || isCeaseFireActive;
                    const reasonText = attackDisabled
                      ? (noOpponentYet ? 'Opponent not revealed yet' : `Cease fire in effect (${formatCeaseFireCountdown(ceaseFireRemainingSeconds)})`)
                      : null;
                    return (
                      <button
                        type="button"
                        className="war-effort-owned-option-btn"
                        disabled={attackDisabled}
                        onClick={() => {
                          if (attackDisabled) return;
                          setAttackFromCountry({ cellId: ownedCountryChoice.cellId, countryName: ownedCountryChoice.countryName });
                          setOwnedCountryChoice(null);
                          setError(null);
                        }}
                        title={attackDisabled ? (noOpponentYet ? 'Wait for a second player to join' : 'Cease fire: no attacks allowed') : undefined}
                        style={{
                          padding: '20px 24px',
                          fontSize: '18px',
                          fontWeight: 'bold',
                          backgroundColor: attackDisabled ? '#555' : '#333',
                          color: 'white',
                          border: '2px solid rgba(255,255,255,0.3)',
                          borderRadius: '10px',
                          cursor: attackDisabled ? 'not-allowed' : 'pointer',
                          opacity: attackDisabled ? 0.8 : 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '4px',
                          backgroundImage: `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.6)), url(/images/attack_country_button.jpg)`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }}
                      >
                        <span>Attack Country</span>
                        {reasonText && (
                          <span style={{ fontSize: '13px', fontWeight: 400, opacity: 0.95 }}>
                            {reasonText}
                          </span>
                        )}
                      </button>
                    );
                  })()}
                  <button
                    type="button"
                    className="war-effort-owned-option-btn"
                    onClick={async () => {
                      const cellId = ownedCountryChoice.cellId;
                      const countryName = ownedCountryChoice.countryName;
                      setDefenseStrategyDialog(prev => ({
                        ...prev,
                        show: true,
                        cellId,
                        countryName,
                        selectedStrategyId: userStrategies.length > 0 ? (userStrategies[0].id) : null,
                        useEverywhere: false,
                        loading: true,
                        saving: false
                      }));
                      setOwnedCountryChoice(null);
                      try {
                        const token = localStorage.getItem('token');
                        const res = await fetch(
                          `${API_URL}/war-effort/defense-strategy?missionId=${missionData?.id}&countryCellId=${cellId}`,
                          { headers: { 'Authorization': `Bearer ${token}` } }
                        );
                        if (res.ok) {
                          const data = await res.json();
                          const prefill = data.countryStrategyId ?? data.defaultStrategyId ?? (userStrategies.length > 0 ? userStrategies[0].id : null);
                          const stillValid = prefill && userStrategies.some(s => String(s.id) === String(prefill));
                          setDefenseStrategyDialog(prev => ({
                            ...prev,
                            selectedStrategyId: stillValid ? prefill : (userStrategies.length > 0 ? userStrategies[0].id : null),
                            useEverywhere: !!data.defaultStrategyId,
                            loading: false
                          }));
                        } else {
                          setDefenseStrategyDialog(prev => ({ ...prev, loading: false }));
                        }
                      } catch (e) {
                        setDefenseStrategyDialog(prev => ({ ...prev, loading: false }));
                      }
                    }}
                    style={{
                      padding: '20px 24px',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      backgroundColor: '#333',
                      color: 'white',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '4px',
                      backgroundImage: 'linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.6)), url(/images/defense_strategy_button.jpg)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  >
                    <span>Set defense strategy</span>
                    <span style={{ fontSize: '13px', fontWeight: 400, opacity: 0.95 }}>
                      {ownedCountryDefenseStrategyLoading
                        ? 'Loading...'
                        : ownedCountryDefenseStrategyId != null && userStrategies.length > 0
                          ? `Current: ${userStrategies.find(s => String(s.id) === String(ownedCountryDefenseStrategyId))?.name ?? 'Unknown'}`
                          : 'Used when this country is attacked'}
                    </span>
                  </button>
                </>
              )}
              <button
                type="button"
                className="war-effort-owned-choice-cancel"
                onClick={() => setOwnedCountryChoice(null)}
              >
                {ownedCountryChoice.cellId === protectedCellId ? 'Close' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Own HQ choice: Attack Country vs Show HQ */}
      {ownedHQChoice && (
        <div
          className="war-effort-owned-choice-overlay"
          onClick={() => setOwnedHQChoice(null)}
        >
          <div
            className="war-effort-owned-choice-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="war-effort-owned-choice-title">
              What would you like to do at your HQ ({ownedHQChoice.countryName})?
            </h3>
            <div className="war-effort-owned-choice-actions">
              {(() => {
                const noOpponentYet = missionData?.mission_mode === 'ffa'
                  ? [missionData.player1_id, missionData.player2_id, missionData.player3_id, missionData.player4_id].filter(Boolean).filter(id => Number(id) !== Number(userId)).length === 0
                  : !missionData?.player2_id;
                const attackDisabled = noOpponentYet || isCeaseFireActive;
                const reasonText = attackDisabled
                  ? (noOpponentYet ? 'Opponent not revealed yet' : `Cease fire in effect (${formatCeaseFireCountdown(ceaseFireRemainingSeconds)})`)
                  : null;
                return (
                  <button
                    type="button"
                    className="war-effort-owned-option-btn"
                    disabled={attackDisabled}
                    onClick={() => {
                      if (attackDisabled) return;
                      setAttackFromCountry({ cellId: ownedHQChoice.cellId, countryName: ownedHQChoice.countryName });
                      setOwnedHQChoice(null);
                      setError(null);
                    }}
                    title={attackDisabled ? (noOpponentYet ? 'Wait for a second player to join' : 'Cease fire: no attacks allowed') : undefined}
                    style={{
                      padding: '20px 24px',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      backgroundColor: attackDisabled ? '#555' : '#333',
                      color: 'white',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderRadius: '10px',
                      cursor: attackDisabled ? 'not-allowed' : 'pointer',
                      opacity: attackDisabled ? 0.8 : 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '4px',
                      backgroundImage: 'linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.6)), url(/images/attack_country_button.jpg)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  >
                    <span>Attack Country</span>
                    {reasonText && (
                      <span style={{ fontSize: '13px', fontWeight: 400, opacity: 0.95 }}>
                        {reasonText}
                      </span>
                    )}
                  </button>
                );
              })()}
              {(() => {
                const playerPurpose = isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose;
                const playerFactionName = playerPurpose ? DEV_PURPOSE_TO_FACTION[playerPurpose] : null;
                const hqImage = playerFactionName ? getHeadquartersImage(playerFactionName) : null;
                return (
                  <button
                    type="button"
                    className="war-effort-owned-option-btn"
                    onClick={() => {
                      setHqBuildingCountryName(ownedHQChoice.countryName);
                      onBuildingPageChange?.(true);
                      setOwnedHQChoice(null);
                      setError(null);
                    }}
                    style={{
                      padding: '20px 24px',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      backgroundColor: '#333',
                      color: 'white',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '4px',
                      backgroundImage: hqImage
                        ? `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.6)), url(${hqImage})`
                        : undefined,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  >
                    <span>Show HQ</span>
                    <span style={{ fontSize: '13px', fontWeight: 400, opacity: 0.95 }}>
                      Enter building mode
                    </span>
                  </button>
                );
              })()}
              <button
                type="button"
                className="war-effort-owned-option-btn"
                onClick={async () => {
                  const cellId = ownedHQChoice.cellId;
                  const countryName = ownedHQChoice.countryName;
                  setDefenseStrategyDialog(prev => ({
                    ...prev,
                    show: true,
                    cellId,
                    countryName,
                    selectedStrategyId: userStrategies.length > 0 ? (userStrategies[0].id) : null,
                    useEverywhere: false,
                    loading: true,
                    saving: false
                  }));
                  setOwnedHQChoice(null);
                  try {
                    const token = localStorage.getItem('token');
                    const res = await fetch(
                      `${API_URL}/war-effort/defense-strategy?missionId=${missionData?.id}&countryCellId=${cellId}`,
                      { headers: { 'Authorization': `Bearer ${token}` } }
                    );
                    if (res.ok) {
                      const data = await res.json();
                      const prefill = data.countryStrategyId ?? data.defaultStrategyId ?? (userStrategies.length > 0 ? userStrategies[0].id : null);
                      const stillValid = prefill && userStrategies.some(s => String(s.id) === String(prefill));
                      setDefenseStrategyDialog(prev => ({
                        ...prev,
                        selectedStrategyId: stillValid ? prefill : (userStrategies.length > 0 ? userStrategies[0].id : null),
                        useEverywhere: !!data.defaultStrategyId,
                        loading: false
                      }));
                    } else {
                      setDefenseStrategyDialog(prev => ({ ...prev, loading: false }));
                    }
                  } catch (e) {
                    setDefenseStrategyDialog(prev => ({ ...prev, loading: false }));
                  }
                }}
                style={{
                  padding: '20px 24px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  backgroundColor: '#333',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  backgroundImage: 'linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.6)), url(/images/defense_strategy_button.jpg)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                <span>Set defense strategy</span>
                <span style={{ fontSize: '13px', fontWeight: 400, opacity: 0.95 }}>
                  {ownedCountryDefenseStrategyLoading
                    ? 'Loading...'
                    : ownedCountryDefenseStrategyId != null && userStrategies.length > 0
                      ? `Current: ${userStrategies.find(s => String(s.id) === String(ownedCountryDefenseStrategyId))?.name ?? 'Unknown'}`
                      : 'Used when your HQ is attacked'}
                </span>
              </button>
              <button
                type="button"
                className="war-effort-owned-choice-cancel"
                onClick={() => setOwnedHQChoice(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Country Tooltip */}
      {hoveredCell !== null && (
        <div
          className="country-tooltip"
          style={{
            position: 'fixed',
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '8px',
            padding: '15px',
            zIndex: 2000,
            minWidth: '200px',
            maxHeight: `min(calc(100vh - 24px), ${Math.max(200, window.innerHeight - tooltipPosition.y - 12)}px)`,
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.7)',
            pointerEvents: 'none'
          }}
        >
          <div style={{ 
            color: 'white', 
            fontSize: '14px', 
            fontWeight: 'bold', 
            marginBottom: '10px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
            paddingBottom: '8px'
          }}>
            {getCountryFromCellId(hoveredCell)}
          </div>
          {!isHeadquarters(hoveredCell) && (() => {
            const cellForWarlord = getCellData(hoveredCell);
            const warlordName = cellForWarlord?.warlord_username ?? t('warEffort.warlordNone');
            return (
              <div style={{ color: '#c9a227', fontSize: '12px', marginBottom: '6px' }}>
                {t('warEffort.warlordLabel')}: {warlordName}
              </div>
            );
          })()}
          {tectonicBoredriveCellsForMap.includes(Number(hoveredCell)) && (
            <div style={{ color: '#b8860b', fontSize: '12px', marginBottom: '8px' }}>
              {t('warEffort.tectonicBoredriveDamaged')}
            </div>
          )}
          {(() => {
            const cellIdNum = Number(hoveredCell);
            const cell = getCellData(hoveredCell);

            // When cell data is missing (e.g. slow load, or type mismatch), still show country bonus for Cuba/Iceland/Sri Lanka/Taiwan – same section style as Fortification
            if (!cell) {
              const bonusText = COUNTRY_BONUS_CELL_IDS.includes(cellIdNum)
                ? (COUNTRY_BONUS_TOOLTIPS[cellIdNum] ?? COUNTRY_BONUS_TOOLTIPS[String(cellIdNum)])
                : null;
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <span style={{ color: '#aaa', fontSize: '13px' }}>Loading…</span>
                  {bonusText && (
                    <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
                      <div style={{ color: '#4ECDC4', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px' }}>Country bonus</div>
                      <div style={{ color: '#aaa', fontSize: '11px' }}>{bonusText}</div>
                    </div>
                  )}
                </div>
              );
            }

            // For headquarters, show image, owner, and fortifications (Hospital, Command Center) when viewer owns HQ
            if (isHeadquarters(hoveredCell) && cell.owner_name) {
              const headquartersImage = getHeadquartersImage(cell.owner_name);
              const playerPurpose = viewerPurpose ?? (isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose);
              const viewerFactions = playerPurpose ? PURPOSE_TO_FACTION_MAPPING[playerPurpose] || [] : [];
              const isViewerHq = viewerFactions.includes(cell.owner_name);
              const hospitalPlacement = placedBuildings.find((p) => p.building?.id === 'hospital' || p.building?.id === 'hospital_upgraded');
              const hasHospital = !!hospitalPlacement;
              const hospitalBuilding = hospitalPlacement ? HQ_BUILDINGS.find((b) => b.id === hospitalPlacement.building?.id) : HQ_BUILDINGS.find((b) => b.id === 'hospital');
              const commandCenterBuilding = HQ_BUILDINGS.find((b) => b.id === 'command_center');
              const isHqCommandCenterFortified = isViewerHq && (commandCenterBoostCellIds.includes(cellIdNum) || commandCenterBoostCellIds.includes(hoveredCell));
              const isOpponentHq = !viewerFactions.includes(cell.owner_name);
              const isOpponentHqCommandCenterFortified = isOpponentHq && (opponentCommandCenterBoostCellIds.includes(cellIdNum) || opponentCommandCenterBoostCellIds.includes(hoveredCell));
              const hasAnyHqFortification = isViewerHq && (hasHospital || isHqCommandCenterFortified);
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {headquartersImage && (
                    <img
                      src={headquartersImage}
                      alt={cell.owner_name}
                      style={{
                        width: '250px',
                        height: '150px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        marginBottom: '6px'
                      }}
                    />
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        background: getFactionBackground(cell.owner_name),
                        flexShrink: 0
                      }}
                    />
                    <span style={{ color: 'white', fontSize: '13px', flex: 1 }}>
                      {cell.owner_name}
                    </span>
                    <span style={{ color: 'white', fontSize: '13px', fontWeight: 'bold' }}>
                      Headquarters
                    </span>
                  </div>
                  {/* Buildings list: small building images (viewer's placedBuildings or opponent's buildings; FFA/2v2: per-opponent from opponentHqBuildingIdsByUser) */}
                  {(() => {
                    const hoveredNum = Number(hoveredCell);
                    const isFfaOr2v2 = missionData?.mission_mode === 'ffa' || missionData?.mission_mode === '2v2';
                    let buildingIds = [];
                    if (isViewerHq) {
                      buildingIds = placedBuildings.map((p) => p.building?.id).filter(Boolean);
                    } else if (isFfaOr2v2) {
                      const oppForCell = opponentsForBox.find((o) => o.hqCellId != null && o.hqCellId === hoveredNum);
                      buildingIds = oppForCell ? (opponentHqBuildingIdsByUser[String(oppForCell.userId)] || []) : [];
                    } else {
                      const oppPurpose = isPlayer1 ? missionData?.player2_purpose : missionData?.player1_purpose;
                      const oppHqCellId = oppPurpose ? PURPOSE_TO_HQ_CELL_ID[oppPurpose] : null;
                      const isOpponentPurposeHq = oppHqCellId != null && (hoveredNum === oppHqCellId);
                      buildingIds = isOpponentPurposeHq ? opponentHqBuildingIds : [];
                    }
                    if (buildingIds.length === 0) return null;
                    // 10 buildings per row: 10 * 28px + 9 * 4px gap = 316px
                    const buildingSize = 28;
                    const buildingGap = 4;
                    const buildingsPerRow = 10;
                    const rowWidth = buildingsPerRow * buildingSize + (buildingsPerRow - 1) * buildingGap;
                    return (
                      <div style={{
                        marginTop: '6px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: `${buildingGap}px`,
                        alignItems: 'center',
                        width: `${rowWidth}px`
                      }}>
                        {buildingIds.map((bid) => {
                          const b = HQ_BUILDINGS.find((x) => x.id === bid);
                          if (!b?.image) return null;
                          return (
                            <img
                              key={bid}
                              src={b.image}
                              alt={b.name}
                              title={b.name}
                              style={{
                                width: `${buildingSize}px`,
                                height: `${buildingSize}px`,
                                objectFit: 'cover',
                                borderRadius: '4px',
                                border: '1px solid rgba(255,255,255,0.2)'
                              }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          );
                        })}
                      </div>
                    );
                  })()}
                  {/* HQ fortifications: Hospital and/or Command Center (same detailed format as non-HQ) */}
                  {hasHospital && hospitalBuilding && (
                    <div style={{
                      marginTop: '8px',
                      paddingTop: '8px',
                      borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      <div style={{ color: '#4ECDC4', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px' }}>
                        Fortification
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {hospitalBuilding.image && (
                          <img
                            src={hospitalBuilding.image}
                            alt="Hospital"
                            style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ color: 'white', fontSize: '13px', fontWeight: 'bold' }}>{hospitalBuilding.name}</div>
                          <div style={{ color: '#aaa', fontSize: '11px', marginTop: '4px' }}>{hospitalBuilding.description}</div>
                        </div>
                      </div>
                    </div>
                  )}
                  {isHqCommandCenterFortified && commandCenterBuilding && (() => {
                    const hqCountryName = getCountryFromCellId(hoveredCell);
                    const allNeighbourCountries = hqCountryName ? getNeighbourCountries(hqCountryName) : [];
                    const playerPurpose = viewerPurpose ?? (isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose);
                    const allowedFactions = playerPurpose ? PURPOSE_TO_FACTION_MAPPING[playerPurpose] || [] : [];
                    const playerControlledCellIds = new Set(
                      Object.entries(cellOwnersMap)
                        .filter(([, owner]) => owner && allowedFactions.includes(owner))
                        .map(([cellIdStr]) => Number(cellIdStr))
                    );
                    const neighbourCountries = allNeighbourCountries.filter((name) => {
                      const cellId = getCellIdFromCountry(name);
                      return cellId != null && playerControlledCellIds.has(cellId);
                    });
                    return (
                      <div style={{
                        marginTop: '8px',
                        paddingTop: '8px',
                        borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                      }}>
                        <div style={{ color: '#4ECDC4', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px' }}>
                          Fortified by command center
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {commandCenterBuilding.image && (
                            <img
                              src={commandCenterBuilding.image}
                              alt="Command Center"
                              style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{ color: 'white', fontSize: '13px', fontWeight: 'bold' }}>{commandCenterBuilding.name}</div>
                            <div style={{ color: '#aaa', fontSize: '11px', marginTop: '4px' }}>{commandCenterBuilding.description}</div>
                            {neighbourCountries.length > 0 && (
                              <div style={{ color: '#aaa', fontSize: '11px', marginTop: '8px' }}>
                                <div style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}>Neighbouring countries:</div>
                                <ul style={{ margin: 0, paddingLeft: '16px' }}>
                                  {neighbourCountries.map((name) => (
                                    <li key={name} style={{ marginBottom: '2px' }}>{name}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  {/* Opponent HQ: Command Center boost (opponent owns 3+ neighbours of their HQ) */}
                  {isOpponentHqCommandCenterFortified && commandCenterBuilding && (() => {
                    const hqCountryName = getCountryFromCellId(hoveredCell);
                    const allNeighbourCountries = hqCountryName ? getNeighbourCountries(hqCountryName) : [];
                    let opponentPurpose = isPlayer1 ? missionData?.player2_purpose : missionData?.player1_purpose;
                    if ((missionData?.mission_mode === 'ffa' || missionData?.mission_mode === '2v2') && viewerSlot >= 1 && viewerSlot <= 4) {
                      const hoveredNum = Number(hoveredCell);
                      for (let s = 1; s <= 4; s++) {
                        if (s === viewerSlot) continue;
                        const p = missionData?.[`player${s}_purpose`];
                        if (p && PURPOSE_TO_HQ_CELL_ID[p] === hoveredNum) {
                          opponentPurpose = p;
                          break;
                        }
                      }
                    }
                    const opponentAllowedFactions = opponentPurpose ? PURPOSE_TO_FACTION_MAPPING[opponentPurpose] || [] : [];
                    const opponentControlledCellIds = new Set(
                      Object.entries(cellOwnersMap)
                        .filter(([, owner]) => owner && opponentAllowedFactions.includes(owner))
                        .map(([cellIdStr]) => Number(cellIdStr))
                    );
                    const neighbourCountries = allNeighbourCountries.filter((name) => {
                      const cellId = getCellIdFromCountry(name);
                      return cellId != null && opponentControlledCellIds.has(cellId);
                    });
                    return (
                      <div style={{
                        marginTop: '8px',
                        paddingTop: '8px',
                        borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                      }}>
                        <div style={{ color: '#4ECDC4', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px' }}>
                          Opponent&apos;s Command Center
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {commandCenterBuilding.image && (
                            <img
                              src={commandCenterBuilding.image}
                              alt="Command Center"
                              style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{ color: 'white', fontSize: '13px', fontWeight: 'bold' }}>Command Center</div>
                            <div style={{ color: '#aaa', fontSize: '11px', marginTop: '4px' }}>
                              +1 health to defender&apos;s units when you attack this HQ.
                            </div>
                            {neighbourCountries.length > 0 && (
                              <div style={{ color: '#aaa', fontSize: '11px', marginTop: '8px' }}>
                                <div style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}>Opponent&apos;s neighbouring countries:</div>
                                <ul style={{ margin: 0, paddingLeft: '16px' }}>
                                  {neighbourCountries.map((name) => (
                                    <li key={name} style={{ marginBottom: '2px' }}>{name}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  {/* Trading Hub protection – show when this country has been protected via Trading Hub */}
                  {protectedCellId != null && cellIdNum === Number(protectedCellId) && (
                    <div style={{
                      marginTop: '6px',
                      padding: '6px 8px',
                      background: 'rgba(76, 175, 80, 0.2)',
                      borderRadius: '4px',
                      borderLeft: '3px solid #4CAF50'
                    }}>
                      <span style={{ color: '#4CAF50', fontSize: '12px', fontWeight: 'bold' }}>{t('warEffort.tradingHub.protectedByTradingHub', 'Protected by Trading Hub')}</span>
                      <span style={{ color: 'white', fontSize: '12px' }}>{t('warEffort.tradingHub.cannotBeAttacked', ' — cannot be attacked.')}</span>
                    </div>
                  )}
                  {/* Iceland bonus on HQ: when the owner of this HQ owns Iceland, defending countries get +1 intel (same as other owned countries) */}
                  {(() => {
                    const icelandCell = warEffortData?.cells?.find(c => Number(c.cell_id) === ICELAND_CELL_ID);
                    const icelandOwnerName = icelandCell?.owner_name;
                    const ownerHasIceland = cell.owner_name && icelandOwnerName && cell.owner_name === icelandOwnerName;
                    if (!ownerHasIceland) return null;
                    return (
                      <div style={{
                        marginTop: '8px',
                        paddingTop: '8px',
                        borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                      }}>
                        <div style={{
                          color: '#4ECDC4',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          marginBottom: '6px'
                        }}>
                          Country bonus
                        </div>
                        <div style={{ color: '#aaa', fontSize: '11px' }}>
                          {ICELAND_BONUS_TOOLTIP} (owner owns Iceland)
                        </div>
                      </div>
                    );
                  })()}
                </div>
              );
            }
            
            // For regular countries, show owner and fortification if applicable (cellIdNum already set above)
            const isPlayerFortified = playerFortifiedCountries.includes(cellIdNum) || playerFortifiedCountries.includes(hoveredCell);
            const isOpponentFortified = opponentFortifiedCountries.includes(cellIdNum) || opponentFortifiedCountries.includes(hoveredCell);
            const isCommandCenterFortified = commandCenterBoostCellIds.includes(cellIdNum) || commandCenterBoostCellIds.includes(hoveredCell);
            const isOpponentCommandCenterFortified = opponentCommandCenterBoostCellIds.includes(cellIdNum) || opponentCommandCenterBoostCellIds.includes(hoveredCell);
            
            // Get fortification data (same pattern as attack dialog)
            let fortificationData = null;
            let fortification = null;
            
            if (isPlayerFortified || isOpponentFortified) {
              let fortifications = null;
              if (isPlayerFortified) {
                const slot = (missionData?.mission_mode === 'ffa' || missionData?.mission_mode === '2v2') ? viewerSlot : null;
                fortifications = (slot >= 1 && slot <= 4) ? (missionData?.[`player${slot}_fortifications`] ?? null) : (isPlayer1 ? missionData?.player1_fortifications : missionData?.player2_fortifications);
              } else {
                // Opponent: find which slot owns this cell (FFA/2v2 can have multiple opponents)
                const mode = missionData?.mission_mode;
                const mySlot = (mode === 'ffa' || mode === '2v2') ? viewerSlot : null;
                if ((mode === 'ffa' || mode === '2v2') && mySlot >= 1 && mySlot <= 4) {
                  for (let s = 1; s <= 4; s++) {
                    if (s === mySlot) continue;
                    const arr = getFortifiedCountriesArray(missionData?.[`player${s}_fortified_countries`]);
                    if (arr.includes(cellIdNum) || arr.includes(Number(hoveredCell))) {
                      fortifications = missionData?.[`player${s}_fortifications`] ?? null;
                      break;
                    }
                  }
                }
                if (!fortifications) fortifications = isPlayer1 ? missionData?.player2_fortifications : missionData?.player1_fortifications;
              }
              if (fortifications) {
                // Try both string and number keys (JSONB keys might be strings) - same pattern as attack dialog
                fortificationData = (
                  fortifications[cellIdNum] || 
                  fortifications[String(cellIdNum)] ||
                  fortifications[hoveredCell] || 
                  fortifications[String(hoveredCell)]
                ) ? (fortifications[cellIdNum] || fortifications[String(cellIdNum)] || fortifications[hoveredCell] || fortifications[String(hoveredCell)]) : null;
                
                if (fortificationData) {
                  const fortificationType = fortificationData.type || 'unknown';
                  fortification = fortificationType === 'eclipse_hidden'
                    ? { type: 'eclipse_hidden', name: 'Fortified' }
                    : FORTIFICATION_TYPES.find(f => f.type === fortificationType);
                  // Eclipse Array: show opponent fortification bonuses only when viewer has upgraded
                  if (isOpponentFortified && !viewerHasEclipseArrayUpgraded) {
                    fortification = { type: 'eclipse_hidden', name: 'Fortified' };
                  }
                }
              }
            }
            
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {/* Owner info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {cell.owner_name ? (
                    <>
                      <div
                        style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          background: getFactionBackground(cell.owner_name),
                          flexShrink: 0
                        }}
                      />
                      <span style={{ color: 'white', fontSize: '13px', flex: 1 }}>
                        {cell.owner_name}
                      </span>
                    </>
                  ) : (
                    <span style={{ color: '#aaa', fontSize: '13px' }}>Unclaimed</span>
                  )}
                </div>
                {/* Country ownership bonus (Cuba / Iceland / Sri Lanka / Taiwan) – same style as Fortification section; shown even when not fortified */}
                {COUNTRY_BONUS_CELL_IDS.includes(Number(cellIdNum)) && (() => {
                  const bonusText = COUNTRY_BONUS_TOOLTIPS[cellIdNum] ?? COUNTRY_BONUS_TOOLTIPS[String(cellIdNum)];
                  if (!bonusText) return null;
                  return (
                    <div style={{
                      marginTop: '8px',
                      paddingTop: '8px',
                      borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      <div style={{
                        color: '#4ECDC4',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        marginBottom: '6px'
                      }}>
                        Country bonus
                      </div>
                      <div style={{ color: '#aaa', fontSize: '11px' }}>
                        {bonusText}
                      </div>
                    </div>
                  );
                })()}
                {/* Iceland bonus on any country: when the owner of this country owns Iceland, all their defending countries get +1 intel – show in tooltip (e.g. Norway when you own Iceland) */}
                {(() => {
                  const icelandCell = warEffortData?.cells?.find(c => Number(c.cell_id) === ICELAND_CELL_ID);
                  const icelandOwnerName = icelandCell?.owner_name;
                  const ownerHasIceland = cell.owner_name && icelandOwnerName && cell.owner_name === icelandOwnerName;
                  if (!ownerHasIceland) return null;
                  if (cellIdNum === ICELAND_CELL_ID) return null; // already shown in Country bonus block above
                  return (
                    <div style={{
                      marginTop: '8px',
                      paddingTop: '8px',
                      borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      <div style={{
                        color: '#4ECDC4',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        marginBottom: '6px'
                      }}>
                        Country bonus
                      </div>
                      <div style={{ color: '#aaa', fontSize: '11px' }}>
                        {ICELAND_BONUS_TOOLTIP} (owner owns Iceland)
                      </div>
                    </div>
                  );
                })()}
                {/* Completion building bonuses (80 collected + 30 orders of required faction) – only active ones, only for countries owned by the viewer */}
                {(() => {
                  const isViewerOwned = controlledCountries.includes(cellIdNum) || controlledCountries.includes(hoveredCell);
                  if (!isViewerOwned) return null;
                  const COMPLETION_BONUS_ROWS = [
                    { key: 'lavender', name: 'Lavender Farm', image: '/images/lavender.png' },
                    { key: 'cattle', name: 'Cattle Farm', image: '/images/cattle.png' },
                    { key: 'quarry', name: 'Quarry', image: '/images/stone.png' },
                    { key: 'sewer', name: 'Sewer', image: '/images/sewers.png' },
                    { key: 'portal', name: 'Portal', image: '/images/erray.png' }
                  ];
                  const loading = completionBonusLoadingCell === cellIdNum;
                  const status = completionBonusStatus[cellIdNum] || completionBonusStatus[hoveredCell];
                  const activeRows = status ? COMPLETION_BONUS_ROWS.filter((row) => status[row.key] === true) : [];
                  if (loading && activeRows.length === 0) {
                    return (
                      <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        <div style={{ color: '#4ECDC4', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px' }}>Completion bonuses</div>
                        <div style={{ color: '#aaa', fontSize: '11px' }}>Loading…</div>
                      </div>
                    );
                  }
                  if (activeRows.length === 0) return null;
                  return (
                    <div style={{
                      marginTop: '8px',
                      paddingTop: '8px',
                      borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      <div style={{ color: '#4ECDC4', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px' }}>
                        Completion bonuses
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {activeRows.map((row) => (
                          <div key={row.key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {row.image && (
                              <img
                                src={row.image}
                                alt=""
                                style={{ width: 24, height: 24, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                            <span style={{ color: '#4CAF50', fontSize: '11px', fontWeight: 'bold' }}>{row.name}: Active</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                {/* Trading Hub protection – show when this country has been protected via Trading Hub */}
                {protectedCellId != null && cellIdNum === Number(protectedCellId) && (
                  <div style={{
                    marginTop: '6px',
                    padding: '6px 8px',
                    background: 'rgba(76, 175, 80, 0.2)',
                    borderRadius: '4px',
                    borderLeft: '3px solid #4CAF50'
                  }}>
                    <span style={{ color: '#4CAF50', fontSize: '12px', fontWeight: 'bold' }}>{t('warEffort.tradingHub.protectedByTradingHub', 'Protected by Trading Hub')}</span>
                    <span style={{ color: 'white', fontSize: '12px' }}>{t('warEffort.tradingHub.cannotBeAttacked', ' — cannot be attacked.')}</span>
                  </div>
                )}
                {/* Neutral fortifications (random fortifications on neutral countries) */}
                {(() => {
                  const nf = missionData?.neutral_fortifications;
                  const neutralData = nf && (nf[cellIdNum] || nf[String(cellIdNum)] || nf[hoveredCell] || nf[String(hoveredCell)]);
                  if (!neutralData || !neutralData.count || neutralData.count < 1) return null;
                  const hasTypes = neutralData.types && neutralData.types.length > 0;
                  const shineColor = neutralData.count === 1 ? '#87CEEB' : neutralData.count === 2 ? '#9370DB' : '#FFD700';
                  return (
                    <div style={{
                      marginTop: '8px',
                      paddingTop: '8px',
                      borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      <div style={{
                        color: shineColor,
                        fontSize: '12px',
                        fontWeight: 'bold',
                        marginBottom: hasTypes ? '6px' : 0
                      }}>
                        Neutral fortifications ({neutralData.count}){!hasTypes ? ' — types unknown' : ''}
                      </div>
                      {hasTypes && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {neutralData.types.map((ft) => {
                          const f = FORTIFICATION_TYPES.find(x => x.type === ft);
                          if (!f) return <span key={ft} style={{ color: '#aaa', fontSize: '11px' }}>{ft}</span>;
                          return (
                            <div key={ft} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                              {f.unitId && (
                                <img
                                  src={`/images/${f.unitId}.jpg`}
                                  alt={t(`warEffort.fortificationTypes.${f.type}`)}
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    objectFit: 'cover',
                                    borderRadius: '4px',
                                    flexShrink: 0
                                  }}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              )}
                              <div>
                                <div style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>{t(`warEffort.fortificationTypes.${f.type}`)}</div>
                                <div style={{ color: '#aaa', fontSize: '11px' }}>{t(`warEffort.fortificationTypes.${f.type}_desc`)}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      )}
                    </div>
                  );
                })()}
                {/* Fortification info (Fortify country) */}
                {fortification && (
                  <div style={{ 
                    marginTop: '8px', 
                    paddingTop: '8px', 
                    borderTop: '1px solid rgba(255, 255, 255, 0.2)' 
                  }}>
                    <div style={{ 
                      color: '#4ECDC4', 
                      fontSize: '12px', 
                      fontWeight: 'bold', 
                      marginBottom: '6px' 
                    }}>
                      {t('warEffort.fortificationLabel')}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {fortification.unitId && (
                        <img
                          src={`/images/${fortification.unitId}.jpg`}
                          alt={t(`warEffort.fortificationTypes.${fortification.type}`)}
                          style={{
                            width: '60px',
                            height: '60px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            border: '1px solid rgba(255, 255, 255, 0.3)'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ color: 'white', fontSize: '13px', fontWeight: 'bold' }}>
                          {fortification.type === 'eclipse_hidden'
                            ? t('warEffort.fortificationTypes.eclipse_hidden')
                            : t(`warEffort.fortificationTypes.${fortification.type}`)}
                        </div>
                        {fortification.type !== 'eclipse_hidden' && (
                        <div style={{ color: '#aaa', fontSize: '11px', marginTop: '4px' }}>
                          {t(`warEffort.fortificationTypes.${fortification.type}_desc`)}
                        </div>
                        )}
                        {fortification.type === 'defend_twice' && (() => {
                          const wins = fortificationData?.defend_twice_consecutive_wins ?? 0;
                          const statusKey = wins > 0
                            ? (isOpponentFortified
                              ? 'warEffort.defendTwice.statusYouWonOne'
                              : 'warEffort.defendTwice.statusAttackerWonOne')
                            : null;
                          return (
                            <div style={{ color: '#4ECDC4', fontSize: '12px', marginTop: '6px', fontWeight: 'bold' }}>
                              {wins}/2{statusKey != null ? ` (${t(statusKey)})` : ''}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}
                {/* Command Center boost (own 3+ neighbours) — same logic and tooltip as 1v1; viewer slot resolved for FFA/2v2 */}
                {isCommandCenterFortified && (() => {
                  const commandCenter = HQ_BUILDINGS.find(b => b.id === 'command_center');
                  const countryName = getCountryFromCellId(hoveredCell);
                  const allNeighbourCountries = countryName ? getNeighbourCountries(countryName) : [];
                  // Show only neighbouring countries the viewer controls (viewerPurpose for FFA/2v2 so slot is correct)
                  const playerPurpose = viewerPurpose ?? (isPlayer1 ? missionData?.player1_purpose : missionData?.player2_purpose);
                  const allowedFactions = playerPurpose ? PURPOSE_TO_FACTION_MAPPING[playerPurpose] || [] : [];
                  const playerControlledCellIds = new Set(
                    Object.entries(cellOwnersMap)
                      .filter(([, owner]) => owner && allowedFactions.includes(owner))
                      .map(([cellIdStr]) => Number(cellIdStr))
                  );
                  const neighbourCountries = allNeighbourCountries.filter((name) => {
                    const cellId = getCellIdFromCountry(name);
                    return cellId != null && playerControlledCellIds.has(cellId);
                  });
                  return (
                    <div style={{ 
                      marginTop: '8px', 
                      paddingTop: '8px', 
                      borderTop: '1px solid rgba(255, 255, 255, 0.2)' 
                    }}>
                      <div style={{ 
                        color: '#4ECDC4', 
                        fontSize: '12px', 
                        fontWeight: 'bold', 
                        marginBottom: '6px' 
                      }}>
                        Fortified by command center
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {commandCenter?.image && (
                          <img
                            src={commandCenter.image}
                            alt="Command Center"
                            style={{
                              width: '60px',
                              height: '60px',
                              objectFit: 'cover',
                              borderRadius: '4px'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ color: 'white', fontSize: '13px', fontWeight: 'bold' }}>
                            Command Center
                          </div>
                          <div style={{ color: '#aaa', fontSize: '11px', marginTop: '4px' }}>
                            {commandCenter?.description}
                          </div>
                          {neighbourCountries.length > 0 && (
                            <div style={{ color: '#aaa', fontSize: '11px', marginTop: '8px' }}>
                              <div style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}>Neighbouring countries:</div>
                              <ul style={{ margin: 0, paddingLeft: '16px' }}>
                                {neighbourCountries.map((name) => (
                                  <li key={name} style={{ marginBottom: '2px' }}>{name}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
                {/* Opponent's Command Center boost (opponent owns 3+ neighbours) — same tooltip as 1v1; in FFA use cell owner so correct opponent's neighbours shown */}
                {isOpponentCommandCenterFortified && !isCommandCenterFortified && (() => {
                  const commandCenter = HQ_BUILDINGS.find(b => b.id === 'command_center');
                  const countryName = getCountryFromCellId(hoveredCell);
                  const allNeighbourCountries = countryName ? getNeighbourCountries(countryName) : [];
                  // 1v1: single opponent purpose; FFA/2v2: this cell is owned by one opponent — use cell.owner_name so we show that opponent's neighbouring countries
                  const opponentAllowedFactions = cell?.owner_name
                    ? [cell.owner_name]
                    : (isPlayer1 ? PURPOSE_TO_FACTION_MAPPING[missionData?.player2_purpose] : PURPOSE_TO_FACTION_MAPPING[missionData?.player1_purpose]) || [];
                  const opponentControlledCellIds = new Set(
                    Object.entries(cellOwnersMap)
                      .filter(([, owner]) => owner && opponentAllowedFactions.includes(owner))
                      .map(([cellIdStr]) => Number(cellIdStr))
                  );
                  const neighbourCountries = allNeighbourCountries.filter((name) => {
                    const cellId = getCellIdFromCountry(name);
                    return cellId != null && opponentControlledCellIds.has(cellId);
                  });
                  return (
                    <div style={{
                      marginTop: '8px',
                      paddingTop: '8px',
                      borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      <div style={{
                        color: '#4ECDC4',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        marginBottom: '6px'
                      }}>
                        Opponent&apos;s Command Center
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {commandCenter?.image && (
                          <img
                            src={commandCenter.image}
                            alt="Command Center"
                            style={{
                              width: '60px',
                              height: '60px',
                              objectFit: 'cover',
                              borderRadius: '4px'
                            }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ color: 'white', fontSize: '13px', fontWeight: 'bold' }}>
                            Command Center
                          </div>
                          <div style={{ color: '#aaa', fontSize: '11px', marginTop: '4px' }}>
                            +1 health to defender&apos;s units when you attack this country.
                          </div>
                          {neighbourCountries.length > 0 && (
                            <div style={{ color: '#aaa', fontSize: '11px', marginTop: '8px' }}>
                              <div style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}>Opponent&apos;s neighbouring countries:</div>
                              <ul style={{ margin: 0, paddingLeft: '16px' }}>
                                {neighbourCountries.map((name) => (
                                  <li key={name} style={{ marginBottom: '2px' }}>{name}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })()}
        </div>
      )}
      
      {/* Fortification selection - brought to front with high z-index */}
      {selectedCountry && (
        <div className="fortification-selection" style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 3000, backgroundColor: 'rgba(0, 0, 0, 0.95)', padding: '30px', borderRadius: '12px', border: '2px solid rgba(255, 255, 255, 0.3)', maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto', pointerEvents: 'auto' }}>
          <h3 style={{ color: 'white', marginBottom: '20px' }}>Select Fortification for {getCountryFromCellId(selectedCountry)}</h3>
          
          {availableFortifications.length === 0 ? (
            <div className="error" style={{ color: '#ff4444', textAlign: 'center' }}>{t('warEffort.insufficientResourcesFortification')}</div>
          ) : (
            <div className="fortification-options">
              {FORTIFICATION_TYPES.map(fort => (
                <div
                  key={fort.type}
                  className={`fortification-option ${selectedFortification === fort.type ? 'selected' : ''} ${playerResources < fort.cost ? 'disabled' : ''} ${loading ? 'loading' : ''}`}
                  onClick={() => {
                    if (playerResources >= fort.cost && !loading && playerFortifiedCountries.length < 3) {
                      setSelectedFortification(fort.type);
                      // Fortify immediately
                      handleFortifyWithType(fort.type);
                    }
                  }}
                  style={{ 
                    cursor: (playerResources >= fort.cost && !loading && playerFortifiedCountries.length < 3) ? 'pointer' : 'not-allowed', 
                    pointerEvents: 'auto',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: '170px'
                  }}
                >
                  {fort.unitId && (
                    <img 
                      src={`/images/${fort.unitId}.jpg`} 
                      alt={t(`warEffort.fortificationTypes.${fort.type}`)}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        zIndex: 0
                      }}
                      onError={(e) => {
                        // Fallback if image doesn't exist
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <div style={{
                    position: 'relative',
                    zIndex: 1,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    padding: '12px',
                    background: 'linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.4) 50%, transparent 100%)'
                  }}>
                    <div className="fortification-name" style={{ 
                      textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                      marginBottom: '5px'
                    }}>{t(`warEffort.fortificationTypes.${fort.type}`)}</div>
                    <div className="fortification-cost" style={{ 
                      textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                      marginBottom: '5px'
                    }}>Cost: {fort.cost}</div>
                    <div className="fortification-description" style={{ 
                      textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)',
                      fontSize: '0.8em'
                    }}>{t(`warEffort.fortificationTypes.${fort.type}_desc`)}</div>
                    {loading && selectedFortification === fort.type && (
                      <div style={{ 
                        color: '#4a9eff', 
                        marginTop: '10px', 
                        fontWeight: 'bold',
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)'
                      }}>{t('warEffort.fortifying')}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <button
            onClick={() => {
              setSelectedCountry(null);
              setSelectedFortification(null);
              setError(null);
            }}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              background: '#555',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '8px 16px',
              cursor: 'pointer',
              pointerEvents: 'auto'
            }}
          >
            ✕ Close
          </button>
        </div>
      )}
      
      {error && (
        <div className="error-message" style={{ position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 3001, backgroundColor: 'rgba(255, 68, 68, 0.95)', color: 'white', padding: '15px 30px', borderRadius: '8px', border: '2px solid rgba(255, 255, 255, 0.3)', pointerEvents: 'auto' }}>
          {error}
        </div>
      )}
      
      {/* Attack Dialog */}
      {attackDialog.show && (
        <div className="attack-dialog-overlay" style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100vw', 
          height: '100vh', 
          backgroundColor: 'rgba(0, 0, 0, 0.8)', 
          zIndex: 4000, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          pointerEvents: 'auto'
        }}>
          <div className="attack-dialog" style={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.95)', 
            border: '2px solid rgba(255, 255, 255, 0.3)', 
            borderRadius: '12px', 
            padding: '30px', 
            maxWidth: '500px', 
            width: '90vw',
            color: 'white',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.7)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#FFD700' }}>
              Attack Fortified Country
            </h3>
            <p style={{ marginBottom: '30px', fontSize: '16px', lineHeight: '1.6' }}>
              {attackDialog.fortificationTypeHidden
                ? `This country (${attackDialog.countryName}) has been fortified.`
                : <>This country ({attackDialog.countryName}) has been fortified with <strong style={{ color: '#4ECDC4' }}>{attackDialog.fortificationType}</strong>.</>}
            </p>
            
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setAttackDialog({ show: false, countryCellId: null, fortificationType: null, countryName: null, selectedStrategyId: null })}
                disabled={loading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#555',
                  color: 'white',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  opacity: loading ? 0.5 : 1
                }}
              >
                NO
              </button>
              <button
                onClick={handleAttackOpponentFortified}
                disabled={loading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#4ECDC4',
                  color: 'white',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  opacity: loading ? 0.5 : 1
                }}
              >
                {loading ? 'Launching...' : 'YES'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Attack Other Country Dialog */}
      {attackOtherCountryDialog.show && (
        <div className="attack-dialog-overlay" style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100vw', 
          height: '100vh', 
          backgroundColor: 'rgba(0, 0, 0, 0.8)', 
          zIndex: 4000, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          pointerEvents: 'auto'
        }}>
          <div className="attack-dialog" style={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.95)', 
            border: '2px solid rgba(255, 255, 255, 0.3)', 
            borderRadius: '12px', 
            padding: '30px', 
            maxWidth: '500px', 
            width: '90vw',
            color: 'white',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.7)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#FFD700' }}>
              {attackOtherCountryDialog.isOpponentUnfortified ? t('warEffort.attackOpponentCountry') : t('warEffort.attackOtherCountry')}
            </h3>
            <p style={{ marginBottom: '30px', fontSize: '16px', lineHeight: '1.6' }}>
              This country ({attackOtherCountryDialog.countryName}) is controlled by <strong style={{ color: '#FF6B6B' }}>{attackOtherCountryDialog.ownerFaction}</strong>.
              {attackOtherCountryDialog.isOpponentUnfortified && ' It is not fortified. '}
              If you win, you will take control of this country.
            </p>
            
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setAttackOtherCountryDialog({ show: false, countryCellId: null, countryName: null, ownerFaction: null, selectedStrategyId: null, isOpponentUnfortified: false })}
                disabled={loading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#555',
                  color: 'white',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  opacity: loading ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAttackOtherCountry}
                disabled={loading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#4ECDC4',
                  color: 'white',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  opacity: loading ? 0.5 : 1
                }}
              >
                {loading ? t('warEffort.launching') : t('warEffort.mission.attack')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attack Enemy HQ Dialog (when opponent has no fortified countries left; winner wins mission) */}
      {attackEnemyHQDialog.show && (
        <div className="attack-dialog-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          zIndex: 4000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'auto'
        }}>
          <div className="attack-dialog" style={{
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '90vw',
            color: 'white',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.7)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#FFD700' }}>
              Attack Enemy Headquarters
            </h3>
            <p style={{ marginBottom: '20px', fontSize: '16px', lineHeight: '1.6' }}>
              This is the enemy HQ ({attackEnemyHQDialog.countryName}). If you win this battle, the mission is over and you win.
            </p>
            {attackEnemyHQDialog.movementCost != null ? (
              <p style={{ marginBottom: '20px', fontSize: '14px', color: '#4ECDC4' }}>
                Cost: <strong>{attackEnemyHQDialog.movementCost} Energy</strong> (movement + 400)
              </p>
            ) : (
              <p style={{ marginBottom: '20px', fontSize: '14px', color: '#aaa' }}>
                Cost: Loading…
              </p>
            )}
            {(() => {
              const canAttackThisHQ = missionData?.mission_mode === 'ffa'
                ? (Array.isArray(enemyHqAttackInfo?.attackableHQs) && enemyHqAttackInfo.attackableHQs.some(h => h.hqCellId === attackEnemyHQDialog.hqCellId))
                : (enemyHqAttackInfo?.canAttack && enemyHqAttackInfo?.hqCellId === attackEnemyHQDialog.hqCellId);
              return !canAttackThisHQ && (
                <div style={{ marginBottom: '20px', fontSize: '14px', color: '#ff6b6b' }}>
                  <p style={{ marginBottom: '8px' }}>Enemy HQ is only attackable when the opponent has no fortified countries left.</p>
                  {enemyHqAttackInfo?.opponentFortifiedCountryNames?.length > 0 ? (
                    <p style={{ margin: 0 }}>
                      Countries the game still considers fortified: <strong>{enemyHqAttackInfo.opponentFortifiedCountryNames.join(', ')}</strong>
                    </p>
                  ) : enemyHqAttackInfo?.opponentFortifiedCellIds?.length > 0 ? (
                    <p style={{ margin: 0 }}>
                      Countries the game still considers fortified (cell IDs): <strong>{enemyHqAttackInfo.opponentFortifiedCellIds.join(', ')}</strong>
                    </p>
                  ) : null}
                </div>
              );
            })()}
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setAttackEnemyHQDialog({ show: false, countryName: null, hqCellId: null, movementCost: null, defenderUserId: null })}
                disabled={loading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#555',
                  color: 'white',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  opacity: loading ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAttackEnemyHQ}
                disabled={loading || !(missionData?.mission_mode === 'ffa'
                  ? (Array.isArray(enemyHqAttackInfo?.attackableHQs) && enemyHqAttackInfo.attackableHQs.some(h => h.hqCellId === attackEnemyHQDialog.hqCellId))
                  : (enemyHqAttackInfo?.canAttack && enemyHqAttackInfo?.hqCellId === attackEnemyHQDialog.hqCellId))}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#4ECDC4',
                  color: 'white',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  opacity: loading ? 0.5 : 1
                }}
              >
                {loading ? t('warEffort.launching') : (missionData?.mission_mode === 'ffa'
                  ? (!(Array.isArray(enemyHqAttackInfo?.attackableHQs) && enemyHqAttackInfo.attackableHQs.some(h => h.hqCellId === attackEnemyHQDialog.hqCellId)) ? t('warEffort.notAttackableYet') : t('warEffort.attackHq'))
                  : (!enemyHqAttackInfo?.canAttack ? t('warEffort.notAttackableYet') : t('warEffort.attackHq')))}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Defense Strategy Dialog: set strategy used when this country is attacked */}
      {defenseStrategyDialog.show && (
        <div className="attack-dialog-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          zIndex: 4000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'auto'
        }}>
          <div style={{
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '90vw',
            color: 'white',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.7)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '8px', color: '#3498db' }}>
              Defense strategy {defenseStrategyDialog.countryName ? `– ${defenseStrategyDialog.countryName}` : ''}
            </h3>
            <p style={{ marginBottom: '20px', fontSize: '14px', color: '#aaa' }}>
              Choose the strategy the defending bot will use when this country is attacked. Only strategies matching your purpose are shown.
            </p>
            {defenseStrategyDialog.loading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#aaa' }}>Loading...</div>
            ) : (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#CCCCCC' }}>
                    Strategy:
                  </label>
                  {userStrategies.length === 0 ? (
                    <div style={{ color: '#ff4444', fontSize: '14px' }}>No strategies available for this purpose</div>
                  ) : (
                    <CustomDropdown
                      options={userStrategies.map(s => ({ value: s.id, label: s.name }))}
                      value={defenseStrategyDialog.selectedStrategyId}
                      onChange={(value) => setDefenseStrategyDialog(prev => ({ ...prev, selectedStrategyId: value }))}
                      placeholder={t('warEffort.selectStrategyPlaceholderShort')}
                    />
                  )}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={defenseStrategyDialog.useEverywhere}
                    onChange={(e) => setDefenseStrategyDialog(prev => ({ ...prev, useEverywhere: e.target.checked }))}
                  />
                  <span style={{ fontSize: '14px' }}>Use this strategy everywhere (all countries and future countries)</span>
                </label>
                <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setDefenseStrategyDialog(prev => ({ ...prev, show: false }))}
                    disabled={defenseStrategyDialog.saving}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#555',
                      color: 'white',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      if (!defenseStrategyDialog.selectedStrategyId || !defenseStrategyDialog.cellId || !missionData?.id) return;
                      setDefenseStrategyDialog(prev => ({ ...prev, saving: true }));
                      try {
                        const token = localStorage.getItem('token');
                        const res = await fetch(`${API_URL}/war-effort/defense-strategy`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                          },
                          body: JSON.stringify({
                            missionId: missionData.id,
                            countryCellId: defenseStrategyDialog.cellId,
                            strategyId: defenseStrategyDialog.selectedStrategyId,
                            useEverywhere: defenseStrategyDialog.useEverywhere
                          })
                        });
                        if (res.ok) {
                          setDefenseStrategyDialog(prev => ({ ...prev, show: false, saving: false }));
                          setError(null);
                        } else {
                          const data = await res.json().catch(() => ({}));
                          setError(data.error || t('warEffort.failedToSetDefenseStrategy'));
                          setDefenseStrategyDialog(prev => ({ ...prev, saving: false }));
                        }
                      } catch (e) {
                        setError(t('warEffort.failedToSetDefenseStrategy'));
                        setDefenseStrategyDialog(prev => ({ ...prev, saving: false }));
                      }
                    }}
                    disabled={defenseStrategyDialog.saving || !defenseStrategyDialog.selectedStrategyId || userStrategies.length === 0}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#3498db',
                      color: 'white',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: '8px',
                      cursor: defenseStrategyDialog.saving ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      opacity: defenseStrategyDialog.saving ? 0.7 : 1
                    }}
                  >
                    {defenseStrategyDialog.saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Trading Hub (Madagascar / The Sentients) – card-based offers with images */}
      {tradingHubOpen && (
        <div className="trading-hub-overlay">
          <div className={`trading-hub-dialog${tradingHubStatus?.tradingBlocked && !tradingHubLoading ? ' trading-hub-dialog--blocked' : ''}`}>
            {!(tradingHubStatus?.tradingBlocked && !tradingHubLoading) && (
              <div className="trading-hub-header">
                <h3>{t('warEffort.tradingHub.title', 'Trading Hub – Madagascar')}</h3>
                <p className="trading-hub-subtitle">{t('warEffort.tradingHub.subtitle', 'The Sentients offer deals for energy.')}</p>
              </div>
            )}
            {tradingHubLoading ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#aaa' }}>{t('warEffort.tradingHub.loading', 'Loading...')}</div>
            ) : tradingHubStatus?.tradingBlocked ? (
              <div
                className="trading-hub-blocked"
                style={{ backgroundImage: resolvePublicAssetBackgroundUrl('/images/shop_closed.jpg') }}
              >
                <p className="trading-hub-blocked__message">
                  {tradingHubStatus.tradingBlockedReason || t('warEffort.tradingHub.tradingBlockedDefault', 'Madagascar will not trade with a war criminal.')}
                </p>
                <button className="trading-hub-blocked__close" onClick={() => setTradingHubOpen(false)}>{t('warEffort.tradingHub.close', 'Close')}</button>
              </div>
            ) : tradingHubStatus ? (
              <>
                <div className="trading-hub-energy">
                  <strong>{t('warEffort.tradingHub.yourEnergy', 'Your energy:')}</strong> {tradingHubStatus.energyBalance}
                  {typeof tradingHubStatus.billyBucksBalance === 'number' && (
                    <span style={{ marginLeft: '16px' }}><strong>{t('warEffort.tradingHub.billyBucks', 'Billy Bucks:')}</strong> {tradingHubStatus.billyBucksBalance}</span>
                  )}
                </div>
                <div className="trading-hub-offers">
                  {/* Restock button: free restock countdown + Restock now (n Energy) inside same button */}
                  <div className="trading-hub-restock-wrap" style={{ marginBottom: '16px' }}>
                    <button
                      type="button"
                      className="trading-hub-restock-btn"
                      disabled={(tradingHubStatus.energyBalance ?? 0) < (tradingHubStatus.restockCost ?? 100)}
                      onClick={async () => {
                        try {
                          const res = await fetch(`${API_URL}/war-effort/trading-hub/restock`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
                            body: JSON.stringify({ missionId: missionData.id })
                          });
                          const data = await res.json();
                          if (res.ok) {
                            setTradingHubStatus(prev => prev ? { ...prev, energyBalance: data.energyBalance, shopSlots: data.shopSlots, restockCount: data.restockCount, restockCost: data.restockCost, nextAutoRestockAtMs: data.nextAutoRestockAtMs ?? prev.nextAutoRestockAtMs } : null);
                            if (playerResource) setPlayerResource(prev => prev ? { ...prev, amount: data.energyBalance } : null);
                            setShopSlotCellId({});
                            playSoundEffect('/sounds/restock.mp3');
                          } else {
                            setError(data.error || t('warEffort.tradingHub.restockFailed', 'Restock failed'));
                          }
                        } catch (e) {
                          setError(e.message || t('warEffort.tradingHub.restockFailed', 'Restock failed'));
                        }
                      }}
                    >
                      <img src={resolvePublicAssetUrl('/images/restock_button.png')} alt="" aria-hidden />
                      <span className="trading-hub-restock-btn__labels" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                        {tradingHubStatus.nextAutoRestockAtMs != null && (
                          <span className="trading-hub-auto-restock-countdown" style={{ fontSize: '14px', color: '#FFD700' }}>
                            {t('warEffort.tradingHub.freeRestockIn', {
                              mm: Math.floor(Math.max(0, (tradingHubStatus.nextAutoRestockAtMs - (shopCountdownTick ?? Date.now())) / 1000) / 60),
                              ss: String(Math.max(0, Math.floor((tradingHubStatus.nextAutoRestockAtMs - (shopCountdownTick ?? Date.now())) / 1000) % 60)).padStart(2, '0'),
                              defaultValue: 'Free restock in {{mm}}:{{ss}}'
                            })}
                          </span>
                        )}
                        <span className="trading-hub-restock-btn__label">{t('warEffort.tradingHub.restockNow', { cost: tradingHubStatus.restockCost ?? 100, defaultValue: 'Restock now ({{cost}} Energy)' })}</span>
                      </span>
                    </button>
                  </div>
                  {/* Unified 3-slot shop (catalog 0–17). Sold slots show "Sold" until next global restock; no per-slot countdown. */}
                  {(Array.isArray(tradingHubStatus.shopSlots) ? tradingHubStatus.shopSlots : []).map((item, slotIndex) => {
                    if (item.countdownEndsAt != null) {
                      return (
                        <div key={`shop-slot-${slotIndex}-sold`} className="trading-hub-offer-card trading-hub-offer-card--sold">
                          <div className="trading-hub-sold-strike" aria-hidden />
                          <div className="trading-hub-offer-card__body trading-hub-offer-card__body--sold">
                            <span className="trading-hub-sold-word">{t('warEffort.tradingHub.shop.sold', 'Sold')}</span>
                            <p className="trading-hub-offer-card__description trading-hub-offer-card__description--sold">
                              {t('warEffort.tradingHub.shop.newItemAtNextRestock', 'New item at next restock')}
                            </p>
                          </div>
                        </div>
                      );
                    }
                    const needsCellId = item.id === 12 || item.id === 13 || item.id === 2 || item.id === 6 || item.id === 19;
                    const selectedCellId = (shopSlotCellId || {})[slotIndex] ?? null;
                    const costLabel = item.costCurrency === 'free'
                      ? t('warEffort.tradingHub.energyFreeOncePerHour', 'Free')
                      : item.costCurrency === 'billyBucks'
                        ? t('warEffort.tradingHub.billyBucksCost', { cost: item.cost, defaultValue: '{{cost}} Billy Bucks' })
                        : t('warEffort.tradingHub.energyCost', { cost: item.cost, defaultValue: '{{cost}} energy' });
                    const canAfford = item.costCurrency === 'free'
                      ? (tradingHubStatus.remainingCooldownSeconds ?? 0) === 0
                      : item.costCurrency === 'billyBucks'
                        ? (tradingHubStatus.billyBucksBalance ?? 0) >= (item.cost || 0)
                        : (tradingHubStatus.energyBalance ?? 0) >= (item.cost || 0);
                    const isClaimEnergy = item.id === 11;
                    const isProtect = item.id === 12;
                    const isSubvert = item.id === 13;
                    const subvertDisabled = isSubvert && !tradingHubStatus.canSubvert;
                    const isDisabled = !canAfford || (needsCellId && !selectedCellId) || (item.id === 14 && tradingHubStatus.hasBoughtBoosterBundleForMission) || subvertDisabled;
                    const targetOptions = item.id === 12
                      ? (tradingHubStatus.protectableCells || [])
                      : item.id === 13
                        ? (tradingHubStatus.subvertableCells || [])
                        : item.id === 2
                          ? (tradingHubStatus.structuralExposureTargetCells || [])
                          : item.id === 6
                            ? (tradingHubStatus.reconquerCooldownCells || [])
                            : item.id === 19
                              ? (tradingHubStatus.tectonicReconcilementCells || [])
                              : [];
                    const showDropdown = (isProtect || isSubvert) || (needsCellId && targetOptions.length > 0);
                    return (
                      <div key={`shop-slot-${slotIndex}-${item.id}`} className="trading-hub-offer-card">
                        <div className="trading-hub-offer-card__image-wrap">
                          <img src={`/images/${item.image || 'shop_energy.jpg'}`} alt="" className="trading-hub-offer-card__image" />
                          <div className="trading-hub-offer-card__price-overlay">{costLabel}</div>
                        </div>
                        <div className="trading-hub-offer-card__body">
                          <p className="trading-hub-offer-card__title">{item.id === 11 ? t('warEffort.tradingHub.askForEnergy', { amount: 200 }) : t(`warEffort.${item.nameKey}`)}</p>
                          <p className="trading-hub-offer-card__description">{t(`warEffort.${item.descriptionKey || item.nameKey}`)}</p>
                          {isClaimEnergy && (energyCooldownDisplaySeconds ?? tradingHubStatus.remainingCooldownSeconds ?? 0) > 0 && (
                            <p style={{ fontSize: '12px', color: '#FFD700', margin: 0 }}>
                              {t('warEffort.tradingHub.nextClaimIn', {
                                mm: Math.floor((energyCooldownDisplaySeconds ?? tradingHubStatus.remainingCooldownSeconds) / 60),
                                ss: String((energyCooldownDisplaySeconds ?? tradingHubStatus.remainingCooldownSeconds) % 60).padStart(2, '0'),
                                defaultValue: 'Next claim in: {{mm}}:{{ss}}'
                              })}
                            </p>
                          )}
                          {isProtect && tradingHubStatus.protectedCellId != null && (
                            <p style={{ fontSize: '12px', color: '#4ECDC4', margin: 0 }}>{t('warEffort.tradingHub.currentlyProtected', { country: getCountryFromCellId(tradingHubStatus.protectedCellId), defaultValue: 'Currently protected: {{country}}' })}</p>
                          )}
                          {isSubvert && tradingHubStatus.subvertBlockedReason && (
                            <p style={{ fontSize: '12px', color: '#FFD700', margin: 0 }}>{tradingHubStatus.subvertBlockedReason}</p>
                          )}
                          {item.id === 14 && (
                            <div style={{ marginBottom: 6 }}>
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    const res = await fetch(`${API_URL}/bank/buy-lemonade`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` }
                                    });
                                    const text = await res.text();
                                    let data = {};
                                    try { data = text ? JSON.parse(text) : {}; } catch {
                                      setError(t('warEffort.tradingHub.serverInvalidResponse', 'Server returned an invalid response. Is the backend running?'));
                                      return;
                                    }
                                    if (res.ok) {
                                      setTradingHubStatus(prev => prev ? { ...prev, billyBucksBalance: data.newBalance } : null);
                                      playSoundEffect('/sounds/drink.mp3');
                                      if (data.achievementUnlocked) setAchievementUnlocked(data.achievementUnlocked);
                                    } else {
                                      setError(data.error || t('warEffort.tradingHub.purchaseFailed', 'Purchase failed'));
                                    }
                                  } catch (e) {
                                    setError(e.message || t('warEffort.tradingHub.purchaseFailed', 'Purchase failed'));
                                  }
                                }}
                                style={{ padding: '4px 10px', fontSize: '0.72rem', cursor: 'pointer', background: '#4ECDC4', color: '#000', border: 'none', borderRadius: 4, fontWeight: 'bold', width: '100%' }}
                              >
                                {t('warEffort.tradingHub.buyLemonade', 'Buy Lemonade for 10 Billy Bucks')}
                              </button>
                            </div>
                          )}
                          {showDropdown && (
                            <div style={{ opacity: subvertDisabled ? 0.6 : 1, pointerEvents: subvertDisabled ? 'none' : 'auto' }}>
                              <CustomDropdown
                                placeholder={
                                  item.id === 12
                                    ? t('warEffort.tradingHub.selectCountryProtect', 'Select country to protect')
                                    : item.id === 13
                                      ? t('warEffort.tradingHub.selectCountrySubvert', 'Select country to subvert')
                                      : item.id === 2
                                        ? t('warEffort.tradingHub.shop.selectCountryReveal', 'Select country to reveal')
                                        : item.id === 6
                                          ? t('warEffort.tradingHub.shop.selectCountrySpeedpass', 'Select country (reconquer cooldown)')
                                          : t('warEffort.tradingHub.shop.selectCountryReconcile', 'Select country to restore')
                                }
                                options={[{ value: '', label: '—' }, ...targetOptions.map(c => ({ value: String(c.cellId), label: c.countryName || `Cell ${c.cellId}` }))]}
                                value={selectedCellId != null ? String(selectedCellId) : ''}
                                onChange={(val) => setShopSlotCellId(prev => ({ ...prev, [slotIndex]: val ? parseInt(val, 10) : null }))}
                              />
                            </div>
                          )}
                          <div className="trading-hub-offer-card__actions">
                            <button
                              disabled={isDisabled}
                              onClick={async () => {
                                try {
                                  const body = { missionId: missionData.id, itemId: item.id };
                                  if (needsCellId && selectedCellId != null) body.cellId = selectedCellId;
                                  const res = await fetch(`${API_URL}/war-effort/trading-hub/buy-shop-item`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
                                    body: JSON.stringify(body)
                                  });
                                  const data = await res.json();
                                  if (res.ok) {
                                    setTradingHubStatus(prev => prev ? {
                                      ...prev,
                                      energyBalance: data.energyBalance ?? prev.energyBalance,
                                      billyBucksBalance: data.billyBucksBalance ?? prev.billyBucksBalance,
                                      remainingCooldownSeconds: data.remainingCooldownSeconds ?? prev.remainingCooldownSeconds,
                                      protectedCellId: data.protectedCellId ?? prev.protectedCellId,
                                      hasBoughtBoosterBundleForMission: data.hasBoughtBoosterBundleForMission ?? prev.hasBoughtBoosterBundleForMission,
                                      shopSlots: Array.isArray(data.shopSlots) ? data.shopSlots : prev.shopSlots
                                    } : null);
                                    if (data.energyBalance != null && playerResource) setPlayerResource(prev => prev ? { ...prev, amount: data.energyBalance } : null);
                                    if (data.protectedCellId != null) setProtectedCellId(data.protectedCellId);
                                    setShopSlotCellId(prev => ({ ...prev, [slotIndex]: null }));
                                    if (data.orders) setTradingHubBoosterOrders(data.orders);
                                    if (Array.isArray(data.activeEffects)) setActiveShopEffects(data.activeEffects);
                                    else if (missionData?.id) {
                                      fetch(`${API_URL}/war-effort/mission-status/${missionData.id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
                                        .then(r => r.ok ? r.json() : null)
                                        .then(status => { if (status && Array.isArray(status.activeEffects)) setActiveShopEffects(status.activeEffects); })
                                        .catch(() => {});
                                    }
                                    if (data.gameId) {
                                      setTradingHubOpen(false);
                                      fetchControlledCountries();
                                      const statusRes = await fetch(`${API_URL}/war-effort/status`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                                      if (statusRes.ok) setWarEffortData(await statusRes.json());
                                      navigate('/after-battle', {
                                        replace: true,
                                        state: {
                                          gameId: data.gameId,
                                          result: 'win',
                                          isWarEffort: true,
                                          gameState: { warEffortMissionId: missionData.id },
                                          missionId: missionData.id
                                        }
                                      });
                                    }
                                    if (data.granted != null) setEnergyCooldownDisplaySeconds(3600);
                                    if (data.gambleWonItem) setGambleWonItem(data.gambleWonItem);
                                    playSoundEffect('/sounds/buy.mp3');
                                  } else {
                                    setError(data.error || t('warEffort.tradingHub.purchaseFailed', 'Purchase failed'));
                                  }
                                } catch (e) {
                                  setError(e.message || t('warEffort.tradingHub.purchaseFailed', 'Purchase failed'));
                                }
                              }}
                            >
                              {isClaimEnergy ? t('warEffort.tradingHub.claimUpToEnergy', { amount: tradingHubStatus.claimAmount ?? 200, defaultValue: 'Claim up to {{amount}} energy' }) : item.id === 14 && tradingHubStatus.hasBoughtBoosterBundleForMission ? t('warEffort.tradingHub.alreadyPurchased', 'Already purchased') : t('warEffort.tradingHub.buy', 'Buy')}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="trading-hub-close-wrap">
                  <button onClick={() => { setTradingHubOpen(false); setTradingHubStatus(null); setTradingHubBoosterOrders(null); setGambleWonItem(null); }}>{t('warEffort.tradingHub.close', 'Close')}</button>
                </div>
              </>
            ) : (
              <div style={{ padding: '24px', textAlign: 'center', color: '#aaa' }}>
                <p>{t('warEffort.tradingHub.unableToLoad', 'Unable to load trading hub.')}</p>
                {error && <p style={{ marginTop: 12, color: '#e74c3c', fontSize: '14px' }}>{error}</p>}
                <div className="trading-hub-close-wrap" style={{ marginTop: 16 }}>
                  <button onClick={() => { setTradingHubOpen(false); setTradingHubStatus(null); setError(null); }}>{t('warEffort.tradingHub.close', 'Close')}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Gamble result overlay: show what the player won when they buy Gamble */}
      {gambleWonItem && typeof document !== 'undefined' && createPortal(
        <div
          className="trading-hub-gamble-result-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.75)',
            zIndex: 10001
          }}
        >
          <div
            className="trading-hub-gamble-result-panel"
            style={{
              background: 'linear-gradient(180deg, #1a2332 0%, #0d1520 100%)',
              borderRadius: 12,
              padding: '24px 32px',
              maxWidth: 360,
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              border: '1px solid rgba(78, 205, 196, 0.3)'
            }}
          >
            <p style={{ margin: '0 0 16px', fontSize: '1.25rem', color: '#4ECDC4' }}>
              {t('warEffort.tradingHub.gambleYouWon', 'You won!')}
            </p>
            <div style={{ marginBottom: 16 }}>
              <img
                src={`/images/${gambleWonItem.image || 'shop_energy.jpg'}`}
                alt=""
                style={{ width: 120, height: 120, objectFit: 'contain', borderRadius: 8 }}
              />
            </div>
            <p style={{ margin: 0, fontSize: '1rem', color: '#eee', fontWeight: 600 }}>
              {t(`warEffort.${gambleWonItem.nameKey}`)}
            </p>
            {gambleWonItem.descriptionKey && (
              <p style={{ margin: '12px 0 0', fontSize: '0.9rem', color: '#aaa', lineHeight: 1.4 }}>
                {t(`warEffort.${gambleWonItem.descriptionKey}`)}
              </p>
            )}
            <button
              onClick={() => setGambleWonItem(null)}
              style={{
                marginTop: 20,
                padding: '10px 24px',
                fontSize: '1rem',
                cursor: 'pointer',
                background: '#4ECDC4',
                color: '#0d1520',
                border: 'none',
                borderRadius: 8,
                fontWeight: 600
              }}
            >
              {t('warEffort.tradingHub.close', 'Close')}
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Booster bundle reward overlay – on top of existing page (backdrop + centered panel) */}
      {tradingHubBoosterOrders && tradingHubBoosterOrders.length > 0 && typeof document !== 'undefined' && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.7)',
            zIndex: 10000
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              color: '#fff',
              padding: 24,
              background: 'linear-gradient(180deg, #1a1a2e 0%, #0a0a12 100%)',
              borderRadius: 12,
              border: '1px solid rgba(255,255,255,0.15)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
            }}
          >
            <h2 style={{ marginBottom: 8, fontSize: '1.5rem' }}>{t('warEffort.tradingHub.boosterOverlayTitle', 'Booster bundle')}</h2>
            <p style={{ marginBottom: 16, color: '#aaa', fontSize: '0.9rem' }}>{t('warEffort.tradingHub.boosterOverlaySubtitle', 'Orders added to your attack strategy')}</p>
            <div
              className="war-effort-mission-over-rewards-grid"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 40,
                justifyContent: 'center',
                marginBottom: 20
              }}
            >
              {tradingHubBoosterOrders.map((order, index) => (
                <WarEffortMissionOverRewardCard key={order.id || index} reward={{ type: 'order', data: order }} index={index} t={t} />
              ))}
            </div>
            <button
              type="button"
              onClick={() => setTradingHubBoosterOrders(null)}
              style={{
                padding: '12px 24px',
                fontSize: '1rem',
                fontWeight: 'bold',
                color: '#fff',
                backgroundColor: '#4ECDC4',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                marginTop: 5
              }}
            >
              {t('warEffort.continue', 'Continue')}
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Dev: Neutral fortified countries popup */}
      {process.env.NODE_ENV === 'development' && showNeutralFortifiedPopup && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="neutral-fortified-title"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.7)',
            padding: 24
          }}
          onClick={(e) => e.target === e.currentTarget && setShowNeutralFortifiedPopup(false)}
        >
          <div
            style={{
              background: 'linear-gradient(180deg, #1a1a2e 0%, #0a0a12 100%)',
              border: '2px solid #4ECDC4',
              borderRadius: 12,
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              maxWidth: 420,
              width: '100%',
              maxHeight: '80vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="neutral-fortified-title" style={{ margin: 0, padding: '16px 20px', color: '#4ECDC4', fontSize: '1.1rem', borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
              Neutral fortified countries
            </h2>
            <div style={{ padding: '12px 20px', color: '#ccc', fontSize: '14px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              Total: {Object.keys(missionData?.neutral_fortifications || {}).length} countries
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '12px 20px 20px' }}>
              {(() => {
                const raw = missionData?.neutral_fortifications;
                if (!raw || typeof raw !== 'object') {
                  return <div style={{ color: '#888', fontSize: '13px' }}>No neutral fortifications data.</div>;
                }
                const entries = Object.entries(raw)
                  .map(([cellIdStr, data]) => ({
                    cellId: Number(cellIdStr),
                    count: data?.count ?? 0,
                    types: data?.types
                  }))
                  .filter((e) => !isNaN(e.cellId) && e.count >= 1)
                  .sort((a, b) => getCountryFromCellId(a.cellId).localeCompare(getCountryFromCellId(b.cellId)));
                return (
                  <ul style={{ margin: 0, paddingLeft: 20, color: '#ddd', fontSize: '13px', lineHeight: 1.6 }}>
                    {entries.map(({ cellId, count }) => (
                      <li key={cellId}>
                        {getCountryFromCellId(cellId)} — {count} fortification{count !== 1 ? 's' : ''}
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <button
                type="button"
                onClick={() => setShowNeutralFortifiedPopup(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#4ECDC4',
                  color: '#1a1a2e',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Dev Mode Panel (only for player1) */}
      {isDevUser && (
        <div className="dev-mode-panel" style={{
          position: 'fixed',
          bottom: '20px',
          left: '44px',
          zIndex: 5000,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px'
        }}>
          {/* Toggle Dev Mode Button */}
          <button
            onClick={() => {
              setDevModeActive(!devModeActive);
              if (devModeActive) {
                setDevSelectedPurpose(null);
                setDevShowFortifications(false);
              }
            }}
            className="dev-mode-toggle-btn"
            style={{
              padding: '10px 16px',
              backgroundColor: devModeActive ? '#ff6b35' : '#444',
              color: 'white',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)'
            }}
          >
            {devModeActive ? 'DEV MODE ON' : 'DEV MODE'}
          </button>
          
          {/* Dev Mode Controls */}
          {devModeActive && (
            <div className="dev-mode-controls" style={{
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              border: '2px solid #ff6b35',
              borderRadius: '8px',
              padding: '15px',
              minWidth: '250px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.7)'
            }}>
              <div style={{ marginBottom: '12px', color: '#ff6b35', fontWeight: 'bold', fontSize: '14px' }}>
                World Map Editor
              </div>

              {/* Bot status (1v1 simulated or FFA bot) */}
              {devHasBot && (
                <div style={{
                  marginBottom: '12px',
                  padding: '8px 10px',
                  backgroundColor: 'rgba(78, 205, 196, 0.15)',
                  border: '1px solid rgba(78, 205, 196, 0.5)',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#ccc'
                }}>
                  <div style={{ color: '#4ECDC4', fontWeight: 'bold', marginBottom: '4px' }}>Bot status</div>
                  <div>Energy: {devBotEnergy != null ? devBotEnergy : '—'}</div>
                  <div>Attacking: {devBotCurrentAttack?.countryName ?? '—'}</div>
                </div>
              )}
              
              {/* Pending Changes Indicator */}
              {Object.keys(devPendingOwners).length > 0 && (
                <div style={{ 
                  marginBottom: '12px', 
                  padding: '6px 10px', 
                  backgroundColor: 'rgba(231, 76, 60, 0.2)', 
                  border: '1px solid #e74c3c',
                  borderRadius: '4px',
                  fontSize: '11px',
                  color: '#e74c3c'
                }}>
                  {Object.keys(devPendingOwners).length} unsaved changes
                </div>
              )}

              {/* Purpose Selector */}
              <div style={{ marginBottom: '15px' }}>
                <div style={{ color: '#aaa', fontSize: '11px', marginBottom: '6px' }}>Click country to assign purpose:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {['Consume', 'Eradicate', 'Colonize', 'Protect', 'Survive'].map(purpose => {
                    // Use actual faction colors from factionColors mapping
                    const purposeColors = {
                      'Consume': '#8B0000',      // Asrah - dark red
                      'Eradicate': '#4B0082',    // Exolot Dominion - indigo/purple
                      'Colonize': '#3A3A3A',     // League of Shadows - dark gray (brightened for visibility)
                      'Protect': '#F4A460',      // Ra's Children - sandy brown
                      'Survive': '#556B2F'       // Remnants of Humanity - olive green
                    };
                    return (
                      <button
                        key={purpose}
                        onClick={() => setDevSelectedPurpose(devSelectedPurpose === purpose ? null : purpose)}
                        disabled={devLoading}
                        style={{
                          padding: '6px 10px',
                          backgroundColor: devSelectedPurpose === purpose ? purposeColors[purpose] : 'rgba(255,255,255,0.1)',
                          color: 'white',
                          border: `2px solid ${purposeColors[purpose]}`,
                          borderRadius: '4px',
                          cursor: devLoading ? 'not-allowed' : 'pointer',
                          fontSize: '11px',
                          fontWeight: devSelectedPurpose === purpose ? 'bold' : 'normal',
                          opacity: devLoading ? 0.5 : 1
                        }}
                      >
                        {purpose}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setDevSelectedPurpose('clear')}
                    disabled={devLoading}
                    style={{
                      padding: '6px 10px',
                      backgroundColor: devSelectedPurpose === 'clear' ? '#555' : 'rgba(255,255,255,0.1)',
                      color: 'white',
                      border: '2px solid #555',
                      borderRadius: '4px',
                      cursor: devLoading ? 'not-allowed' : 'pointer',
                      fontSize: '11px',
                      fontWeight: devSelectedPurpose === 'clear' ? 'bold' : 'normal',
                      opacity: devLoading ? 0.5 : 1
                    }}
                  >
                    Clear
                  </button>
                </div>
                {devSelectedPurpose && (
                  <div style={{ color: '#4ECDC4', fontSize: '11px', marginTop: '6px' }}>
                    Mode: {devSelectedPurpose === 'clear' ? 'Clear ownership' : `Assign to ${devSelectedPurpose}`}
                  </div>
                )}
              </div>
              
              {/* Dev Statistics */}
              {devStatistics && (
                <div style={{ 
                  marginBottom: '15px', 
                  padding: '10px', 
                  backgroundColor: 'rgba(255,255,255,0.05)', 
                  borderRadius: '6px',
                  fontSize: '11px'
                }}>
                  <div style={{ color: '#aaa', marginBottom: '8px', fontWeight: 'bold' }}>
                    Statistics ({devStatistics.total} countries)
                  </div>
                  {['Consume', 'Eradicate', 'Colonize', 'Protect', 'Survive'].map(purpose => {
                    const stats = devStatistics.purposes[purpose];
                    // Use actual faction colors from factionColors mapping
                    const purposeColors = {
                      'Consume': '#8B0000',      // Asrah - dark red
                      'Eradicate': '#4B0082',    // Exolot Dominion - indigo/purple
                      'Colonize': '#6A6A6A',     // League of Shadows - gray (brightened for text visibility)
                      'Protect': '#F4A460',      // Ra's Children - sandy brown
                      'Survive': '#7A9B4F'       // Remnants of Humanity - olive green (brightened for text)
                    };
                    return (
                      <div key={purpose} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '4px',
                        padding: '3px 6px',
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        borderRadius: '3px',
                        borderLeft: `3px solid ${purposeColors[purpose]}`
                      }}>
                        <span style={{ color: purposeColors[purpose], fontWeight: 'bold' }}>
                          {purpose}
                        </span>
                        <span style={{ color: '#ccc' }}>
                          {stats.count} 
                          <span style={{ color: '#777', marginLeft: '6px', fontSize: '10px' }}>
                            HQ: {stats.hqCountryName || '?'}
                            {stats.ownsHq ? ' ✓' : ' ✗'}
                          </span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Show Fortifications Toggle */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#ccc', fontSize: '12px' }}>
                  <input
                    type="checkbox"
                    checked={devShowFortifications}
                    onChange={(e) => setDevShowFortifications(e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Show Command Center Fortifications
                </label>
                {devShowFortifications && devCommandCenterCells.allCells.length > 0 && (
                  <div style={{ color: '#FFD700', fontSize: '10px', marginTop: '4px' }}>
                    {devCommandCenterCells.allCells.length} countries eligible
                  </div>
                )}
              </div>

              {/* Show neutral fortified countries (dev) */}
              {process.env.NODE_ENV === 'development' && (
                <div style={{ marginBottom: '15px' }}>
                  <button
                    type="button"
                    onClick={() => setShowNeutralFortifiedPopup(true)}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: 'rgba(78, 205, 196, 0.2)',
                      color: '#4ECDC4',
                      border: '1px solid #4ECDC4',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}
                  >
                    Show neutral fortified countries
                  </button>
                </div>
              )}
              
              {/* Simulate opponent (only when mission has no player2) */}
              {!missionData?.player2_id && (
                <div style={{ marginBottom: '12px' }}>
                  <button
                    onClick={handleSimulateOpponent}
                    disabled={devSimulateOpponentLoading}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: devSimulateOpponentLoading ? '#555' : '#27ae60',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: devSimulateOpponentLoading ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      opacity: devSimulateOpponentLoading ? 0.5 : 1,
                      width: '100%'
                    }}
                  >
                    {devSimulateOpponentLoading ? 'Simulating…' : 'Simulate opponent'}
                  </button>
                  <div style={{ color: '#888', fontSize: '10px', marginTop: '4px' }}>
                    Joins as player2 and fortifies 3 random owned countries
                  </div>
                </div>
              )}
              {/* End cease fire (dev only, when full or opponent-fortified cease fire is active) */}
              {(isCeaseFireActive || isOpponentFortifiedCeaseFireActive || isConquestAttackerCeaseFireActive) && (
                <div style={{ marginBottom: '12px' }}>
                  <button
                    onClick={handleEndCeaseFire}
                    disabled={devEndCeaseFireLoading}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: devEndCeaseFireLoading ? '#555' : '#e67e22',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: devEndCeaseFireLoading ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      opacity: devEndCeaseFireLoading ? 0.5 : 1,
                      width: '100%'
                    }}
                  >
                    {devEndCeaseFireLoading ? 'Ending…' : 'End cease fire'}
                  </button>
                </div>
              )}

              {/* Reset energy to 1000 (dev only) */}
              {missionData?.id && (
                <div style={{ marginBottom: '12px' }}>
                  <button
                    onClick={handleDevResetEnergy}
                    disabled={devResetEnergyLoading}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: devResetEnergyLoading ? '#555' : '#4ECDC4',
                      color: '#000',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: devResetEnergyLoading ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      opacity: devResetEnergyLoading ? 0.5 : 1,
                      width: '100%'
                    }}
                  >
                    {devResetEnergyLoading ? 'Resetting…' : 'Reset energy to 1000'}
                  </button>
                </div>
              )}

              {/* Simulate "fortified under attack" warning (dev only, player1) */}
              <div style={{ marginBottom: '12px' }}>
                <button
                  onClick={handleDevSimulateUnderAttackWarning}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#cc0000',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    width: '100%'
                  }}
                >
                  Simulate under attack warning
                </button>
                <div style={{ color: '#888', fontSize: '10px', marginTop: '4px' }}>
                  Shows red &quot;WARNING: Austria is being attacked!&quot; for 5s
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <button
                  onClick={handleDevRestoreDefault}
                  disabled={devLoading || Object.keys(devPendingOwners).length === 0}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: Object.keys(devPendingOwners).length > 0 ? '#e74c3c' : '#3498db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: (devLoading || Object.keys(devPendingOwners).length === 0) ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    opacity: (devLoading || Object.keys(devPendingOwners).length === 0) ? 0.5 : 1
                  }}
                >
                  {Object.keys(devPendingOwners).length > 0 ? 'Discard Changes' : 'No Changes'}
                </button>
                <button
                  onClick={handleDevStoreAsDefault}
                  disabled={devLoading || Object.keys(devPendingOwners).length === 0}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#e67e22',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: (devLoading || Object.keys(devPendingOwners).length === 0) ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    opacity: (devLoading || Object.keys(devPendingOwners).length === 0) ? 0.5 : 1
                  }}
                >
                  Store as Default
                </button>
                <button
                  onClick={handleDevRandomize}
                  disabled={devLoading}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#9b59b6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: devLoading ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    opacity: devLoading ? 0.5 : 1
                  }}
                >
                  Randomize
                </button>
              </div>
              
              {devLoading && (
                <div style={{ color: '#4ECDC4', fontSize: '11px', marginTop: '10px', textAlign: 'center' }}>
                  Processing...
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default WarEffortFortification;
